<template>
  <div class="bg-white dark:bg-[#0c1427]/95 font-sans min-h-screen">
    <!-- Loading Screen -->
      <div
            v-if="isLoading"
            class="fixed inset-0 z-50 flex items-center justify-center bg-white dark:bg-[#0c1427] backdrop-blur-sm modal"
          >
            <div class="flex flex-col items-center justify-center text-center">

              <!-- Spinner גדול עם האייקון בפנים -->
              <div class="relative w-32 h-32 mb-8 flex items-center justify-center">
                <!-- טבעת חיצונית אפורה -->
                <div class="absolute inset-0 border-[6px] border-gray-200 dark:border-gray-700 rounded-full"></div>
                <!-- טבעת צבעונית שמסתובבת -->
                <div class="absolute inset-0 border-t-[6px] border-primary-500 rounded-full animate-spin"></div>
                <!-- אייקון יומן -->
                <div class="w-20 h-20 bg-primary-100 dark:bg-primary-900 rounded-xl flex items-center justify-center z-10 shadow-md">
                  <i class="ri-calendar-line text-3xl text-primary-500 dark:text-primary-400"></i>
                </div>
              </div>

              <!-- כותרת טעינה -->
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {{ $t("calendar.loadingTitle") || "Loading Calendar" }}
              </h3>

              <!-- תיאור טעינה -->
              <p class="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
                {{ $t("calendar.loadingDescription") || "Please wait while we load your data from the database..." }}
              </p>

              <!-- שלבי טעינה -->
              <div class="mt-6 space-y-2 text-xs text-gray-400">
                <div class="flex items-center justify-center space-x-2">
                  <div class="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
                  <span>{{ $t("calendar.loadingProfile") || "Loading user profile" }}</span>
                </div>
                <div class="flex items-center justify-center space-x-2">
                  <div class="w-2 h-2 bg-primary-500 rounded-full animate-pulse animation-delay-200"></div>
                  <span>{{ $t("calendar.loadingEvents") || "Loading events" }}</span>
                </div>
                <div class="flex items-center justify-center space-x-2">
                  <div class="w-2 h-2 bg-primary-500 rounded-full animate-pulse animation-delay-400"></div>
                  <span>{{ $t("calendar.loadingSettings") || "Loading settings" }}</span>
                </div>
              </div>
            </div>
        </div>

    <!-- Mobile Toggle Slider - Fixed Position -->
    <div class="mobile-toggle-fixed" v-if="!isLoading">
      <div class="view-toggle-slider">
        <div class="slider-track">
          <input 
            type="range" 
            min="0" 
            max="1" 
            step="0.01"
            :value="mobileView === 'sidebar' ? 0 : 1"
            @input="handleSliderChange"
            @change="handleSliderChange"
            class="slider-input"
          />
          <div class="slider-thumb" 
            :class="{ 'moved': mobileView === 'calendar' }"
            @touchstart="handleThumbTouchStart"
            @touchmove="handleThumbTouchMove"
            @touchend="handleThumbTouchEnd"
            @mousedown="handleThumbMouseDown"
            @click="handleThumbClick">
            <i class="ri-drag-move-2-line"></i>
          </div>
        </div>
        <div class="slider-labels">
          <span class="label-left">{{ $t('calendar.menu') }}</span>
          <span class="label-right">{{ $t('calendar.calendar') }}</span>
        </div>
      </div>
    </div>
    <!-- Main Calendar Layout -->
    <main class="max-w-7xl mx-auto p-6" v-if="!isLoading">
                <div class="grid grid-cols-1 lg:grid-cols-4 gap-6 calendar-container-height">
        
        <!-- Left Sidebar -->
                 <div class="bg-white dark:bg-[#15203c] rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden sidebar-responsive" :class="{ 'mobile-active': mobileView === 'sidebar', 'sidebar-hidden': mobileView !== 'sidebar' }">

          
          <!-- User Profile Section -->
          <div class="p-4 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="relative">
                  <img 
                    class="h-10 w-10 rounded-full object-cover" 
                    :src="userProfilePicture"
                    :alt="userDisplayName"
                    @error="handleImageError"
                  />
                </div>
                <div>
                  <h3 class="font-medium text-gray-900 dark:text-white" style="margin-bottom: 0px !important"> {{ userDisplayName }}</h3>
                </div>
              </div>
              <button @click="openSettingsModal" class="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300">
                <i class="fa-solid fa-gear"></i>
              </button>
            </div>
          </div>

          <!-- Mini Calendar -->
          <div class="p-4 border-b border-gray-100 dark:border-gray-700">
            <div class="text-center mb-3">
              <h4 class="font-medium text-gray-900 dark:text-white">{{ getMiniCalendarTitle }}</h4>
              <div class="flex justify-between items-center mt-2" style="margin-top: -33px !important">                <button @click="navigateMiniCalendar('prev')" class="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300">
                  <i class="fa-solid fa-chevron-left"></i>
                </button>
                <button @click="navigateMiniCalendar('next')" class="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300">
                  <i class="fa-solid fa-chevron-right"></i>
                </button>
              </div>
            </div>
            <div class="grid grid-cols-7 gap-1 text-xs">
              <div 
                v-for="day in miniCalendarDays" 
                :key="day" 
                class="text-center text-gray-500 dark:text-gray-400 p-1"
              >
                {{ day }}
              </div>
              <div
                v-for="day in getMiniCalendarDays"
                :key="day.date"
                class="text-center p-1 cursor-pointer rounded"
                :class="{
                  'text-gray-400 dark:text-gray-500': day.isOtherMonth,
                  'bg-primary-600 text-white': day.isToday,
                  'hover:bg-gray-100 dark:hover:bg-gray-700': !day.isToday && !day.isOtherMonth,
                  'bg-primary-100 text-primary-600 dark:bg-primary-900 dark:text-primary-400': day.isSelected && !day.isToday
                }"
                @click="selectMiniDay(day)"
              >
                {{ day.dayNumber }}
              </div>
            </div>
          </div>

          <!-- My Calendars -->
          <div class="p-4 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-center justify-between mb-3">
              <h4 class="font-medium text-gray-900 dark:text-white">{{ $t("calendar.myCalendars") }}</h4>
              <button @click="toggleMyCalendars" class="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300">
                <i class="ri-arrow-down-s-line" :class="{ 'rotate-180': myCalendarsExpanded }"></i>
              </button>
            </div>
            <div class="space-y-2" v-show="myCalendarsExpanded">
              <!-- Current User Calendar -->
              <div class="flex items-center justify-between cursor-pointer p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700" @click="toggleUserCalendar(currentUserId || '')">
                <div class="flex items-center gap-3">
                  <input 
                    type="checkbox" 
                    :checked="selectedUserCalendars.includes(currentUserId || '')"
                    @click.stop
                    @change="toggleUserCalendar(currentUserId || '')"
                    class="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <div class="w-3 h-3 bg-primary-500 rounded"></div>
                  <span class="text-sm text-gray-700 dark:text-gray-300">{{ userDisplayName }} ({{ $t("calendar.me") }})</span>
                </div>
                <span class="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full">
                  {{ getUserEventCount(currentUserId || "") }}
                </span>
              </div>
              
              <!-- Other Users Calendars -->
              <template v-if="userPermissions.view_all_calendars">
                <div v-for="(user, index) in otherUsers" :key="user.user_id" class="flex items-center justify-between cursor-pointer p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700" @click="toggleUserCalendar(user.user_id)">
                  <div class="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      :checked="selectedUserCalendars.includes(user.user_id)"
                      @click.stop
                      @change="toggleUserCalendar(user.user_id)"
                      class="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <div class="w-3 h-3 rounded" :style="{ backgroundColor: getUserCalendarColor(index) }"></div>
                    <span class="text-sm text-gray-700 dark:text-gray-300">{{ user.display_name || user.email }}</span>
                  </div>
                  <span class="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full">
                    {{ getUserEventCount(user.user_id) }}
                  </span>
                </div>
              </template>
            </div>
          </div>

          <!-- Appointment Types -->
          <div class="p-4">
            <div class="flex items-center justify-between mb-3">
              <h4 class="font-medium text-gray-900 dark:text-white">{{ $t("calendar.appointmentTypes") }}</h4>
              <button @click="openAppointmentTypesSettings" class="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300">
                <i class="fa-solid fa-gear"></i>
              </button>
            </div>
            <div class="space-y-2">
              <!-- Loading State -->
              <div v-if="loadingAppointmentTypes" class="loading-state text-center py-4">
                <i class="ri-loader-5-line animate-spin"></i>
                <span class="text-sm text-gray-500 dark:text-gray-400">{{ $t("calendar.loadingAppointmentTypes") || "טוען סוגי פגישות..." }}</span>
              </div>

              <!-- Appointment Types List -->
              <div
                v-else-if="appointmentTypes.length > 0"
                v-for="type in appointmentTypes.slice(0, 4)"
                :key="type.id || type.name"
                class="flex items-center justify-between"
              >
                <span class="text-sm text-gray-700 dark:text-gray-300">{{ type.name }}</span>
                <span class="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full">
                  {{ getAppointmentTypeCount(type.name) }}
                </span>
              </div>

              <!-- Empty State -->
              <div v-else class="empty-state text-center py-4">
                <i class="ri-calendar-line text-gray-400 text-2xl mb-2"></i>
                <span class="text-sm text-gray-500 dark:text-gray-400">{{ $t("calendar.noAppointmentTypes") || "אין סוגי פגישות זמינים" }}</span>
              </div>
            </div>
          </div>

        <!-- Mobile Settings Modal inside Sidebar -->
        <div 
          v-if="showSettingsModal && mobileView === 'sidebar'"
          class="modal"
          @click="closeSettingsModal"
        >
          <div class="mobile-modal-content" @click.stop>
            <!-- Modal Header -->
            <div class="mobile-modal-header">
              <div class="header-content">
                <div class="header-icon">
                  <i class="ri-settings-3-line"></i>
                </div>
                <h3>{{ t("calendar.settings.title") }}</h3>
              </div>
              <button @click="closeSettingsModal" class="close-btn">
                <i class="ri-close-line"></i>
              </button>
            </div>

            <!-- Settings Navigation -->
            <div class="mobile-settings-nav">
              <button
                v-for="tab in settingsTabs"
                :key="tab.id"
                @click="activeTab = tab.id"
                :class="{ active: activeTab === tab.id }"
                class="mobile-nav-tab"
              >
                <i :class="tab.icon"></i>
                <span>{{ t(tab.nameKey) }}</span>
              </button>
            </div>

            <!-- Modal Body -->
            <div class="mobile-modal-body">
              <!-- Working Hours Tab -->
              <div v-if="activeTab === 'hours'" class="mobile-settings-tab">
                <div class="mobile-setting-group">
                  <div class="mobile-group-header">
                    <h4>{{ t("calendar.settings.businessHours") }}</h4>
                    <p>{{ t("calendar.settings.businessHoursDescription") }}</p>
                  </div>

                  <!-- Loading State for Availability -->
                  <div v-if="loadingAvailability" class="mobile-loading-state">
                    <i class="ri-loader-5-line animate-spin"></i>
                    <span>{{ t("calendar.settings.loadingWorkingHours") }}</span>
                  </div>

                  <div v-else>
                    <div class="mobile-setting-row">
                      <div class="mobile-setting-info">
                        <label>{{ t("calendar.settings.enableWorkingHours") }}</label>
                        <span class="mobile-setting-desc">{{
                          t("calendar.settings.enableWorkingHoursDescription")
                        }}</span>
                      </div>
                      <label class="mobile-switch">
                        <input type="checkbox" v-model="workingHoursEnabled" />
                        <span class="mobile-switch-slider"></span>
                      </label>
                    </div>

                    <div v-if="workingHoursEnabled" class="mobile-working-hours">
                      <div
                        v-for="day in workingDays"
                        :key="day.key"
                        class="mobile-day-row"
                        :class="{ disabled: !day.enabled }"
                      >
                        <div class="mobile-day-info">
                          <label class="mobile-day-checkbox">
                            <input type="checkbox" v-model="day.enabled" />
                            <span class="mobile-checkmark"></span>
                          </label>
                          <div class="mobile-day-name">
                            {{ t(`calendar.dayAbbreviations.${day.name.toLowerCase()}`) }}
                          </div>
                        </div>

                        <div class="mobile-time-controls" v-if="day.enabled">
                          <input
                            v-model="day.startTime"
                            type="time"
                            class="mobile-time-input"
                          />
                          <span class="mobile-time-separator">–</span>
                          <input
                            v-model="day.endTime"
                            type="time"
                            class="mobile-time-input"
                          />
                        </div>
                      </div>
                    </div>

                    <!-- Save Button -->
                    <div class="mobile-actions">
                      <button
                        @click="saveEmployeeAvailability"
                        class="mobile-btn-primary"
                        :disabled="savingAvailability"
                      >
                        <i
                          v-if="savingAvailability"
                          class="ri-loader-5-line animate-spin"
                        ></i>
                        <i v-else class="ri-save-line"></i>
                        {{ t("calendar.settings.saveWorkingHours") }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Break Times Tab -->
              <div v-if="activeTab === 'breaks'" class="mobile-settings-tab">
                <div class="mobile-setting-group">
                  <div class="mobile-group-header">
                    <h4>{{ t("calendar.settings.breakTimes") }}</h4>
                    <p>{{ t("calendar.settings.breakTimesDescription") }}</p>
                    <button 
                      @click="addBlockedTime" 
                      @touchend.prevent="addBlockedTime"
                      class="mobile-add-btn"
                      style="touch-action: manipulation; -webkit-touch-callout: none; -webkit-user-select: none; -khtml-user-select: none; -moz-user-select: none; -ms-user-select: none; user-select: none;"
                    >
                      <i class="ri-add-line"></i>
                      {{ t("calendar.settings.addBreak") }}
                    </button>
                  </div>

                  <!-- Loading State for Breaks -->
                  <div v-if="loadingBreaks" class="mobile-loading-state">
                    <i class="ri-loader-5-line animate-spin"></i>
                    <span>{{ t("calendar.settings.loadingBreaks") }}</span>
                  </div>

                  <div v-else class="mobile-breaks-list">
                    <div
                      v-for="(block, index) in blockedTimes"
                      :key="block.id || index"
                      class="mobile-break-item"
                    >
                      <div class="mobile-break-header">
                        <input
                          v-if="block.isEditing"
                          v-model="block.name"
                          type="text"
                          class="mobile-break-name editing"
                          :placeholder="t('calendar.settings.breakNamePlaceholder')"
                        />
                        <span v-else class="mobile-break-name">{{ block.name }}</span>

                        <div class="mobile-break-actions">
                          <button
                            v-if="!block.isEditing"
                            @click="editBreak(index)"
                            @touchend.prevent="editBreak(index)"
                            class="mobile-edit-btn"
                            :title="t('common.edit')"
                            style="touch-action: manipulation; -webkit-touch-callout: none; -webkit-user-select: none; -khtml-user-select: none; -moz-user-select: none; -ms-user-select: none; user-select: none;"
                          >
                            <i class="ri-edit-line"></i>
                          </button>
                          <button
                            @click="removeBlockedTime(index)"
                            @touchend.prevent="removeBlockedTime(index)"
                            class="mobile-remove-btn"
                            :title="t('common.delete')"
                            :disabled="savingBreak"
                            style="touch-action: manipulation; -webkit-touch-callout: none; -webkit-user-select: none; -khtml-user-select: none; -moz-user-select: none; -ms-user-select: none; user-select: none;"
                          >
                            <i class="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>

                      <div v-if="block.isEditing" class="mobile-break-edit-form">
                        <div class="mobile-break-time">
                          <label>{{ t("calendar.settings.startTime") }}:</label>
                          <input
                            v-model="block.startTime"
                            type="time"
                            class="mobile-time-input"
                          />
                        </div>
                        <div class="mobile-break-time">
                          <label>{{ t("calendar.settings.endTime") }}:</label>
                          <input
                            v-model="block.endTime"
                            type="time"
                            class="mobile-time-input"
                          />
                        </div>

                        <!-- Days Selection -->
                        <div class="mobile-break-days">
                          <label class="mobile-form-label">{{ t("calendar.settings.activeDays") }}:</label>
                          <div class="mobile-days-grid">
                            <label
                              v-for="day in daysOfWeek"
                              :key="day.value"
                              class="mobile-day-pill"
                              :class="{ active: block.days && block.days.includes(day.value) }"
                            >
                              <input
                                type="checkbox"
                                v-model="block.days"
                                :value="day.value"
                                style="display: none;"
                              />
                              <span>{{ day.label }}</span>
                            </label>
                          </div>
                        </div>
                        <div class="mobile-break-buttons">
                          <button
                            @click="saveBreak(block, index)"
                            @touchend.prevent="saveBreak(block, index)"
                            class="mobile-save-btn"
                            :disabled="savingBreak"
                            style="touch-action: manipulation;"
                          >
                            <i v-if="savingBreak" class="ri-loader-5-line animate-spin"></i>
                            <i v-else class="ri-save-line"></i>
                            {{ t("common.save") }}
                          </button>
                          <button 
                            @click="cancelEditBreak(index)" 
                            @touchend.prevent="cancelEditBreak(index)"
                            class="mobile-cancel-btn"
                            style="touch-action: manipulation;"
                          >
                            <i class="ri-close-line"></i>
                            {{ t("common.cancel") }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Appointment Types Tab -->
              <div v-if="activeTab === 'types'" class="mobile-settings-tab">
                <div class="mobile-setting-group">
                  <div class="mobile-group-header">
                    <h4>{{ t("calendar.settings.appointmentTypes") }}</h4>
                    <p>{{ t("calendar.settings.appointmentTypesDescription") }}</p>
                    <button 
                      @click="addAppointmentType" 
                      @touchend.prevent="addAppointmentType"
                      class="mobile-add-btn"
                      style="touch-action: manipulation; -webkit-touch-callout: none; -webkit-user-select: none; -khtml-user-select: none; -moz-user-select: none; -ms-user-select: none; user-select: none;"
                    >
                      <i class="ri-add-line"></i>
                      {{ t("calendar.settings.addType") }}
                    </button>
                  </div>

                  <!-- Loading State -->
                  <div v-if="loadingAppointmentTypes" class="mobile-loading-state">
                    <i class="ri-loader-5-line animate-spin"></i>
                    <span>{{ t("calendar.settings.loadingTypes") }}</span>
                  </div>

                  <div v-else class="mobile-types-list">
                    <div
                      v-for="(type, index) in appointmentTypes"
                      :key="type.id || index"
                      class="mobile-type-item"
                    >
                      <div class="mobile-type-header">
                        <div class="mobile-type-info">
                          <div
                            class="mobile-type-color"
                            :style="{ backgroundColor: type.colorCode || getColorValue(type.color) }"
                          ></div>
                          <span v-if="!type.isEditing" class="mobile-type-name">{{ type.name }}</span>
                          <input
                            v-else
                            v-model="type.name"
                            type="text"
                            class="mobile-type-name-input"
                            :placeholder="t('calendar.settings.appointmentTypeNamePlaceholder')"
                          />
                          <span v-if="!type.isEditing" class="mobile-type-duration">{{ type.duration }}{{ t("calendar.minutes") }}</span>
                        </div>
                        <div class="mobile-type-actions">
                          <button
                            v-if="!type.isEditing"
                            @click="editAppointmentType(index)"
                            @touchend.prevent="editAppointmentType(index)"
                            class="mobile-edit-btn"
                            :title="t('common.edit')"
                            style="touch-action: manipulation; -webkit-touch-callout: none; -webkit-user-select: none; -khtml-user-select: none; -moz-user-select: none; -ms-user-select: none; user-select: none;"
                          >
                            <i class="ri-edit-line"></i>
                          </button>
                          <button
                            @click="removeAppointmentType(index)"
                            @touchend.prevent="removeAppointmentType(index)"
                            class="mobile-remove-btn"
                            :title="t('common.delete')"
                            style="touch-action: manipulation; -webkit-touch-callout: none; -webkit-user-select: none; -khtml-user-select: none; -moz-user-select: none; -ms-user-select: none; user-select: none;"
                          >
                            <i class="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>

                      <!-- Mobile Edit Form -->
                      <div v-if="type.isEditing" class="mobile-type-edit-form">
                        <!-- Duration -->
                        <div class="mobile-form-group">
                          <label class="mobile-form-label">{{ t("calendar.settings.duration") }} ({{ t("calendar.settings.minutes") }})</label>
                          <div class="mobile-duration-control">
                            <button
                              @click="adjustDuration(type, -15)"
                              @touchend.prevent="adjustDuration(type, -15)"
                              class="mobile-duration-btn"
                              style="touch-action: manipulation;"
                            >
                              -
                            </button>
                            <input
                              v-model.number="type.duration"
                              type="number"
                              min="15"
                              max="480"
                              step="15"
                              @blur="validateDuration(type)"
                              class="mobile-duration-input"
                            />
                            <button
                              @click="adjustDuration(type, 15)"
                              @touchend.prevent="adjustDuration(type, 15)"
                              class="mobile-duration-btn"
                              style="touch-action: manipulation;"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        <!-- Color Selection -->
                        <div class="mobile-form-group">
                          <label class="mobile-form-label">{{ t("calendar.settings.backgroundColor") }}</label>
                          <div class="mobile-color-grid">
                            <div
                              v-for="color in colorOptions"
                              :key="color.value"
                              class="mobile-color-dot"
                              :class="{ active: type.color === color.value }"
                              :style="{ backgroundColor: color.bg }"
                              @click="
                                type.color = color.value;
                                type.colorCode = color.bg;
                              "
                              @touchend.prevent="
                                type.color = color.value;
                                type.colorCode = color.bg;
                              "
                              style="touch-action: manipulation;"
                              :title="color.value"
                            ></div>
                          </div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="mobile-form-actions">
                          <button
                            @click="cancelEditAppointmentType(index)"
                            @touchend.prevent="cancelEditAppointmentType(index)"
                            class="mobile-btn-secondary"
                            :disabled="savingAppointmentType"
                            style="touch-action: manipulation;"
                          >
                            <i class="ri-close-line"></i>
                            {{ t("common.cancel") }}
                          </button>
                          <button
                            @click="saveAppointmentType(type, index)"
                            @touchend.prevent="saveAppointmentType(type, index)"
                            class="mobile-btn-primary"
                            :disabled="savingAppointmentType"
                            style="touch-action: manipulation;"
                          >
                            <i
                              v-if="savingAppointmentType"
                              class="ri-loader-5-line animate-spin"
                            ></i>
                            <i v-else class="ri-save-line"></i>
                            {{ t("common.save") }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

        <!-- Main Calendar View -->
                 <div class="col-span-1 lg:col-span-3 bg-white dark:bg-[#15203c] rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 calendar-responsive" :class="{ 'mobile-active': mobileView === 'calendar', 'calendar-hidden': mobileView !== 'calendar' }">
          
          <!-- Calendar Header -->
          <div class="p-4 border-b border-gray-100 dark:border-gray-700 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div class="flex items-center gap-4">
              <h2 class="text-xl font-semibold text-gray-900 dark:text-white" style="margin-bottom: 0px !important">{{ getCurrentMonthYear }}</h2>
              <div class="flex gap-2">
                <button @click="navigateCalendar('prev')" class="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300">
                  <i class="fa-solid fa-chevron-left"></i>
                </button>
                <button @click="navigateCalendar('next')" class="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300">
                  <i class="fa-solid fa-chevron-right"></i>
                </button>
              </div>
            </div>
            <div class="flex flex-wrap items-center gap-2 lg:gap-3">
              <button 
                @click="changeView('month')"
                :class="{ 'bg-gray-100 dark:bg-gray-700': currentView === 'month' }"
                class="px-2 lg:px-3 py-2 text-xs lg:text-sm border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                {{ $t("calendar.month") || "Month" }}
              </button>
              <button 
                @click="changeView('week')"
                :class="{ 'bg-gray-100 dark:bg-gray-700': currentView === 'week' }"
                class="px-2 lg:px-3 py-2 text-xs lg:text-sm border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                {{ $t("calendar.week") || "Week" }}
              </button>
              <button 
                @click="changeView('day')"
                :class="{ 'bg-gray-100 dark:bg-gray-700': currentView === 'day' }"
                class="px-2 lg:px-3 py-2 text-xs lg:text-sm border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                {{ $t("calendar.day") || "Day" }}
              </button>
              <button @click="openAddEventModal" class="bg-primary-600 hover:bg-primary-700 text-white px-3 lg:px-4 py-2 rounded-lg text-xs lg:text-sm font-medium">
                <i class="fa-solid fa-plus mr-1 lg:mr-2"></i><span class="hidden sm:inline">{{ $t("calendar.addEvent") || "New Appointment" }}</span><span class="sm:hidden">{{ $t("calendar.add") || "Add" }}</span>
              </button>
            </div>
          </div>

          <!-- Calendar Grid - Month View -->
          <div v-if="currentView === 'month'" class="p-4">
            <!-- Days Header -->
            <div class="grid grid-cols-7 gap-1 mb-4">
              <div 
                v-for="(day, index) in monthViewDayHeaders" 
                :key="day"
                class="text-center text-sm font-medium text-gray-700 dark:text-gray-300 p-3"
              >
                <!-- Full name on desktop, 2 letters on mobile -->
                <span class="hidden lg:inline">{{ day }}</span>
                <span class="lg:hidden">{{ monthViewDayHeadersMobile[index] }}</span>
              </div>
            </div>
            
            <!-- Calendar Days Grid -->
            <div class="grid grid-cols-7 gap-1" style="grid-template-rows: repeat(6, minmax(80px, 1fr));">
              <div
                v-for="day in getMonthViewDays"
                :key="day.date"
                class="border border-gray-200 dark:border-gray-600 p-1 lg:p-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer calendar-day-min-height"
                :class="{
                  'bg-gray-50 dark:bg-gray-700': day.isOtherMonth,
                  'bg-blue-50 dark:calendar-today-bg': day.isToday && !isDayOfWeekBlocked(day.date),
                  'bg-gray-100 dark:bg-gray-600': isDayOfWeekBlocked(day.date) && !day.isOtherMonth
                }"
                @click="createEventAt(day.date, 9, $event)"
              >
                <span 
                  class="text-xs lg:text-sm font-medium"
                  :class="{
                    'text-gray-400 dark:text-gray-500': day.isOtherMonth || (isDayOfWeekBlocked(day.date) && !day.isOtherMonth),
                    'text-blue-600 dark:text-blue-400': day.isToday && !isDayOfWeekBlocked(day.date),
                    'text-gray-900 dark:text-white': !day.isOtherMonth && !day.isToday && !isDayOfWeekBlocked(day.date)
                  }"
                >
                  {{ day.dayNumber }}
                </span>
                
                <!-- Events for this day -->
                <div class="mt-1 space-y-1">
                  <div
                    v-for="event in getDayEventsForMonth(day.date)"
                    :key="event.id"
                    class="text-xs p-1 rounded cursor-pointer truncate"
                    :class="[getEventColorClasses(event), { 'completed-event': event.is_over }]"
                    @click.stop="openEvent(event)"
                  >
                    <div class="font-medium text-xs flex items-center gap-1">
                      <i v-if="event.is_over" class="ri-checkbox-circle-fill text-green-500 text-xs" :title="$t('calendar.meetingCompleted')"></i>
                      <span :class="{ 'line-through opacity-70': event.is_over }">{{ event.title }}</span>
                    </div>
                    <div v-if="event.timeDisplay" class="text-xs hidden lg:block">{{ event.timeDisplay }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Week/Day View (existing functionality) -->
          <div v-else class="p-2 lg:p-4 space-y-4">
          <!-- Week Header - Modern Tailwind Design -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600" :class="{'overflow-x-auto': currentView !== 'day'}">
            <div class="grid" :class="[
              currentView === 'day' ? 'grid-cols-2 w-full' : 'grid-cols-8 min-w-[600px]'
            ]">
              <div class="bg-white dark:bg-gray-800 p-2 lg:p-4 border-r border-gray-200 dark:border-gray-600 flex items-center justify-center">
                <i class="ri-calendar-line text-gray-500 dark:text-gray-400"></i>
              </div>
              <div
                v-for="day in currentViewDays"
                :key="day.date"
                class="bg-white dark:bg-gray-800 p-2 lg:p-4 text-center cursor-pointer border-r border-gray-200 dark:border-gray-600 last:border-r-0 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                :class="{
                  'bg-primary-600 hover:bg-primary-700': day.isToday,
                  'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300': day.isSelected && !day.isToday
                }"
                @click="selectDay(day)"
              >
                <div class="text-xs font-medium mb-1 opacity-80">{{ day.dayName }}</div>
                <div class="text-sm lg:text-lg font-bold">{{ day.dayNumber }}</div>
              </div>
            </div>
          </div>

          <!-- Calendar Grid - Modern Design -->
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden" style="min-height: 500px;" :class="{'overflow-x-auto': currentView !== 'day'}">
            <div class="grid" :class="[
              currentView === 'day' ? 'grid-cols-2 w-full' : 'grid-cols-8 min-w-[600px]'
            ]" :style="currentView === 'day' ? 'grid-template-rows: repeat(16, 50px) overflow-y-scroll;' : 'grid-template-rows: repeat(16, 60px) !important; overflow-y-auto;'">
              <!-- Time Column -->
              <div class="border-r border-gray-200 dark:border-gray-600">
                <div v-for="hour in hours" :key="hour" class="calendar-hour-height border-b border-gray-100 dark:border-gray-700 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 font-medium">
                  {{ formatHour(hour) }}
                </div>
              </div>

              <!-- Day Columns -->
              <div
                v-for="day in currentViewDays"
                :key="day.date"
                class="border-r border-gray-200 dark:border-gray-600 last:border-r-0 relative"
                :class="{ 'bg-blue-50 dark:calendar-today-week-bg': day.isToday }"
              >
                <!-- Hour slots for background grid -->
                                  <div
                    v-for="hour in hours"
                    :key="hour"
                    class="calendar-hour-height border-b border-gray-100 dark:border-gray-700 relative cursor-pointer hover:bg-gray-50 dark:calendar-hover-gray transition-colors"
                                      :class="{
                      'bg-blue-100 dark:calendar-dragover-bg': dragOverSlot === `${day.date}-${hour}`,
                      'bg-gray-100 dark:bg-gray-600': isHourOccupied(day.date, hour),
                    }"
                  @click="createEventAt(day.date, hour, $event)"
                  @dragover.prevent="handleTimeSlotDragOver($event, day.date, hour)"
                  @dragleave="handleDragLeave"
                  @drop="handleTimeSlotDrop($event, day.date, hour)"
                >
                  <!-- Quarter hour lines -->
                  <div class="absolute inset-0 pointer-events-none">
                    <div class="absolute left-0 right-0 border-t border-gray-50 dark:border-gray-700/50" style="top: 25%"></div>
                    <div class="absolute left-0 right-0 border-t border-gray-50 dark:border-gray-700/50" style="top: 50%"></div>
                    <div class="absolute left-0 right-0 border-t border-gray-50 dark:border-gray-700/50" style="top: 75%"></div>
                  </div>
                </div>

                <!-- Events positioned absolutely within the day column -->
                <div
                  v-for="event in getDayEvents(day.date)"
                  :key="event.id"
                  class="absolute left-1 right-1 rounded-md shadow-sm border cursor-pointer z-10 p-2 text-xs overflow-hidden"
                  :class="[
                    getEventColorClasses(event),
                    { 'opacity-75 scale-95': draggingEvent === event.id },
                    { 'completed-event': event.is_over }
                  ]"
                  :style="getAbsoluteEventStyle(event, day.date)"
                  draggable="true"
                  @dragstart="handleEventDragStart($event, event)"
                  @dragend="handleEventDragEnd"
                  @click.stop="openEvent(event)"
                >
                  <div class="font-medium text-xs mb-1">{{ event.timeDisplay }}</div>
                  <div class="font-semibold text-xs flex items-center gap-1">
                    <i v-if="event.is_over" class="ri-checkbox-circle-fill text-green-500 text-xs flex-shrink-0" :title="$t('calendar.meetingCompleted')"></i>
                    <span class="truncate" :class="{ 'line-through opacity-70': event.is_over }">{{ event.title }}</span>
                  </div>
                  <div v-if="event.location" class="flex items-center gap-1 text-xs opacity-80 mt-1">
                    <i class="ri-map-pin-line"></i>
                    <span class="truncate">{{ event.location }}</span>
                  </div>
                  <!-- User indicator (show when viewing multiple calendars) -->
                  <div
                    v-if="userPermissions.view_all_calendars && selectedUserCalendars.length > 1"
                    class="absolute top-1 right-1 bg-primary-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold"
                    :title="getUserDisplayName(event.userId)"
                  >
                    {{ getUserInitials(event.userId) }}
                  </div>
                  <div class="absolute top-1 left-1 opacity-0 hover:opacity-100 transition-opacity">
                    <i class="ri-drag-move-line text-gray-400"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Event Detail Modal -->
        <div v-if="selectedEvent" class="modal" @click="closeEventDetail">
          <div class="modal-content add-event-modal no-frame" @click.stop>
            <!-- Modal Header -->
            <div class="modal-header-clean">
              <div class="header-content">
                <div class="header-icon-clean">
                  <i class="ri-calendar-check-line"></i>
                </div>
                <div class="header-text-clean">
                  <h3>{{ selectedEvent.title }}</h3>
                  <p>{{ $t("calendar.eventDetails") }}</p>
                </div>
              </div>
              <button @click="closeEventDetail" class="close-btn-clean">
                <i class="ri-close-line"></i>
              </button>
            </div>

            <!-- Modal Body -->
            <div class="modal-body-clean dark:bg-gray-700">
              <div class="event-detail-content dark:bg-gray-700">
                <div class="detail-section dark:bg-gray-700">
                  <div class="detail-row-clean dark:bg-gray-700">
                    <div class="detail-icon">
                      <i class="ri-calendar-line"></i>
                    </div>
                    <div class="detail-info">
                      <label class="detail-label">{{ $t("calendar.date") }}</label>
                      <span class="detail-value">{{ selectedEvent.dateDisplay }}</span>
                    </div>
                  </div>

                  <div class="detail-row-clean">
                    <div class="detail-icon">
                      <i class="ri-time-line"></i>
                    </div>
                    <div class="detail-info">
                      <label class="detail-label">{{ $t("calendar.time") }}</label>
                      <span class="detail-value">{{ selectedEvent.timeRange }}</span>
                    </div>
                  </div>

                  <div v-if="selectedEvent.type" class="detail-row-clean">
                    <div class="detail-icon">
                      <i class="ri-bookmark-line"></i>
                    </div>
                    <div class="detail-info">
                      <label class="detail-label">{{ $t("calendar.eventType") }}</label>
                      <span class="detail-value">{{ selectedEvent.type }}</span>
                    </div>
                  </div>

                  <div v-if="selectedEvent.location" class="detail-row-clean">
                    <div class="detail-icon">
                      <i class="ri-map-pin-line"></i>
                    </div>
                    <div class="detail-info">
                      <label class="detail-label">{{ $t("calendar.location") }}</label>
                      <span class="detail-value">{{ selectedEvent.location }}</span>
                    </div>
                  </div>

                  <div v-if="selectedEvent.description" class="detail-row-clean">
                    <div class="detail-icon">
                      <i class="ri-file-text-line"></i>
                    </div>
                    <div class="detail-info">
                      <label class="detail-label">{{ $t("calendar.description") }}</label>
                      <span class="detail-value">{{ selectedEvent.description }}</span>
                    </div>
                  </div>
                </div>

                <div
                  v-if="
                    selectedEvent.participants && selectedEvent.participants.length > 0
                  "
                  class="participants-section"
                >
                  <h4 class="section-title">{{ $t("calendar.participants") }}</h4>
                  <div class="participants-grid">
                    <div
                      v-for="participant in selectedEvent.participants"
                      :key="participant.id"
                      class="participant-item-detail"
                    >
                      <div class="participant-icon">
                        <i class="ri-user-line"></i>
                      </div>
                      <div class="participant-details">
                        <span class="participant-name">{{ participant.name }}</span>
                        <span v-if="participant.email" class="participant-email">{{
                          participant.email
                        }}</span>
                        <span v-if="participant.phone" class="participant-phone">{{
                          participant.phone
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Modal Footer -->
            <div class="modal-footer-clean">
              <button
                type="button"
                class="btn-clean btn-danger"
                @click="deleteEventFromView"
              >
                <i class="ri-delete-bin-line"></i>
                {{ $t("calendar.delete") }}
              </button>
              <button
                type="button"
                class="btn-clean btn-secondary"
                @click="openEditEventModal"
              >
                <i class="ri-edit-line"></i>
                {{ $t("calendar.edit") }}
              </button>
              <button
                type="button"
                :class="selectedEvent.is_over ? 'btn-clean btn-warning' : 'btn-clean btn-success'"
                style="color: #ffffff !important"
                @click="toggleEventCompletion"
              >
                <i :class="selectedEvent.is_over ? 'ri-refresh-line' : 'ri-checkbox-circle-line'"></i>
                {{ selectedEvent.is_over ? $t("calendar.markAsNotCompleted") : $t("calendar.markAsCompleted") }}
              </button>
            </div>
          </div>
        </div>
        <!-- Add Event Modal -->
        <div
          v-if="showAddEventModal"
          class="modal"
          @click="closeAddEventModal"
        >
          <div class="modal-content add-event-modal no-frame" @click.stop>
            <!-- Modal Header -->
            <div class="modal-header-clean">
              <div class="header-content">
                <div class="header-icon-clean">
                  <i class="ri-calendar-event-line"></i>
                </div>
                <div class="header-text-clean">
                  <h3>{{ $t("calendar.createNewEvent") }}</h3>
                  <p>{{ $t("calendar.addNewEventToCalendar") }}</p>
                </div>
              </div>
              <button @click="closeAddEventModal" class="close-btn-clean">
                <i class="ri-close-line"></i>
              </button>
            </div>

            <!-- Modal Body -->
            <div class="modal-body-clean">
              <form @submit.prevent="saveNewEvent" class="event-form">
                <!-- Event Title & Type Row -->
                <div class="form-row">
                  <div class="form-group">
                    <label class="form-label">{{ $t("calendar.eventTitle") }} *</label>
                    <div class="input-group">
                      <input
                        v-model="newEvent.title"
                        type="text"
                        :placeholder="$t('calendar.enterEventTitle')"
                        class="form-input"
                        required
                      />
                    </div>
                  </div>

                  <div class="form-group">
                    <label class="form-label">{{ $t("calendar.eventType") }} *</label>
                    <div class="input-group">
                      <select
                        v-model="newEvent.type"
                        class="form-input form-select"
                        required
                        @change="updateEventDuration"
                      >
                        <option value="">{{ $t("calendar.selectType") }}</option>
                        <option
                          v-for="type in appointmentTypes"
                          :key="type.id || type.name"
                          :value="type.name"
                        >
                          {{ type.name }} ({{ type.duration }}
                          {{ $t("calendar.minutes") }})
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Date & Time Row -->
                <div class="form-row">
                  <div class="form-group">
                    <label class="form-label">{{ $t("calendar.date") }} *</label>
                    <div class="input-group">
                      <input
                        v-model="newEvent.date"
                        type="date"
                        class="form-input"
                        required
                      />
                    </div>
                  </div>

                  <div class="form-group">
                    <label class="form-label">{{ $t("calendar.startTime") }} *</label>
                    <div class="input-group">
                      <input
                        v-model="newEvent.startTime"
                        type="time"
                        class="form-input"
                        required
                      />
                    </div>
                  </div>

                  <div class="form-group">
                    <label class="form-label">{{ $t("calendar.endTime") }} *</label>
                    <div class="input-group">
                      <input
                        v-model="newEvent.endTime"
                        type="time"
                        class="form-input"
                        required
                      />
                    </div>
                  </div>
                </div>

                <!-- Location -->
                <div class="form-group">
                  <label class="form-label">{{ $t("calendar.location") }}</label>
                  <div class="input-group">
                    <input
                      v-model="newEvent.location"
                      type="text"
                      :placeholder="$t('calendar.enterLocation')"
                      class="form-input"
                    />
                  </div>
                </div>

                <!-- Description -->
                <div class="form-group">
                  <label class="form-label">{{ $t("calendar.description") }}</label>
                  <div class="input-group">
                    <textarea
                      v-model="newEvent.description"
                      :placeholder="$t('calendar.enterDescription')"
                      class="form-input form-textarea"
                      rows="3"
                    ></textarea>
                  </div>
                </div>

                <!-- Participants -->
                <div class="form-group">
                  <div class="participants-header-clean">
                    <label class="form-label">{{ $t("calendar.participants") }}</label>
                    <div class="participants-actions">
                      <div class="contact-search-buttons">
                        <button
                          type="button"
                          @click="openLeadsSearch"
                          class="search-leads-btn"
                        >
                          <i class="ri-user-star-line"></i>
                          {{ t("calendar.searchLeads") }}
                        </button>
                        <button
                          type="button"
                          @click="openClientsSearch"
                          class="search-clients-btn"
                        >
                          <i class="ri-user-heart-line"></i>
                          {{ t("calendar.searchClients") }}
                        </button>
                      </div>
                      <button type="button" @click="addParticipant" class="add-btn">
                        <i class="ri-add-line"></i>
                        {{ $t("calendar.add") }}
                      </button>
                    </div>
                  </div>

                  <div class="participants-list-clean">
                    <div
                      v-for="(participant, index) in newEvent.participants"
                      :key="index"
                      class="participant-card"
                    >
                      <div class="participant-fields">
                        <div class="input-group">
                          <i class="ri-user-line input-icon-clean"></i>
                          <input
                            v-model="participant.name"
                            type="text"
                            :placeholder="$t('calendar.name')"
                            class="form-input participant-input"
                          />
                        </div>

                        <div class="input-group">
                          <i class="ri-mail-line input-icon-clean"></i>
                          <input
                            v-model="participant.email"
                            type="email"
                            :placeholder="$t('calendar.email')"
                            class="form-input participant-input"
                          />
                        </div>

                        <div class="input-group">
                          <i class="ri-phone-line input-icon-clean"></i>
                          <input
                            v-model="participant.phone"
                            type="tel"
                            :placeholder="$t('calendar.phone')"
                            class="form-input participant-input"
                          />
                        </div>
                      </div>

                      <button
                        v-if="newEvent.participants.length > 1"
                        type="button"
                        @click="removeParticipant(index)"
                        class="remove-btn"
                      >
                        <i class="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <!-- Modal Footer -->
            <div class="modal-footer-clean">
              <button
                type="button"
                class="btn-clean btn-secondary"
                @click="closeAddEventModal"
              >
                {{ $t("calendar.cancel") }}
              </button>
              <button type="button" class="btn-clean btn-primary" @click="saveNewEvent">
                <i class="ri-save-line"></i>
                {{ $t("calendar.createEvent") }}
              </button>
            </div>
          </div>
        </div>

        <!-- Edit Event Modal -->
        <div
          v-if="showEditEventModal"
          class="modal"
          @click="closeEditEventModal"
        >
          <div class="modal-content add-event-modal no-frame" @click.stop>
            <!-- Modal Header -->
            <div class="modal-header-clean">
              <div class="header-content">
                <div class="header-icon-clean">
                  <i class="ri-edit-line"></i>
                </div>
                <div class="header-text-clean">
                  <h3>{{ $t("calendar.editEvent") }}</h3>
                  <p>{{ $t("calendar.updateEventDetails") }}</p>
                </div>
              </div>
              <button @click="closeEditEventModal" class="close-btn-clean">
                <i class="ri-close-line"></i>
              </button>
            </div>

            <!-- Modal Body -->
            <div class="modal-body-clean">
              <form @submit.prevent="updateEvent" class="event-form">
                <!-- Event Title & Type Row -->
                <div class="form-row">
                  <div class="form-group">
                    <label class="form-label">{{ $t("calendar.eventTitle") }} *</label>
                    <div class="input-group">
                      <input
                        v-model="editingEvent.title"
                        type="text"
                        :placeholder="$t('calendar.enterEventTitle')"
                        class="form-input"
                        required
                      />
                    </div>
                  </div>

                  <div class="form-group">
                    <label class="form-label">{{ $t("calendar.eventType") }} *</label>
                    <div class="input-group">
                      <select
                        v-model="editingEvent.type"
                        class="form-input form-select"
                        required
                      >
                        <option value="">{{ $t("calendar.selectType") }}</option>
                        <option
                          v-for="type in appointmentTypes"
                          :key="type.id || type.name"
                          :value="type.name"
                        >
                          {{ type.name }} ({{ type.duration }}
                          {{ $t("calendar.minutes") }})
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Date & Time Row -->
                <div class="form-row">
                  <div class="form-group">
                    <label class="form-label">{{ $t("calendar.date") }} *</label>
                    <div class="input-group">
                      <input
                        v-model="editingEvent.date"
                        type="date"
                        class="form-input"
                        required
                      />
                    </div>
                  </div>

                  <div class="form-group">
                    <label class="form-label">{{ $t("calendar.startTime") }} *</label>
                    <div class="input-group">
                      <input
                        v-model="editingEvent.startTime"
                        type="time"
                        class="form-input"
                        required
                      />
                    </div>
                  </div>

                  <div class="form-group">
                    <label class="form-label">{{ $t("calendar.endTime") }} *</label>
                    <div class="input-group">
                      <input
                        v-model="editingEvent.endTime"
                        type="time"
                        class="form-input"
                        required
                      />
                    </div>
                  </div>
                </div>

                <!-- Location -->
                <div class="form-group">
                  <label class="form-label">{{ $t("calendar.location") }}</label>
                  <div class="input-group">
                    <input
                      v-model="editingEvent.location"
                      type="text"
                      :placeholder="$t('calendar.enterLocation')"
                      class="form-input"
                    />
                  </div>
                </div>

                <!-- Description -->
                <div class="form-group">
                  <label class="form-label">{{ $t("calendar.description") }}</label>
                  <div class="input-group">
                    <textarea
                      v-model="editingEvent.description"
                      :placeholder="$t('calendar.enterDescription')"
                      class="form-input form-textarea"
                      rows="3"
                    ></textarea>
                  </div>
                </div>

                <!-- Participants -->
                <div class="form-group">
                  <div class="participants-header-clean">
                    <label class="form-label">{{ $t("calendar.participants") }}</label>
                    <div class="participants-actions">
                      <div class="contact-search-buttons">
                        <button
                          type="button"
                          @click="openLeadsSearchForEdit"
                          class="search-leads-btn"
                        >
                          <i class="ri-user-star-line"></i>
                          {{ t("calendar.searchLeads") }}
                        </button>
                        <button
                          type="button"
                          @click="openClientsSearchForEdit"
                          class="search-clients-btn"
                        >
                          <i class="ri-user-heart-line"></i>
                          {{ t("calendar.searchClients") }}
                        </button>
                      </div>
                      <button type="button" @click="addParticipantToEdit" class="add-btn">
                        <i class="ri-add-line"></i>
                        {{ $t("calendar.add") }}
                      </button>
                    </div>
                  </div>

                  <div class="participants-list-clean">
                    <div
                      v-for="(participant, index) in editingEvent.participants"
                      :key="index"
                      class="participant-card"
                    >
                      <div class="participant-fields">
                        <div class="input-group">
                          <i class="ri-user-line input-icon-clean"></i>
                          <input
                            v-model="participant.name"
                            type="text"
                            :placeholder="$t('calendar.name')"
                            class="form-input participant-input"
                          />
                        </div>

                        <div class="input-group">
                          <i class="ri-mail-line input-icon-clean"></i>
                          <input
                            v-model="participant.email"
                            type="email"
                            :placeholder="$t('calendar.email')"
                            class="form-input participant-input"
                          />
                        </div>

                        <div class="input-group">
                          <i class="ri-phone-line input-icon-clean"></i>
                          <input
                            v-model="participant.phone"
                            type="tel"
                            :placeholder="$t('calendar.phone')"
                            class="form-input participant-input"
                          />
                        </div>
                      </div>

                      <button
                        v-if="editingEvent.participants.length > 1"
                        type="button"
                        @click="removeParticipantFromEdit(index)"
                        class="remove-btn"
                      >
                        <i class="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <!-- Modal Footer -->
            <div class="modal-footer-clean">
              <button type="button" class="btn-clean btn-danger" @click="deleteEvent">
                <i class="ri-delete-bin-line"></i>
                {{ $t("calendar.deleteEvent") }}
              </button>
              <button
                type="button"
                class="btn-clean btn-secondary"
                @click="closeEditEventModal"
              >
                {{ $t("calendar.cancel") }}
              </button>
              <button type="button" class="btn-clean btn-primary" @click="updateEvent">
                <i class="ri-save-line"></i>
                {{ $t("calendar.saveChanges") }}
              </button>
            </div>
          </div>
        </div>

        <!-- Drag Ghost -->
        <div
          v-if="isDragging"
          class="drag-ghost"
          :style="dragGhostStyle"
          :class="dragGhostClass"
        >
          <div class="ghost-content">
            <div class="ghost-title">{{ dragGhostContent.title }}</div>
            <div class="ghost-time">{{ dragGhostContent.time }}</div>
          </div>
        </div>

        <!-- Contact Search Modal (deprecated) -->
        <div
          v-if="showContactSearch"
          class="event-detail-modal"
          @click="closeContactSearch"
        >
          <div class="modal-content contact-search-modal" @click.stop>
            <!-- Modal Header -->
            <div class="modal-header-clean">
              <div class="header-content">
                <div class="header-icon-clean">
                  <i class="ri-contacts-line"></i>
                </div>
                <div class="header-text-clean">
                  <h3>Search Contacts</h3>
                  <p>Find and add contacts to your event</p>
                </div>
              </div>
              <button @click="closeContactSearch" class="close-btn-clean">
                <i class="ri-close-line"></i>
              </button>
            </div>

            <!-- Search Input -->
            <div class="contact-search-body">
              <div class="search-input-group">
                <i class="ri-search-line search-icon"></i>
                <input
                  v-model="contactSearchQuery"
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  class="contact-search-input"
                  @input="searchContacts"
                />
              </div>

              <!-- Search Results -->
              <div class="contact-results">
                <div v-if="isSearching" class="search-loading">
                  <i class="ri-loader-4-line spin"></i>
                  Searching...
                </div>

                <div
                  v-else-if="filteredContacts.length === 0 && contactSearchQuery"
                  class="no-results"
                >
                  <i class="ri-user-search-line"></i>
                  <p>No contacts found</p>
                </div>

                <div v-else-if="contactSearchQuery === ''" class="search-placeholder">
                  <i class="ri-search-2-line"></i>
                  <p>Start typing to search contacts</p>
                </div>

                <div v-else class="contacts-list">
                  <div
                    v-for="contact in filteredContacts"
                    :key="contact.id"
                    class="contact-item"
                    @click="selectContact(contact)"
                  >
                    <div class="contact-avatar">
                      <img
                        v-if="contact.avatar"
                        :src="contact.avatar"
                        :alt="contact.name"
                      />
                      <div v-else class="avatar-placeholder">
                        {{ contact.name.charAt(0).toUpperCase() }}
                      </div>
                    </div>

                    <div class="contact-info">
                      <h4 class="contact-name">{{ contact.name }}</h4>
                      <p class="contact-email">{{ contact.email }}</p>
                      <p v-if="contact.phone" class="contact-phone">
                        {{ contact.phone }}
                      </p>
                    </div>

                    <div class="contact-select">
                      <i class="ri-add-circle-line"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Leads Search Modal -->
        <div v-if="showLeadsSearch" class="modal" @click="closeLeadsSearch">
          <div class="modal-content contact-search-modal" @click.stop>
            <!-- Modal Header -->
            <div class="modal-header-clean">
              <div class="header-content">
                <div class="header-icon-clean">
                  <i class="ri-user-star-line"></i>
                </div>
                <div class="header-text-clean">
                  <h3>{{ t("calendar.searchLeadsTitle") }}</h3>
                  <p>{{ t("calendar.searchLeadsSubtitle") }}</p>
                </div>
              </div>
              <button @click="closeLeadsSearch" class="close-btn-clean">
                <i class="ri-close-line"></i>
              </button>
            </div>

            <!-- Search Input -->
            <div class="contact-search-body">
              <div class="search-input-group">
                <i class="ri-search-line search-icon"></i>
                <input
                  v-model="leadsSearchQuery"
                  type="text"
                  :placeholder="t('calendar.searchByNameEmailPhone')"
                  class="contact-search-input"
                  @input="searchLeads"
                />
              </div>

              <!-- Search Results -->
              <div class="contact-results">
                <div v-if="loadingLeads" class="search-loading">
                  <i class="ri-loader-4-line spin"></i>
                  {{ t("calendar.loadingLeads") }}
                </div>

                <div v-else-if="isSearchingLeads" class="search-loading">
                  <i class="ri-loader-4-line spin"></i>
                  {{ t("calendar.searching") }}
                </div>

                <div
                  v-else-if="filteredLeads.length === 0 && leadsSearchQuery"
                  class="no-results"
                >
                  <i class="ri-user-search-line"></i>
                  <p>{{ t("calendar.noLeadsFound") }}</p>
                </div>

                <div v-else-if="leadsSearchQuery === ''" class="search-placeholder">
                  <i class="ri-search-2-line"></i>
                  <p>{{ t("calendar.startTypingToSearchLeads") }}</p>
                </div>

                <div v-else class="contacts-list">
                  <div
                    v-for="lead in filteredLeads"
                    :key="lead.id"
                    class="contact-item"
                    @click="isEditMode ? selectLeadForEdit(lead) : selectLead(lead)"
                  >
                    <div class="contact-avatar">
                      <img
                        v-if="lead.profileImageLink"
                        :src="lead.profileImageLink"
                        :alt="lead.name"
                      />
                      <div v-else class="avatar-placeholder">
                        {{ (lead.name || "L").charAt(0).toUpperCase() }}
                      </div>
                    </div>

                    <div class="contact-info">
                      <h4 class="contact-name">
                        {{ lead.name || t("calendar.noName") }}
                      </h4>
                      <p class="contact-email">
                        {{ lead.email || t("calendar.noEmail") }}
                      </p>
                      <p v-if="lead.phone" class="contact-phone">{{ lead.phone }}</p>
                      <p class="contact-status">
                        {{ t("calendar.status") }}:
                        {{ lead.status || t("calendar.unknown") }}
                      </p>
                    </div>

                    <div class="contact-select">
                      <i class="ri-add-circle-line"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Clients Search Modal -->
        <div
          v-if="showClientsSearch"
          class="modal"
          @click="closeClientsSearch"
        >
          <div class="modal-content contact-search-modal" @click.stop>
            <!-- Modal Header -->
            <div class="modal-header-clean">
              <div class="header-content">
                <div class="header-icon-clean">
                  <i class="ri-user-heart-line"></i>
                </div>
                <div class="header-text-clean">
                  <h3>{{ t("calendar.searchClientsTitle") }}</h3>
                  <p>{{ t("calendar.searchClientsSubtitle") }}</p>
                </div>
              </div>
              <button @click="closeClientsSearch" class="close-btn-clean">
                <i class="ri-close-line"></i>
              </button>
            </div>

            <!-- Search Input -->
            <div class="contact-search-body">
              <div class="search-input-group">
                <i class="ri-search-line search-icon"></i>
                <input
                  v-model="clientsSearchQuery"
                  type="text"
                  :placeholder="t('calendar.searchByNameEmailPhone')"
                  class="contact-search-input"
                  @input="searchClients"
                />
              </div>

              <!-- Search Results -->
              <div class="contact-results">
                <div v-if="loadingClients" class="search-loading">
                  <i class="ri-loader-4-line spin"></i>
                  {{ t("calendar.loadingClients") }}
                </div>

                <div v-else-if="isSearchingClients" class="search-loading">
                  <i class="ri-loader-4-line spin"></i>
                  {{ t("calendar.searching") }}
                </div>

                <div
                  v-else-if="filteredClients.length === 0 && clientsSearchQuery"
                  class="no-results"
                >
                  <i class="ri-user-search-line"></i>
                  <p>{{ t("calendar.noClientsFound") }}</p>
                </div>

                <div v-else-if="clientsSearchQuery === ''" class="search-placeholder">
                  <i class="ri-search-2-line"></i>
                  <p>{{ t("calendar.startTypingToSearchClients") }}</p>
                </div>

                <div v-else class="contacts-list">
                  <div
                    v-for="client in filteredClients"
                    :key="client.id"
                    class="contact-item"
                    @click="
                      isEditMode ? selectClientForEdit(client) : selectClient(client)
                    "
                  >
                    <div class="contact-avatar">
                      <div class="avatar-placeholder">
                        {{ client.name.charAt(0).toUpperCase() }}
                      </div>
                    </div>

                    <div class="contact-info">
                      <h4 class="contact-name">{{ client.name }}</h4>
                      <p class="contact-email">
                        {{ client.email || t("calendar.noEmail") }}
                      </p>
                      <p v-if="client.phone" class="contact-phone">{{ client.phone }}</p>
                      <p class="contact-status">
                        {{ t("calendar.status") }}:
                        {{ client.status || t("calendar.unknown") }}
                      </p>
                    </div>

                    <div class="contact-select">
                      <i class="ri-add-circle-line"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Calendar Settings Modal (Desktop and Mobile Calendar View) -->
        <div
          v-if="showSettingsModal"
          class="modal"
          :class="{ dark: isDarkMode }"
          @click="closeSettingsModal"
        >
          <div class="modal-content settings-modal apple-style" @click.stop>
            <!-- Modal Header -->
            <div class="settings-header apple-header">
              <div class="header-content">
                <div class="header-icon apple-icon">
                  <i class="ri-settings-3-line"></i>
                </div>
                <div class="header-text">
                  <h3>{{ t("calendar.settings.title") }}</h3>
                  <p>{{ t("calendar.settings.subtitle") }}</p>
                </div>
              </div>
            </div>

            <!-- Settings Navigation -->
            <div class="settings-nav">
              <button
                v-for="tab in settingsTabs"
                :key="tab.id"
                @click="activeTab = tab.id"
                :class="{ active: activeTab === tab.id }"
                class="nav-tab"
              >
                <i :class="tab.icon"></i>
                <span>{{ t(tab.nameKey) }}</span>
              </button>
            </div>

            <!-- Modal Body -->
            <div class="settings-body apple-body">
              <!-- Working Hours Tab -->
              <div v-if="activeTab === 'hours'" class="settings-tab">
                <div class="setting-group">
                  <div class="group-header">
                    <h4>{{ t("calendar.settings.businessHours") }}</h4>
                    <p>{{ t("calendar.settings.businessHoursDescription") }}</p>
                  </div>

                  <!-- Loading State for Availability -->
                  <div v-if="loadingAvailability" class="loading-state">
                    <i class="ri-loader-5-line animate-spin"></i>
                    <span>{{ t("calendar.settings.loadingWorkingHours") }}</span>
                  </div>

                  <div v-else>
                    <div class="setting-row">
                      <div class="setting-info">
                        <label>{{ t("calendar.settings.enableWorkingHours") }}</label>
                        <span class="setting-desc">{{
                          t("calendar.settings.enableWorkingHoursDescription")
                        }}</span>
                      </div>
                      <label class="apple-switch">
                        <input type="checkbox" v-model="workingHoursEnabled" />
                        <span class="switch-slider"></span>
                      </label>
                    </div>

                    <div v-if="workingHoursEnabled" class="working-hours-compact">
                      <div class="days-header">
                        <span class="day-label">{{ t("calendar.settings.day") }}</span>
                        <span class="hours-label">{{
                          t("calendar.settings.workingHours")
                        }}</span>
                        <span class="status-label">{{
                          t("calendar.settings.status")
                        }}</span>
                      </div>

                      <div
                        v-for="day in workingDays"
                        :key="day.key"
                        class="day-row"
                        :class="{ disabled: !day.enabled }"
                      >
                        <div class="day-info">
                          <label class="day-checkbox">
                            <input type="checkbox" v-model="day.enabled" />
                            <span class="checkmark"></span>
                          </label>
                          <div class="day-name">
                            <span class="full-name">{{
                              t(`calendar.days.${day.name.toLowerCase()}`)
                            }}</span>
                            <span class="short-name">{{
                              t(`calendar.dayAbbreviations.${day.name.toLowerCase()}`)
                            }}</span>
                          </div>
                        </div>

                        <div class="time-controls" v-if="day.enabled">
                          <input
                            v-model="day.startTime"
                            type="time"
                            class="time-input compact"
                          />
                          <span class="time-separator">–</span>
                          <input
                            v-model="day.endTime"
                            type="time"
                            class="time-input compact"
                          />
                        </div>

                        <div class="day-status">
                          <span v-if="day.enabled" class="status-badge active">{{
                            t("calendar.settings.active")
                          }}</span>
                          <span v-else class="status-badge inactive">{{
                            t("calendar.settings.off")
                          }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Save Button -->
                    <div class="availability-actions">
                      <button
                        @click="saveEmployeeAvailability"
                        class="btn-primary"
                        :disabled="savingAvailability"
                      >
                        <i
                          v-if="savingAvailability"
                          class="ri-loader-5-line animate-spin"
                        ></i>
                        <i v-else class="ri-save-line"></i>
                        {{ t("calendar.settings.saveWorkingHours") }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Break Times Tab -->
              <div v-if="activeTab === 'breaks'" class="settings-tab">
                <div class="setting-group">
                  <div class="group-header">
                    <h4>{{ t("calendar.settings.breakTimes") }}</h4>
                    <p>{{ t("calendar.settings.breakTimesDescription") }}</p>
                    <button @click="addBlockedTime" class="add-btn compact">
                      <i class="ri-add-line"></i>
                      {{ t("calendar.settings.addBreak") }}
                    </button>
                  </div>

                  <!-- Loading State for Breaks -->
                  <div v-if="loadingBreaks" class="loading-state">
                    <i class="ri-loader-5-line animate-spin"></i>
                    <span>{{ t("calendar.settings.loadingBreaks") }}</span>
                  </div>

                  <div v-else class="breaks-list">
                    <div
                      v-for="(block, index) in blockedTimes"
                      :key="block.id || index"
                      class="break-item"
                    >
                      <div class="break-header">
                        <input
                          v-if="block.isEditing"
                          v-model="block.name"
                          type="text"
                          class="break-name editing"
                          :placeholder="t('calendar.settings.breakNamePlaceholder')"
                        />
                        <span v-else class="break-name">{{ block.name }}</span>

                        <div class="break-actions">
                          <button
                            v-if="!block.isEditing"
                            @click="editBreak(index)"
                            class="edit-btn compact"
                            :title="t('common.edit')"
                          >
                            <i class="ri-edit-line"></i>
                          </button>
                          <button
                            @click="removeBlockedTime(index)"
                            class="remove-btn compact"
                            :title="t('common.delete')"
                            :disabled="savingBreak"
                          >
                            <i class="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>

                      <div v-if="block.isEditing" class="break-edit-form">
                        <div class="break-time">
                          <label>{{ t("calendar.settings.startTime") }}:</label>
                          <input
                            v-model="block.startTime"
                            type="time"
                            class="time-input compact"
                          />
                          <span class="time-separator">–</span>
                          <label>{{ t("calendar.settings.endTime") }}:</label>
                          <input
                            v-model="block.endTime"
                            type="time"
                            class="time-input compact"
                          />
                        </div>

                        <div class="break-days">
                          <span class="days-label"
                            >{{ t("calendar.settings.activeDays") }}:</span
                          >
                          <div class="days-pills">
                            <label
                              v-for="day in daysOfWeek"
                              :key="day.value"
                              class="day-pill"
                              :class="{ active: block.days.includes(day.value) }"
                            >
                              <input
                                type="checkbox"
                                v-model="block.days"
                                :value="day.value"
                              />
                              <span>{{ day.label }}</span>
                            </label>
                          </div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="form-actions" :class="{ rtl: isRTL }">
                          <button
                            @click="cancelEditBreak(index)"
                            class="btn-secondary"
                            :disabled="savingBreak"
                          >
                            <i class="ri-close-line"></i>
                            {{ t("common.cancel") }}
                          </button>
                          <button
                            @click="saveBreak(block, index)"
                            class="btn-primary"
                            :disabled="savingBreak"
                          >
                            <i
                              v-if="savingBreak"
                              class="ri-loader-5-line animate-spin"
                            ></i>
                            <i v-else class="ri-save-line"></i>
                            {{ t("common.save") }}
                          </button>
                        </div>
                      </div>

                      <div v-else class="break-summary">
                        <div class="time-display">
                          <i class="ri-time-line"></i>
                          <span>{{ block.startTime }} - {{ block.endTime }}</span>
                        </div>
                        <div
                          v-if="block.days && block.days.length > 0"
                          class="days-display"
                        >
                          <i class="ri-calendar-line"></i>
                          <span>{{ getTranslatedDays(block.days).join(", ") }}</span>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="!loadingBreaks && blockedTimes.length === 0"
                      class="empty-state"
                    >
                      <i class="ri-time-line"></i>
                      <span>{{ t("calendar.settings.noBreaksDefined") }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Appointment Types Tab -->
              <div v-if="activeTab === 'types'" class="settings-tab">
                <div class="setting-group">
                  <div class="group-header">
                    <h4>{{ t("calendar.settings.appointmentTypes") }}</h4>
                    <p>{{ t("calendar.settings.appointmentTypesDescription") }}</p>
                    <button @click="addAppointmentType" class="add-btn compact">
                      <i class="ri-add-line"></i>
                      {{ t("calendar.settings.newType") }}
                    </button>
                  </div>

                  <div class="types-list">
                    <!-- Loading State for Settings -->
                    <div v-if="loadingAppointmentTypes" class="loading-state">
                      <i class="ri-loader-5-line animate-spin"></i>
                      <span>{{ t("calendar.settings.loadingAppointmentTypes") }}</span>
                    </div>

                    <div
                      v-else
                      v-for="(type, index) in appointmentTypes"
                      :key="type.id || index"
                      class="type-item"
                      :style="{
                        borderLeftColor: type.colorCode || getColorValue(type.color),
                      }"
                    >
                      <!-- Type Header -->
                      <div class="type-header">
                        <div
                          class="type-icon"
                          :style="{
                            backgroundColor: type.colorCode || getColorValue(type.color),
                          }"
                        >
                          <i class="ri-calendar-event-line"></i>
                        </div>
                        <div class="type-details">
                          <input
                            v-if="type.isEditing"
                            v-model="type.name"
                            type="text"
                            class="type-name editing"
                            :placeholder="
                              t('calendar.settings.appointmentTypeNamePlaceholder')
                            "
                          />
                          <span v-else class="type-name">{{ type.name }}</span>

                          <div class="type-meta">
                            <span class="duration"
                              >{{ type.duration }}
                              {{ t("calendar.settings.minutes") }}</span
                            >
                            <span
                              v-if="type.tag"
                              class="tag"
                              :style="{ backgroundColor: type.tagColorCode }"
                              >{{ type.tag }}</span
                            >
                          </div>
                        </div>

                        <div class="type-actions">
                          <button
                            v-if="!type.isEditing"
                            @click="editAppointmentType(index)"
                            class="edit-btn compact"
                            :title="t('common.edit')"
                          >
                            <i class="ri-edit-line"></i>
                          </button>
                          <button
                            @click="removeAppointmentType(index)"
                            class="remove-btn compact"
                            :title="t('common.delete')"
                            :disabled="savingAppointmentType"
                          >
                            <i class="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>

                      <!-- Extended Edit Form -->
                      <div v-if="type.isEditing" class="type-edit-form">
                        <!-- Basic Info Row -->
                        <div class="form-row">
                          <div class="form-group">
                            <label class="duration-label"
                              >{{ t("calendar.settings.duration") }} (<span
                                class="minutes-text"
                                >{{ t("calendar.settings.minutes") }}</span
                              >)</label
                            >
                            <div class="duration-input">
                              <button
                                @click="adjustDuration(type, -15)"
                                class="duration-btn"
                              >
                                -
                              </button>
                              <input
                                v-model.number="type.duration"
                                type="number"
                                min="15"
                                max="480"
                                step="15"
                                @blur="validateDuration(type)"
                                class="duration-number-input"
                              />
                              <button
                                @click="adjustDuration(type, 15)"
                                class="duration-btn"
                              >
                                +
                              </button>
                            </div>
                          </div>

                          <div class="form-group">
                            <label>{{ t("calendar.settings.backgroundColor") }}</label>
                            <div class="color-grid">
                              <div
                                v-for="color in colorOptions"
                                :key="color.value"
                                class="color-dot"
                                :class="{ active: type.color === color.value }"
                                :style="{ backgroundColor: color.bg }"
                                @click="
                                  type.color = color.value;
                                  type.colorCode = color.bg;
                                "
                                :title="color.value"
                              ></div>
                            </div>
                          </div>
                        </div>

                        <!-- Tag Row -->
                        <div class="form-row">
                          <div class="form-group">
                            <label>{{ t("calendar.settings.tag") }}</label>
                            <input
                              v-model="type.tag"
                              type="text"
                              class="form-input tag-input"
                              :placeholder="t('calendar.settings.tagPlaceholder')"
                            />
                          </div>

                          <div class="form-group">
                            <label>{{ t("calendar.settings.tagColor") }}</label>
                            <div class="color-grid">
                              <div
                                v-for="color in colorOptions"
                                :key="'tag-' + color.value"
                                class="color-dot"
                                :class="{ active: type.tagColorCode === color.bg }"
                                :style="{ backgroundColor: color.bg }"
                                @click="type.tagColorCode = color.bg"
                                :title="color.value"
                              ></div>
                            </div>
                          </div>
                        </div>

                        <!-- Access Control -->
                        <div class="form-row">
                          <div class="form-group full-width">
                            <label class="checkbox-label">
                              <input type="checkbox" v-model="type.canAllUsersSee" />
                              {{ t("calendar.settings.allUsersCanSee") }}
                            </label>
                          </div>
                        </div>

                        <!-- Specific Users Selection -->
                        <div v-if="!type.canAllUsersSee" class="form-row">
                          <div class="form-group full-width">
                            <label
                              >{{ t("calendar.settings.selectSpecificUsers") }}:</label
                            >
                            <div class="users-grid" v-if="!loadingUsers">
                              <label
                                v-for="user in users"
                                :key="user.id"
                                class="user-checkbox"
                              >
                                <input
                                  type="checkbox"
                                  :value="user.id"
                                  v-model="type.selectedUsers"
                                />
                                <span>{{ user.full_name }}</span>
                              </label>
                            </div>
                            <div v-else class="loading-users">
                              <i class="ri-loader-5-line animate-spin"></i>
                              <span>{{ t("calendar.settings.loadingUsers") }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- Product Linking -->
                        <div class="form-row">
                          <div class="form-group full-width">
                            <label>{{ t("calendar.settings.linkToProduct") }}</label>
                            <div class="product-search-container" v-if="!loadingProducts">
                              <!-- Product Search Input -->
                              <input
                                v-model="productSearchQuery"
                                type="text"
                                class="form-input product-search-input"
                                :placeholder="
                                  getSelectedProductName(type.productId) ||
                                  t('calendar.settings.searchProductPlaceholder')
                                "
                                @focus="showProductDropdown = true"
                                @input="showProductDropdown = true"
                              />

                              <!-- Clear Selection Button -->
                              <button
                                v-if="type.productId"
                                @click="clearProductSelection(type)"
                                class="clear-product-btn"
                                type="button"
                              >
                                <i class="ri-close-line"></i>
                              </button>

                              <!-- Search Results Dropdown -->
                              <div
                                v-if="
                                  showProductDropdown &&
                                  (filteredProducts.length > 0 ||
                                    productSearchQuery.trim())
                                "
                                class="product-dropdown"
                              >
                                <div
                                  v-if="
                                    filteredProducts.length === 0 &&
                                    productSearchQuery.trim()
                                  "
                                  class="no-results"
                                >
                                  <i class="ri-search-line"></i>
                                  <span
                                    >{{ t("calendar.settings.noProductResults") }} "{{
                                      productSearchQuery
                                    }}"</span
                                  >
                                </div>
                                <div
                                  v-for="product in filteredProducts.slice(0, 10)"
                                  :key="product.id"
                                  @click="selectProduct(product, type)"
                                  class="product-option"
                                  :class="{ selected: type.productId === product.id }"
                                >
                                  <div class="product-info">
                                    <div class="product-name">{{ product.title }}</div>
                                    <div class="product-price" v-if="product.price">
                                      ₪{{ product.price }}
                                    </div>
                                  </div>
                                  <i
                                    v-if="type.productId === product.id"
                                    class="ri-check-line selected-icon"
                                  ></i>
                                </div>
                              </div>
                            </div>

                            <div v-else-if="loadingProducts" class="loading-products">
                              <i class="ri-loader-5-line animate-spin"></i>
                              <span>{{ t("calendar.settings.loadingProducts") }}</span>
                            </div>
                            <div v-else-if="products.length === 0" class="no-products">
                              <i class="ri-information-line"></i>
                              <span>{{ t("calendar.settings.noProductsFound") }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="form-actions" :class="{ rtl: isRTL }">
                          <button
                            @click="cancelEditAppointmentType(index)"
                            class="btn-secondary"
                            :disabled="savingAppointmentType"
                          >
                            <i class="ri-close-line"></i>
                            {{ t("common.cancel") }}
                          </button>
                          <button
                            @click="saveAppointmentType(type, index)"
                            class="btn-primary"
                            :disabled="savingAppointmentType"
                          >
                            <i
                              v-if="savingAppointmentType"
                              class="ri-loader-5-line animate-spin"
                            ></i>
                            <i v-else class="ri-save-line"></i>
                            {{ t("common.save") }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Modal Footer -->
            <div class="settings-footer apple-footer" :class="{ rtl: isRTL }">
              <button class="btn-reset" @click="resetSettings">
                <i class="ri-refresh-line"></i>
                {{ t("common.reset") }}
              </button>
              <div class="footer-info">
                <i class="ri-information-line"></i>
                <span>{{ t("calendar.settings.changesSavedAutomatically") }}</span>
              </div>
              <button class="btn-done" @click="closeSettingsModal">
                <i class="ri-check-line"></i>
                {{ t("common.done") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Container -->
    <div class="toast-container">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="toast"
        :class="`toast-${toast.type}`"
        @click="removeToast(toast.id)"
      >
        <div class="toast-icon">
          <i v-if="toast.type === 'success'" class="ri-check-circle-line"></i>
          <i v-else-if="toast.type === 'error'" class="ri-error-warning-line"></i>
          <i v-else-if="toast.type === 'warning'" class="ri-alert-line"></i>
          <i v-else class="ri-information-line"></i>
        </div>
        <div class="toast-content">
          <span>{{ toast.message }}</span>
        </div>
        <button class="toast-close" @click.stop="removeToast(toast.id)">
          <i class="ri-close-line"></i>
        </button>
      </div>
    </div>

    <!-- Confirm Popup -->
    <div v-if="showConfirmPopup" class="popup-overlay" @click="closeConfirmPopup">
      <div class="popup-content" :class="`popup-${confirmPopup.type}`" @click.stop>
        <div class="popup-header">
          <div class="popup-icon">
            <i v-if="confirmPopup.type === 'success'" class="ri-check-circle-line"></i>
            <i
              v-else-if="confirmPopup.type === 'danger'"
              class="ri-error-warning-line"
            ></i>
            <i v-else-if="confirmPopup.type === 'warning'" class="ri-alert-line"></i>
            <i v-else class="ri-information-line"></i>
          </div>
          <h3>{{ confirmPopup.title }}</h3>
        </div>
        <div class="popup-body">
          <p>{{ confirmPopup.message }}</p>
        </div>
        <div class="popup-footer">
          <button class="btn-cancel" @click="closeConfirmPopup">
            {{ confirmPopup.cancelText }}
          </button>
          <button
            class="btn-confirm"
            :class="`btn-${confirmPopup.type}`"
            @click="confirmAction"
          >
            {{ confirmPopup.confirmText }}
          </button>
        </div>
      </div>
    </div>
    

    <!-- Simple Confirm Dialog -->
    <div v-if="showSimpleConfirm" class="popup-overlay" @click="closeSimpleConfirm">
      <div class="popup-content popup-warning" @click.stop>
        <div class="popup-header">
          <div class="popup-icon">
            <i class="ri-question-line"></i>
          </div>
          <h3>{{ t("common.confirm") }}</h3>
        </div>
        <div class="popup-body">
          <p>{{ simpleConfirmMessage }}</p>
        </div>
        <div class="popup-footer">
          <button class="btn-cancel" @click="closeSimpleConfirm">
            {{ t("common.cancel") }}
          </button>
          <button class="btn-confirm btn-warning" @click="confirmSimpleAction">
            {{ t("common.confirm") }}
          </button>
        </div>
      </div>
    </div>
  </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from "vue";
import { useI18n } from "vue-i18n";
import { supabase } from "@/lib/supabaseClient";
import defaultProfileImage from "@/assets/images/admin.png";

// Internationalization
const { t, locale } = useI18n();

// Check if current locale is RTL
const isRTL = computed(() => ["he", "ar"].includes(locale.value));

// Dark mode detection with reactive updates
const isDarkMode = ref(document.body.classList.contains("dark"));
// Loading state for initial data fetch
const isLoading = ref(true);

// Watch for theme changes
const updateDarkMode = () => {
  isDarkMode.value = document.body.classList.contains("dark");
};

// Create a MutationObserver to watch for class changes on body
let themeObserver: MutationObserver | null = null;

// User Profile data
const userDisplayName = ref("Calendar Manager");
const userRole = ref("Meeting Coordinator");
const userProfilePicture = ref(defaultProfileImage);
const currentUserId = ref<string>("");

// User permissions for calendar access
const userPermissions = ref({
  view_all_calendars: false,
  is_owner: false,
  edit_all_leads: false,
  view_all_leads: false,
  can_invite_users: false,
});

// Calendar users management
const allUsers = ref<any[]>([]);
const selectedUserCalendars = ref<string[]>([]);
const allUsersEventsForCounting = ref<any[]>([]);

// Helper function to get current user's organization ID
const getCurrentUserOrgId = async () => {
  // Get current user from Supabase Auth
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError) throw userError;

  if (!userData.user) {
    throw new Error("User is not authenticated");
  }

  // Get organization ID from user profile
  const { data: profileData, error: profileError } = await supabase
    .from("user_profiles")
    .select("org_id")
    .eq("user_id", userData.user.id)
    .single();

  if (profileError) {
    console.error("Error fetching user profile:", profileError);
    throw new Error("Error fetching user profile");
  }

  if (!profileData || !profileData.org_id) {
    throw new Error("User is not associated with an organization");
  }

  return {
    userId: userData.user.id,
    organizationId: profileData.org_id,
  };
};

// State
const currentView = ref("month");
const currentDate = ref(new Date()); // תאריך נוכחי
const miniCalendarDate = ref(new Date()); // תאריך נוכחי למיני קלנדר
const selectedDay = ref<Date | null>(null);
const selectedEvent = ref<any>(null);

// Add Event Modal state
const showAddEventModal = ref(false);
const showEditEventModal = ref(false);
const showSettingsModal = ref(false);
const editingEvent = ref<any>(null);
const newEvent = ref({
  title: "",
  type: "",
  date: "",
  startTime: "",
  endTime: "",
  location: "",
  description: "",
  participants: [{ name: "", email: "", phone: "" }],
});

// Contact Search state (deprecated)
const showContactSearch = ref(false);
const contactSearchQuery = ref("");
const isSearching = ref(false);
const filteredContacts = ref<any[]>([]);

// Leads Search state
const showLeadsSearch = ref(false);
const leadsSearchQuery = ref("");
const isSearchingLeads = ref(false);
const filteredLeads = ref<any[]>([]);
const leads = ref<any[]>([]);
const loadingLeads = ref(false);
const isEditMode = ref(false); // Track if search is for edit modal

// Clients Search state
const showClientsSearch = ref(false);
const clientsSearchQuery = ref("");
const isSearchingClients = ref(false);
const filteredClients = ref<any[]>([]);
const clients = ref<any[]>([]);
const loadingClients = ref(false);

// Toast and Popup state
const toasts = ref<any[]>([]);
const showConfirmPopup = ref(false);
const confirmPopup = ref({
  title: "",
  message: "",
  confirmText: "",
  cancelText: "",
  confirmAction: null as (() => void) | null,
  type: "warning", // 'warning', 'danger', 'info', 'success'
});

// Additional popup for simple confirmations
const showSimpleConfirm = ref(false);
const simpleConfirmMessage = ref("");
const simpleConfirmCallback = ref<(() => void) | null>(null);

// Calendar Settings state
const showSettings = ref(false);
const activeTab = ref("hours");

const settingsTabs = ref([
  { id: "hours", nameKey: "calendar.settings.hours", icon: "ri-time-line" },
  { id: "breaks", nameKey: "calendar.settings.breaks", icon: "ri-pause-circle-line" },
  { id: "types", nameKey: "calendar.settings.types", icon: "ri-bookmark-line" },
]);

const workingHoursEnabled = ref(true);

const miniCalendarDays = computed(() => [
  t("calendar.dayAbbreviations.sunday"),
  t("calendar.dayAbbreviations.monday"),
  t("calendar.dayAbbreviations.tuesday"),
  t("calendar.dayAbbreviations.wednesday"),
  t("calendar.dayAbbreviations.thursday"),
  t("calendar.dayAbbreviations.friday"),
  t("calendar.dayAbbreviations.saturday"),
]);

// Month view computed properties
const monthViewDayHeaders = computed(() => [
  t("calendar.days.sunday") || "Sunday",
  t("calendar.days.monday") || "Monday", 
  t("calendar.days.tuesday") || "Tuesday",
  t("calendar.days.wednesday") || "Wednesday",
  t("calendar.days.thursday") || "Thursday",
  t("calendar.days.friday") || "Friday",
  t("calendar.days.saturday") || "Saturday",
]);

// Mobile version with 2 letters only
const monthViewDayHeadersMobile = computed(() => [
  (t("calendar.days.sunday") || "Sunday").substring(0, 2),
  (t("calendar.days.monday") || "Monday").substring(0, 2), 
  (t("calendar.days.tuesday") || "Tuesday").substring(0, 2),
  (t("calendar.days.wednesday") || "Wednesday").substring(0, 2),
  (t("calendar.days.thursday") || "Thursday").substring(0, 2),
  (t("calendar.days.friday") || "Friday").substring(0, 2),
  (t("calendar.days.saturday") || "Saturday").substring(0, 2),
]);

const getCurrentMonthYear = computed(() => {
  const months = [
    t("calendar.months.january") || "January",
    t("calendar.months.february") || "February",
    t("calendar.months.march") || "March",
    t("calendar.months.april") || "April",
    t("calendar.months.may") || "May",
    t("calendar.months.june") || "June",
    t("calendar.months.july") || "July",
    t("calendar.months.august") || "August",
    t("calendar.months.september") || "September",
    t("calendar.months.october") || "October",
    t("calendar.months.november") || "November",
    t("calendar.months.december") || "December",
  ];
  return `${months[currentDate.value.getMonth()]} ${currentDate.value.getFullYear()}`;
});

const getMonthViewDays = computed(() => {
  const days = [];
  const firstDay = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth(), 1);
  const lastDay = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 0);
  const startOfWeek = new Date(firstDay);
  const endOfWeek = new Date(lastDay);
  
  // Start from Sunday of the first week
  startOfWeek.setDate(firstDay.getDate() - firstDay.getDay());
  
  // End on Saturday of the last week
  endOfWeek.setDate(lastDay.getDate() + (6 - lastDay.getDay()));
  
  const current = new Date(startOfWeek);
  while (current <= endOfWeek) {
    const today = new Date();
    const isToday = current.toDateString() === today.toDateString();
    const isOtherMonth = current.getMonth() !== currentDate.value.getMonth();
    
    days.push({
      date: current.getFullYear() + '-' + 
            String(current.getMonth() + 1).padStart(2, '0') + '-' + 
            String(current.getDate()).padStart(2, '0'),
      dayNumber: current.getDate(),
      isToday,
      isOtherMonth,
    });
    
    current.setDate(current.getDate() + 1);
  }
  
  return days;
});

const showNotificationBadge = computed(() => true);

const workingDays = ref([
  {
    key: "Mon",
    name: "Monday",
    short: "Mon",
    enabled: true,
    startTime: "09:00",
    endTime: "17:00",
  },
  {
    key: "Tue",
    name: "Tuesday",
    short: "Tue",
    enabled: true,
    startTime: "09:00",
    endTime: "17:00",
  },
  {
    key: "Wed",
    name: "Wednesday",
    short: "Wed",
    enabled: true,
    startTime: "09:00",
    endTime: "17:00",
  },
  {
    key: "Thu",
    name: "Thursday",
    short: "Thu",
    enabled: true,
    startTime: "09:00",
    endTime: "17:00",
  },
  {
    key: "Fri",
    name: "Friday",
    short: "Fri",
    enabled: true,
    startTime: "09:00",
    endTime: "17:00",
  },
  {
    key: "Sat",
    name: "Saturday",
    short: "Sat",
    enabled: false,
    startTime: "10:00",
    endTime: "14:00",
  },
  {
    key: "Sun",
    name: "Sunday",
    short: "Sun",
    enabled: false,
    startTime: "10:00",
    endTime: "14:00",
  },
]);

const blockedTimes = ref<any[]>([]);

// Appointment Types - loaded from database
const appointmentTypes = ref<any[]>([]);
const loadingAppointmentTypes = ref(true);
const savingAppointmentType = ref(false);

// Breaks - loaded from database
const loadingBreaks = ref(true);
const savingBreak = ref(false);

// Employee Availability - loaded from database
const loadingAvailability = ref(true);
const savingAvailability = ref(false);
const products = ref<any[]>([]);
const users = ref<any[]>([]);
const loadingProducts = ref(false);
const loadingUsers = ref(false);
const productSearchQuery = ref("");
const showProductDropdown = ref(false);

const colorOptions = ref([
  { value: "personal", bg: "#fbbf24" },
  { value: "work", bg: "#3b82f6" },
  { value: "health", bg: "#ec4899" },
  { value: "meeting", bg: "#22c55e" },
  { value: "call", bg: "#8b5cf6" },
  { value: "task", bg: "#6b7280" },
]);

// Days of week for breaks selection
const daysOfWeek = computed(() => [
  { value: "Mon", label: t("calendar.days.monday") },
  { value: "Tue", label: t("calendar.days.tuesday") },
  { value: "Wed", label: t("calendar.days.wednesday") },
  { value: "Thu", label: t("calendar.days.thursday") },
  { value: "Fri", label: t("calendar.days.friday") },
  { value: "Sat", label: t("calendar.days.saturday") },
  { value: "Sun", label: t("calendar.days.sunday") },
]);

// Function to translate day abbreviations
const getTranslatedDays = (dayValues: string[]) => {
  return dayValues.map((dayValue) => {
    const dayInfo = daysOfWeek.value.find((day) => day.value === dayValue);
    return dayInfo ? dayInfo.label : dayValue;
  });
};

// Sample contacts data
const contacts = ref([
  {
    id: 1,
    name: "John Smith",
    email: "john.smith@example.com",
    phone: "+1-555-0123",
    avatar: "/api/placeholder/40/40",
  },
  {
    id: 2,
    name: "Sarah Johnson",
    email: "sarah.j@company.com",
    phone: "+1-555-0124",
    avatar: "/api/placeholder/40/40",
  },
  {
    id: 3,
    name: "Michael Brown",
    email: "michael.brown@email.com",
    phone: "+1-555-0125",
    avatar: null,
  },
  {
    id: 4,
    name: "Emily Davis",
    email: "emily.davis@work.com",
    phone: "+1-555-0126",
    avatar: "/api/placeholder/40/40",
  },
  {
    id: 5,
    name: "David Wilson",
    email: "david.w@business.com",
    phone: "+1-555-0127",
    avatar: null,
  },
  {
    id: 6,
    name: "Lisa Anderson",
    email: "lisa.anderson@corp.com",
    phone: "+1-555-0128",
    avatar: "/api/placeholder/40/40",
  },
]);

// Drag and Drop state
const isDragging = ref(false);
const draggingEvent = ref<string | null>(null);
const dragOverSlot = ref<string | null>(null);
const dragGhostStyle = ref({});
const dragGhostClass = ref("");
const dragGhostContent = ref({ title: "", time: "" });

// Events data - will be loaded from database
const events = ref<any[]>([]);

// Computed properties
const weekDays = computed(() => {
  console.log(
    "📆 Computing week days for current date:",
    currentDate.value.toISOString()
  );

  const days = [];
  const startOfWeek = new Date(currentDate.value);
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay()); // Start from Sunday

  console.log("📅 Week starts at:", startOfWeek.toISOString());

  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek);
    day.setDate(startOfWeek.getDate() + i);

    days.push({
      date: day.toISOString().split("T")[0],
      dayName: day.toLocaleDateString("en-US", { weekday: "long" }),
      dayNumber: day.getDate(),
      isToday: isToday(day),
      isSelected: selectedDay.value && isSameDay(day, selectedDay.value),
    });
  }

  return days;
});

// Add computed property for current view days
const currentViewDays = computed(() => {
  if (currentView.value === "day") {
    // Single day view
    const day = new Date(currentDate.value);
    const dayOfWeek = day.getDay();
    const dayNames = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    return [
      {
        date: day.toISOString().split("T")[0],
        dayName: t(`calendar.days.${dayNames[dayOfWeek]}`),
        dayNumber: day.getDate(),
        isToday: isToday(day),
        isSelected: true,
        isCurrentMonth: true,
      },
    ];
  } else {
    // Week view (default)
    const dayNames = [
      t("calendar.days.sunday"),
      t("calendar.days.monday"),
      t("calendar.days.tuesday"),
      t("calendar.days.wednesday"),
      t("calendar.days.thursday"),
      t("calendar.days.friday"),
      t("calendar.days.saturday"),
    ];

    return weekDays.value.map((day) => {
      const dayDate = new Date(day.date);
      const dayOfWeek = dayDate.getDay();
      return {
        ...day,
        dayName: dayNames[dayOfWeek],
        isCurrentMonth: true,
      };
    });
  }
});

const hours = computed(() => {
  const hoursList = [];
  for (let i = 6; i <= 21; i++) {
    // 6 AM to 10 PM (22:00) - טווח מורחב
    hoursList.push(i);
  }
  return hoursList;
});

const getMiniCalendarTitle = computed(() => {
  const monthNames = [
    t("calendar.months.january"),
    t("calendar.months.february"),
    t("calendar.months.march"),
    t("calendar.months.april"),
    t("calendar.months.may"),
    t("calendar.months.june"),
    t("calendar.months.july"),
    t("calendar.months.august"),
    t("calendar.months.september"),
    t("calendar.months.october"),
    t("calendar.months.november"),
    t("calendar.months.december"),
  ];

  const month = miniCalendarDate.value.getMonth();
  const year = miniCalendarDate.value.getFullYear();

  return `${monthNames[month]} ${year}`;
});

const getMiniCalendarDays = computed(() => {
  const days = [];
  const year = miniCalendarDate.value.getFullYear();
  const month = miniCalendarDate.value.getMonth();

  const firstDay = new Date(year, month, 1);
  const startDate = new Date(firstDay);
  const dayOfWeek = firstDay.getDay();
  const mondayOffset =  - dayOfWeek;
  startDate.setDate(firstDay.getDate() + mondayOffset);

  for (let i = 0; i < 42; i++) {
    const day = new Date(startDate);
    day.setDate(startDate.getDate() + i);

    days.push({
      date: day,
      dayNumber: day.getDate(),
      isToday: isToday(day),
      isOtherMonth: day.getMonth() !== month,
      isSelected: selectedDay.value && isSameDay(day, selectedDay.value),
    });
  }

  return days;
});

// Helper functions
const isToday = (date: Date) => {
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

const isSameDay = (date1: Date, date2: Date) => {
  return date1.toDateString() === date2.toDateString();
};

const formatHour = (hour: number) => {
  const period = hour < 12 ? t("calendar.am") : t("calendar.pm");
  let displayHour = hour;

  if (hour === 0) displayHour = 12;
  else if (hour > 12) displayHour = hour - 12;

  return `${displayHour} ${period}`;
};

const getDayEvents = (dateStr: string) => {
  const dayEvents = events.value.filter((event) => {
    // בדיקה שהאירוע הוא תקין ויש לו תאריך התחלה
    if (!event || !event.start) {
      console.warn("⚠️ Event missing start date:", event);
      return false;
    }
    
    const eventDate = new Date(event.start);
    console.log("eventDate", eventDate);
    
    // בדיקה שהתאריך תקין
    if (isNaN(eventDate.getTime())) {
      console.warn("⚠️ Invalid event date:", event.start, "for event:", event);
      return false;
    }
    
    const eventDateStr = eventDate.toISOString().split("T")[0];
    console.log("eventDateStr", eventDateStr);
    return eventDateStr === dateStr;
  });

  // Log only if there are events for this day
  if (dayEvents.length > 0) {
    console.log(`Found ${dayEvents.length} events for ${dateStr}:`, dayEvents);
  }

  // מיון האירועים לפי זמן התחלה עם הגנה
  return dayEvents.sort((a, b) => {
    if (!a || !a.start || !b || !b.start) {
      console.warn("⚠️ Invalid events in sort:", a, b);
      return 0;
    }
    
    const timeA = new Date(a.start).getTime();
    const timeB = new Date(b.start).getTime();
    
    if (isNaN(timeA) || isNaN(timeB)) {
      console.warn("⚠️ Invalid dates in sort:", a.start, b.start);
      return 0;
    }
    
    return timeA - timeB;
  });
};

// Navigation functions
const changeView = (view: string) => {
  currentView.value = view;

  // Update navigation logic based on view
  if (view === "day") {
    // For day view, focus on a single day
    console.log("Switched to Day view");
  } else {
    // Week view (default)
    console.log("Switched to Week view");
  }
};

const navigateCalendar = (direction: "prev" | "next") => {
  const newDate = new Date(currentDate.value);

  if (currentView.value === "day") {
    // Navigate by day
    newDate.setDate(newDate.getDate() + (direction === "next" ? 1 : -1));
  } else {
    // Navigate by week (default)
    newDate.setDate(newDate.getDate() + (direction === "next" ? 7 : -7));
  }

  currentDate.value = newDate;
};

const goToToday = () => {
  currentDate.value = new Date();
};

const navigateMiniCalendar = (direction: "prev" | "next") => {
  const newDate = new Date(miniCalendarDate.value);
  newDate.setMonth(newDate.getMonth() + (direction === "next" ? 1 : -1));
  miniCalendarDate.value = newDate;
};

const selectMiniDay = (day: any) => {
  console.log("selectMiniDay", day);
  
  // בדיקה שהתאריך תקין
  if (!day || !day.date) {
    console.warn("⚠️ Invalid day object:", day);
    return;
  }
  
  const newDate = new Date(day.date);
  if (isNaN(newDate.getTime())) {
    console.warn("⚠️ Invalid date:", day.date);
    return;
  }
  
  selectedDay.value = newDate;
  currentDate.value = newDate;
};

const selectDay = (day: any) => {
  // בדיקה שהתאריך תקין
  if (!day || !day.date) {
    console.warn("⚠️ Invalid day object:", day);
    return;
  }
  
  const newDate = new Date(day.date);
  if (isNaN(newDate.getTime())) {
    console.warn("⚠️ Invalid date:", day.date);
    return;
  }
  
  selectedDay.value = newDate;
  currentDate.value = newDate;
  // Switch to day view when clicking a day
  currentView.value = "day";
};

// Month view helper methods
const getDayEventsForMonth = (dateStr: string) => {
  return getDayEvents(dateStr).slice(0, 3); // Show only first 3 events in month view
};

const getEventColorClasses = (event: any) => {
  // אם יש colorClass שנטען מהמסד נתונים, השתמש בו
  if (event.colorClass) {
    console.log(`🎨 Event "${event.title}" has colorClass: ${event.colorClass}`);
    const colorClassMap = {
      'event-blue': 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-800 dark:border-blue-700',
      'event-yellow': 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-300 dark:hover:bg-yellow-800 dark:border-yellow-700',
      'event-pink': 'bg-pink-100 text-pink-700 hover:bg-pink-200 border-pink-200 dark:bg-pink-900 dark:text-pink-300 dark:hover:bg-pink-800 dark:border-pink-700',
      'event-green': 'bg-green-100 text-green-700 hover:bg-green-200 border-green-200 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-800 dark:border-green-700',
      'event-purple': 'bg-purple-100 text-purple-700 hover:bg-purple-200 border-purple-200 dark:bg-purple-900 dark:text-purple-300 dark:hover:bg-purple-800 dark:border-purple-700',
      'event-gray': 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:border-gray-600',
    };
    const resultClass = colorClassMap[event.colorClass] || 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-800 dark:border-blue-700';
    console.log(`🎨 Applied classes for "${event.title}": ${resultClass}`);
    return resultClass;
  }
  
  // fallback למפת צבעים ישנה אם אין colorClass
  console.log(`⚠️ Event "${event.title}" has no colorClass, using fallback. Event type: ${event.type}, Event title: ${event.title}`);
  const colorMap = {
    'Personal': 'bg-primary-100 text-primary-700 hover:bg-primary-200 border-primary-200 dark:bg-primary-900 dark:text-primary-300 dark:hover:bg-primary-800 dark:border-primary-700',
    'Work': 'bg-green-100 text-green-700 hover:bg-green-200 border-green-200 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-800 dark:border-green-700',
    'Team Meeting': 'bg-primary-100 text-primary-700 hover:bg-primary-200 border-primary-200 dark:bg-primary-900 dark:text-primary-300 dark:hover:bg-primary-800 dark:border-primary-700',
    'Meeting': 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-300 dark:hover:bg-yellow-800 dark:border-yellow-700',
    'Follow-up': 'bg-green-100 text-green-700 hover:bg-green-200 border-green-200 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-800 dark:border-green-700',
    'Demo': 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-300 dark:hover:bg-yellow-800 dark:border-yellow-700',
    'Consultation': 'bg-primary-100 text-primary-700 hover:bg-primary-200 border-primary-200 dark:bg-primary-900 dark:text-primary-300 dark:hover:bg-primary-800 dark:border-primary-700',
    'Client Call': 'bg-green-100 text-green-700 hover:bg-green-200 border-green-200 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-800 dark:border-green-700',
  };
  
  const finalClass = colorMap[event.type] || colorMap[event.title] || 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:border-gray-600';
  console.log(`🎨 Applied fallback classes for "${event.title}": ${finalClass}`);
  return finalClass;
};

const createEventAt = (dateStr: string, hour: number, event?: MouseEvent) => {
  console.log("📅 Creating event at:", dateStr, "hour:", hour);

  // חישוב הדקות אם יש אירוע עכבר
  let minutes = 0;
  if (event) {
    const hourSlot = event.currentTarget as HTMLElement;
    const rect = hourSlot.getBoundingClientRect();
    const relativeY = event.clientY - rect.top;
    const percentY = relativeY / rect.height;

    // חישוב הדקות - כל רבע שעה (0, 15, 30, 45)
    if (percentY >= 0.75) {
      minutes = 45;
    } else if (percentY >= 0.5) {
      minutes = 30;
    } else if (percentY >= 0.25) {
      minutes = 15;
    }
  }

  // בדיקה אם כבר יש אירוע בשעה הזו
  const existingEvents = getDayEvents(dateStr).filter((event) => {
    if (!event || !event.start || !event.end) {
      console.warn("⚠️ Event missing start/end times:", event);
      return false;
    }
    
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end);
    
    if (isNaN(eventStart.getTime()) || isNaN(eventEnd.getTime())) {
      console.warn("⚠️ Invalid event times:", event.start, event.end);
      return false;
    }
    
    const eventStartHour = eventStart.getHours();
    const eventEndHour = eventEnd.getHours();

    // בדיקה אם האירוע חופף עם השעה הנבחרת
    return eventStartHour <= hour && eventEndHour >= hour;
  });

  console.log("📋 Found existing events:", existingEvents);

  if (existingEvents.length > 0) {
    showToast("calendar.alreadyScheduled", "warning");
    return;
  }

  // פתיחת מודל הוספת אירוע עם הזמן המוגדר מראש
  openAddEventModal(dateStr, hour, minutes);
};

const openAddEventModal = (dateStr?: string, hour?: number, minutes = 0) => {
  // Close event details modal if open
  if (selectedEvent.value) {
    closeEventDetail();
  }

  // איפוס הטופס
  const startMinutesStr = minutes.toString().padStart(2, "0");
  const endHour = minutes === 45 ? hour! + 1 : hour!;
  const endMinutes = minutes === 45 ? 0 : minutes + 15;
  const endMinutesStr = endMinutes.toString().padStart(2, "0");

  newEvent.value = {
    title: "",
    type: "",
    date: dateStr || new Date().toISOString().split("T")[0],
    startTime: hour ? `${hour.toString().padStart(2, "0")}:${startMinutesStr}` : "09:00",
    endTime: hour ? `${endHour.toString().padStart(2, "0")}:${endMinutesStr}` : "10:00",
    location: "",
    description: "",
    participants: [{ name: "", email: "", phone: "" }], // משתתפים עם שם ואמצעי יצירת קשר
  };

  showAddEventModal.value = true;
};

const closeAddEventModal = () => {
  showAddEventModal.value = false;
  newEvent.value = {
    title: "",
    type: "",
    date: "",
    startTime: "",
    endTime: "",
    location: "",
    description: "",
    participants: [{ name: "", email: "", phone: "" }], // משתתפים עם שם ואמצעי יצירת קשר
  };
};

const saveNewEvent = async () => {
  if (
    !newEvent.value.title ||
    !newEvent.value.type ||
    !newEvent.value.date ||
    !newEvent.value.startTime ||
    !newEvent.value.endTime
  ) {
    showToast("calendar.fillAllFields", "warning");
    return;
  }

  // יצירת תאריכי התחלה וסיום
  const startDateTime = new Date(`${newEvent.value.date}T${newEvent.value.startTime}`);
  const endDateTime = new Date(`${newEvent.value.date}T${newEvent.value.endTime}`);

  // בדיקה שזמן הסיום אחרי זמן ההתחלה
  if (endDateTime <= startDateTime) {
    showToast("calendar.endTimeAfterStart", "warning");
    return;
  }

  // בדיקה שהשעה לא תפוסה
  const hour = startDateTime.getHours();
  const dateStr = newEvent.value.date;

  console.log("🔍 Checking time slot:", dateStr, "at hour:", hour);

  // בדיקה האם יש אירועים חופפים
  const existingEvents = getDayEvents(dateStr).filter((event) => {
    if (!event || !event.start || !event.end) {
      console.warn("⚠️ Event missing start/end times:", event);
      return false;
    }
    
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end);
    
    if (isNaN(eventStart.getTime()) || isNaN(eventEnd.getTime())) {
      console.warn("⚠️ Invalid event times:", event.start, event.end);
      return false;
    }
    
    return (
      (startDateTime >= eventStart && startDateTime < eventEnd) ||
      (endDateTime > eventStart && endDateTime <= eventEnd) ||
      (startDateTime <= eventStart && endDateTime >= eventEnd)
    );
  });

  console.log("📋 Existing events at this time:", existingEvents);

  if (existingEvents.length > 0) {
    showToast("calendar.timeSlotOccupied", "warning");
    return;
  }

  if (isHourOccupied(dateStr, hour)) {
    showToast("calendar.timeSlotBlocked", "warning");
    return;
  }

  try {
    // קבלת userId ו-organizationId
    const { userId, organizationId } = await getCurrentUserOrgId();

    // סינון משתתפים שיש להם שם
    const validParticipants = newEvent.value.participants.filter(
      (p) => p.name.trim() !== ""
    );

    // מציאת סוג האירוע מהטבלה אם זה סוג מותאם אישית
    const appointmentType = appointmentTypes.value.find(
      (type) => type.name === newEvent.value.type
    );
    const eventTypeId = appointmentType?.id || null;

    // שמירת האירוע במסד נתונים
    console.log("🕐 Original startDateTime:", startDateTime.toString());
    console.log("🕐 Original endDateTime:", endDateTime.toString());
    console.log("🕐 Saving startTime as:", createLocalISOString(startDateTime));
    console.log("🕐 Saving endTime as:", createLocalISOString(endDateTime));
    
    const { data: eventData, error: eventError } = await supabase
      .from("calendar_events")
      .insert({
        user_id: userId,
        title: newEvent.value.title,
        start_time: createLocalISOString(startDateTime),
        end_time: createLocalISOString(endDateTime),
        event_type: eventTypeId,
        location: newEvent.value.location || null,
        description: newEvent.value.description || null,
        org_id: organizationId,
      })
      .select()
      .single();

    if (eventError) {
      console.error("Error creating event:", eventError);
      showToast("calendar.eventCreationError", "error");
      return;
    }

    console.log("Event created in database:", eventData);

    // שמירת המשתתפים
    if (validParticipants.length > 0 && eventData) {
      const participantsToInsert = validParticipants.map((participant) => ({
        event_id: eventData.id,
        lead_id: (participant as any).leadId || null,
        customer_id: (participant as any).clientId || null,
        name: participant.name,
        phone: participant.phone || null,
        email: participant.email || null,
      }));

      const { error: participantsError } = await supabase
        .from("calendar_events_participants")
        .insert(participantsToInsert);

      if (participantsError) {
        console.error("Error creating participants:", participantsError);
        // המשך גם אם יש שגיאה במשתתפים
      }
    }

    // קביעת צבע האירוע לפי הסוג
    const getEventColor = (type: string) => {
      // אם זה סוג מותאם אישית, השתמש בצבע שלו
      if (appointmentType && appointmentType.colorCode) {
        console.log(`🎨 Found appointment type with color: ${appointmentType.colorCode}`);
        const colorMap: { [key: string]: string } = {
          "#fbbf24": "event-yellow",
          "#3b82f6": "event-blue",
          "#ec4899": "event-pink",
          "#22c55e": "event-green",
          "#8b5cf6": "event-purple",
          "#6b7280": "event-gray",
        };
        return colorMap[appointmentType.colorCode] || "event-blue";
      }

      console.log(`⚠️ No appointment type found for "${type}", using fallback colors`);
      switch (type) {
        case "personal":
          return "event-yellow";
        case "work":
          return "event-blue";
        case "health":
          return "event-pink";
        case "meeting":
          return "event-green";
        case "call":
          return "event-purple";
        case "task":
          return "event-gray";
        default:
          return "event-blue";
      }
    };

    // יצירת אירוע חדש להצגה
    const newEventDisplay: any = {
      id: eventData.id,
      title: newEvent.value.title,
      type: newEvent.value.type,
      start: startDateTime,
      end: endDateTime,
      colorClass: getEventColor(newEvent.value.type),
      timeDisplay: `${newEvent.value.startTime} - ${newEvent.value.endTime}`,
      location: newEvent.value.location,
      description: newEvent.value.description,
      dateDisplay: startDateTime.toLocaleDateString("he-IL", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    };

    // הוספת משתתפים אם יש
    if (validParticipants.length > 0) {
      newEventDisplay.participants = validParticipants.map((p, index) => ({
        id: index + 1,
        name: p.name,
        email: p.email,
        phone: p.phone,
        avatar: "/api/placeholder/24/24",
      }));
    }

    // הוספת האירוע לרשימה
    events.value.push(newEventDisplay);

    // סגירת המודל
    closeAddEventModal();

    console.log("New event created successfully!");

    // טען מחדש את האירועים מהמסד נתונים
    await fetchCalendarEvents();
  } catch (error) {
    console.error("Error saving event:", error);
    showToast("calendar.eventSaveError", "error");
  }
};

// פונקציות לניהול משתתפים
const addParticipant = () => {
  newEvent.value.participants.push({ name: "", email: "", phone: "" });
};

const removeParticipant = (index: number) => {
  if (newEvent.value.participants.length > 1) {
    newEvent.value.participants.splice(index, 1);
  }
};

// פונקציות לניהול משתתפים בעריכה
const addParticipantToEdit = () => {
  if (!editingEvent.value.participants) {
    editingEvent.value.participants = [];
  }
  editingEvent.value.participants.push({ name: "", email: "", phone: "" });
};

const removeParticipantFromEdit = (index: number) => {
  if (editingEvent.value.participants && editingEvent.value.participants.length > 1) {
    editingEvent.value.participants.splice(index, 1);
  }
};

const openEvent = (event: any) => {
  selectedEvent.value = {
    ...event,
    timeRange: event.timeDisplay,
    dateDisplay: event.dateDisplay || "Today",
  };
};

const closeEventDetail = () => {
  selectedEvent.value = null;
};

// Drag and Drop handlers
const handleEventDragStart = (event: DragEvent, eventData: any) => {
  isDragging.value = true;
  draggingEvent.value = eventData.id;
  dragGhostContent.value = {
    title: eventData.title,
    time: eventData.timeDisplay,
  };
  dragGhostClass.value = eventData.colorClass;

  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(
      "text/plain",
      JSON.stringify({
        type: "event",
        event: eventData,
      })
    );
  }
};

const handleEventDragEnd = () => {
  isDragging.value = false;
  draggingEvent.value = null;
  dragOverSlot.value = null;
};

const handleTimeSlotDragOver = (event: DragEvent, dayDate: string, hour: number) => {
  event.preventDefault();
  dragOverSlot.value = `${dayDate}-${hour}`;

  // חישוב הדקות לפי מיקום העכבר
  const hourSlot = event.currentTarget as HTMLElement;
  const rect = hourSlot.getBoundingClientRect();
  const relativeY = event.clientY - rect.top;
  const percentY = relativeY / rect.height;

  // חישוב הדקות - כל רבע שעה (0, 15, 30, 45)
  let minutes = 0;
  if (percentY >= 0.75) {
    minutes = 45;
  } else if (percentY >= 0.5) {
    minutes = 30;
  } else if (percentY >= 0.25) {
    minutes = 15;
  }

  // Update drag ghost with new time preview
  if (isDragging.value && draggingEvent.value) {
    const originalEvent = events.value.find((e) => e.id === draggingEvent.value);
    if (originalEvent) {
      const originalStart = new Date(originalEvent.start);
      const originalEnd = new Date(originalEvent.end);
      const duration = originalEnd.getTime() - originalStart.getTime();

      const newStartDate = new Date(dayDate);
      newStartDate.setHours(hour, minutes, 0, 0);
      const newEndDate = new Date(newStartDate.getTime() + duration);

      const newTimeDisplay = `${formatTimeInLocalTimezone(newStartDate)} - ${formatTimeInLocalTimezone(newEndDate)}`;

      dragGhostContent.value = {
        title: originalEvent.title,
        time: newTimeDisplay,
      };
    }
  }

  updateDragGhostPosition(event);
};

const handleDragLeave = () => {
  dragOverSlot.value = null;
};

const handleTimeSlotDrop = async (event: DragEvent, dayDate: string, hour: number) => {
  event.preventDefault();

  try {
    const data = JSON.parse(event.dataTransfer?.getData("text/plain") || "{}");

    if (data.type === "event") {
      // חישוב הדקות לפי מיקום העכבר בתוך ה-slot
      const hourSlot = event.target as HTMLElement;
      const rect = hourSlot.getBoundingClientRect();
      const relativeY = event.clientY - rect.top;
      const percentY = relativeY / rect.height;

      // חישוב הדקות - כל רבע שעה (0, 15, 30, 45)
      let minutes = 0;
      if (percentY >= 0.75) {
        minutes = 45;
      } else if (percentY >= 0.5) {
        minutes = 30;
      } else if (percentY >= 0.25) {
        minutes = 15;
      }

      // בדיקה אם השעה החדשה תפוסה (למעט האירוע הנוכחי)
      const existingEvents = getDayEvents(dayDate).filter((existingEvent) => {
        if (existingEvent.id === data.event.id) return false; // לא לספור את האירוע שאנחנו מעבירים

        const eventStart = new Date(existingEvent.start);
        const eventEnd = new Date(existingEvent.end);
        const eventStartHour = eventStart.getHours();
        const eventEndHour = eventEnd.getHours();

        return eventStartHour <= hour && eventEndHour >= hour;
      });

      if (existingEvents.length > 0) {
        showToast("calendar.cannotMoveEvent", "warning");
        handleEventDragEnd();
        return;
      }

      await moveEventToTimeSlot(data.event, dayDate, hour, minutes);
    }
  } catch (error) {
    console.error("Error handling time slot drop:", error);
  }

  handleEventDragEnd();
};

const updateDragGhostPosition = (event: DragEvent) => {
  dragGhostStyle.value = {
    left: `${event.clientX + 10}px`,
    top: `${event.clientY - 10}px`,
    transform: "translate(0, 0)",
    pointerEvents: "none",
    zIndex: 9999,
  };
};

const moveEventToTimeSlot = async (
  eventData: any,
  dayDate: string,
  hour: number,
  minutes = 0
) => {
  // Find the event in our events array and update it
  const eventIndex = events.value.findIndex((e) => e.id === eventData.id);
  if (eventIndex !== -1) {
    const newDate = new Date(dayDate);
    newDate.setHours(hour, minutes, 0, 0);

    const originalStart = new Date(eventData.start);
    const originalEnd = new Date(eventData.end);
    const duration = originalEnd.getTime() - originalStart.getTime();

    const newEndDate = new Date(newDate.getTime() + duration);

    try {
      // עדכון במסד נתונים
      console.log("🕐 Drag&Drop - Moving event to:", createLocalISOString(newDate));
      console.log("🕐 Drag&Drop - New end time:", createLocalISOString(newEndDate));
      
      const { error: updateError } = await supabase
        .from("calendar_events")
        .update({
          start_time: createLocalISOString(newDate),
          end_time: createLocalISOString(newEndDate),
          updated_at: createLocalISOString(new Date()),
        })
        .eq("id", eventData.id);

      if (updateError) {
        console.error("Error updating event position:", updateError);
        showToast("calendar.eventMoveError", "error");
        return;
      }

      // Format the new time display
      const newTimeDisplay = `${formatTimeInLocalTimezone(newDate)} - ${formatTimeInLocalTimezone(newEndDate)}`;

      events.value[eventIndex] = {
        ...events.value[eventIndex],
        start: newDate,
        end: newEndDate,
        timeDisplay: newTimeDisplay,
      };

      console.log("Event moved successfully! New time:", newTimeDisplay);
    } catch (error) {
      console.error("Error moving event:", error);
      showToast("calendar.eventMoveError", "error");
    }
  }
};

const getAbsoluteEventStyle = (event: any, dayDate: string) => {
  const start = new Date(event.start);
  const end = new Date(event.end);

  // בדיקה אם התאריך של האירוע תואם את היום המבוקש
  const eventDateStr = start.toISOString().split("T")[0];
  if (eventDateStr !== dayDate) {
    console.log(
      `⚠️ Event ${event.title} date ${eventDateStr} doesn't match day ${dayDate}`
    );
    return {
      display: "none",
    };
  }

  // התאמה לעיצוב החדש - כל תא שעה הוא 60px
  const heightPerHour = 60; // עדכון לעיצוב החדש
  const minHeight = 30; // גובה מינימלי מופחת

  // חישוב המיקום מתחילת היום (6 AM)
  const startHour = 6; // השעה הראשונה ביומן
  const eventStartHour = start.getHours();
  const eventStartMinutes = start.getMinutes();

  // וידוא שהאירוע בטווח השעות המוצג
  if (eventStartHour < startHour || eventStartHour > 21) {
    console.log(
      `⏰ Event ${event.title} at hour ${eventStartHour} is outside display range (${startHour}-21)`
    );
    return {
      display: "none",
    };
  }

  // חישוב המיקום מלמעלה
  const hoursFromStart = eventStartHour - startHour;
  const minutesOffset = (eventStartMinutes / 60) * heightPerHour;
  const top = hoursFromStart * heightPerHour + minutesOffset;

  console.log(`📍 Event "${event.title}" positioning:`, {
    eventStartHour,
    eventStartMinutes,
    startHour,
    hoursFromStart,
    minutesOffset,
    heightPerHour,
    calculatedTop: top,
  });

  // חישוב גובה האירוע
  const duration = (end.getTime() - start.getTime()) / (1000 * 60); // דקות
  const height = Math.max(minHeight, (duration / 60) * heightPerHour);

  // כל אירוע תופס את כל הרוחב - ללא חפיפות
  return {
    position: "absolute" as const,
    height: `${height}px`,
    top: `${top}px`,
    width: "calc(100% - 8px)", // רוחב מלא פחות ריווח
    left: "4px", // ריווח קטן מהצד
    zIndex: 10,
  };
};

// פונקציה לבדיקה אם שעה תפוסה (רק הפסקות ושעות לא פעילות, לא אירועים)
const isHourOccupied = (dateStr: string, hour: number) => {
  // Check if time is blocked by settings (breaks or non-working hours)
  return isTimeBlocked(dateStr, hour);
};

// Contact Search functionality (deprecated)
const openContactSearch = () => {
  showContactSearch.value = true;
  contactSearchQuery.value = "";
  filteredContacts.value = [];
};

const searchContacts = async () => {
  if (!contactSearchQuery.value.trim()) {
    filteredContacts.value = [];
    return;
  }

  isSearching.value = true;

  // Simulate API delay
  setTimeout(() => {
    const query = contactSearchQuery.value.toLowerCase();
    filteredContacts.value = contacts.value.filter(
      (contact) =>
        contact.name.toLowerCase().includes(query) ||
        contact.email.toLowerCase().includes(query) ||
        (contact.phone && contact.phone.includes(query))
    );
    isSearching.value = false;
  }, 300);
};

const selectContact = (contact: any) => {
  // Check if contact is already added
  const isAlreadyAdded = newEvent.value.participants.some(
    (p) => p.email === contact.email || p.name === contact.name
  );

  if (isAlreadyAdded) {
    showToast("calendar.contactAlreadyAdded", "warning");
    return;
  }

  // Find first empty participant slot
  const emptyIndex = newEvent.value.participants.findIndex(p => !p.name || p.name.trim() === "");
  
  const participantData = {
    name: contact.name,
    email: contact.email,
    phone: contact.phone || "",
  };

  if (emptyIndex !== -1) {
    // Fill empty slot
    newEvent.value.participants[emptyIndex] = participantData;
  } else {
    // Add new participant if no empty slots
    newEvent.value.participants.push(participantData);
  }

  closeContactSearch();
};

const closeContactSearch = () => {
  showContactSearch.value = false;
  contactSearchQuery.value = "";
  filteredContacts.value = [];
};

// Leads Search functionality
const openLeadsSearch = () => {
  isEditMode.value = false;
  showLeadsSearch.value = true;
  leadsSearchQuery.value = "";
  filteredLeads.value = [];
  fetchLeads();
};

const openLeadsSearchForEdit = () => {
  isEditMode.value = true;
  showLeadsSearch.value = true;
  leadsSearchQuery.value = "";
  filteredLeads.value = [];
  fetchLeads();
};

const fetchLeads = async () => {
  try {
    loadingLeads.value = true;

    const { organizationId } = await getCurrentUserOrgId();

    const { data, error } = await supabase
      .from("leads")
      .select("id, name, email, phone, status, profileImageLink")
      .eq("org_id", organizationId)
      .order("name");

    if (error) throw error;

    leads.value = data || [];
    console.log("Loaded leads:", leads.value);
  } catch (error) {
    console.error("Error fetching leads:", error);
    leads.value = [];
  } finally {
    loadingLeads.value = false;
  }
};

const searchLeads = async () => {
  if (!leadsSearchQuery.value.trim()) {
    filteredLeads.value = [];
    return;
  }

  isSearchingLeads.value = true;

  const query = leadsSearchQuery.value.toLowerCase();
  filteredLeads.value = leads.value.filter(
    (lead) =>
      (lead.name && lead.name.toLowerCase().includes(query)) ||
      (lead.email && lead.email.toLowerCase().includes(query)) ||
      (lead.phone && lead.phone.includes(query))
  );

  isSearchingLeads.value = false;
};

const selectLead = (lead: any) => {
  // Check if lead is already added
  const isAlreadyAdded = newEvent.value.participants.some(
    (p) => p.email === lead.email || p.name === lead.name
  );

  if (isAlreadyAdded) {
    showToast("calendar.leadAlreadyAdded", "warning");
    return;
  }

  // Find first empty participant slot
  const emptyIndex = newEvent.value.participants.findIndex(p => !p.name || p.name.trim() === "");
  
  const participantData = {
    name: lead.name || "ללא שם",
    email: lead.email || "",
    phone: lead.phone || "",
    type: "lead",
    leadId: lead.id,
  };

  if (emptyIndex !== -1) {
    // Fill empty slot
    newEvent.value.participants[emptyIndex] = participantData;
  } else {
    // Add new participant if no empty slots
    newEvent.value.participants.push(participantData);
  }

  closeLeadsSearch();
};

// Select lead for edit modal
const selectLeadForEdit = (lead: any) => {
  if (!editingEvent.value.participants) {
    editingEvent.value.participants = [];
  }

  // Check if lead is already added
  const isAlreadyAdded = editingEvent.value.participants.some(
    (p) => p.email === lead.email || p.name === lead.name
  );

  if (isAlreadyAdded) {
    showToast("calendar.leadAlreadyAdded", "warning");
    return;
  }

  // Find first empty participant slot
  const emptyIndex = editingEvent.value.participants.findIndex(p => !p.name || p.name.trim() === "");
  
  const participantData = {
    name: lead.name || "ללא שם",
    email: lead.email || "",
    phone: lead.phone || "",
    type: "lead",
    leadId: lead.id,
  };

  if (emptyIndex !== -1) {
    // Fill empty slot
    editingEvent.value.participants[emptyIndex] = participantData;
  } else {
    // Add new participant if no empty slots
    editingEvent.value.participants.push(participantData);
  }

  closeLeadsSearch();
};

const closeLeadsSearch = () => {
  showLeadsSearch.value = false;
  leadsSearchQuery.value = "";
  filteredLeads.value = [];
};

// Clients Search functionality
const openClientsSearch = () => {
  isEditMode.value = false;
  showClientsSearch.value = true;
  clientsSearchQuery.value = "";
  filteredClients.value = [];
  fetchClients();
};

const openClientsSearchForEdit = () => {
  isEditMode.value = true;
  showClientsSearch.value = true;
  clientsSearchQuery.value = "";
  filteredClients.value = [];
  fetchClients();
};

const fetchClients = async () => {
  try {
    loadingClients.value = true;

    const { organizationId } = await getCurrentUserOrgId();

    const { data, error } = await supabase
      .from("clients")
      .select("id, first_name, last_name, email, phone, status")
      .eq("organization_id", organizationId)
      .order("first_name");

    if (error) throw error;

    clients.value =
      data?.map((client) => ({
        ...client,
        name: `${client.first_name || ""} ${client.last_name || ""}`.trim() || "ללא שם",
      })) || [];

    console.log("Loaded clients:", clients.value);
  } catch (error) {
    console.error("Error fetching clients:", error);
    clients.value = [];
  } finally {
    loadingClients.value = false;
  }
};

const searchClients = async () => {
  if (!clientsSearchQuery.value.trim()) {
    filteredClients.value = [];
    return;
  }

  isSearchingClients.value = true;

  const query = clientsSearchQuery.value.toLowerCase();
  filteredClients.value = clients.value.filter(
    (client) =>
      (client.name && client.name.toLowerCase().includes(query)) ||
      (client.first_name && client.first_name.toLowerCase().includes(query)) ||
      (client.last_name && client.last_name.toLowerCase().includes(query)) ||
      (client.email && client.email.toLowerCase().includes(query)) ||
      (client.phone && client.phone.includes(query))
  );

  isSearchingClients.value = false;
};

const selectClient = (client: any) => {
  // Check if client is already added
  const isAlreadyAdded = newEvent.value.participants.some(
    (p) => p.email === client.email || p.name === client.name
  );

  if (isAlreadyAdded) {
    showToast("calendar.clientAlreadyAdded", "warning");
    return;
  }

  // Find first empty participant slot
  const emptyIndex = newEvent.value.participants.findIndex(p => !p.name || p.name.trim() === "");
  
  const participantData = {
    name: client.name,
    email: client.email || "",
    phone: client.phone || "",
    type: "client",
    clientId: client.id,
  };

  if (emptyIndex !== -1) {
    // Fill empty slot
    newEvent.value.participants[emptyIndex] = participantData;
  } else {
    // Add new participant if no empty slots
    newEvent.value.participants.push(participantData);
  }

  closeClientsSearch();
};

// Select client for edit modal
const selectClientForEdit = (client: any) => {
  if (!editingEvent.value.participants) {
    editingEvent.value.participants = [];
  }

  // Check if client is already added
  const isAlreadyAdded = editingEvent.value.participants.some(
    (p) => p.email === client.email || p.name === client.name
  );

  if (isAlreadyAdded) {
    showToast("calendar.clientAlreadyAdded", "warning");
    return;
  }

  // Find first empty participant slot
  const emptyIndex = editingEvent.value.participants.findIndex(p => !p.name || p.name.trim() === "");
  
  const participantData = {
    name: client.name,
    email: client.email || "",
    phone: client.phone || "",
    type: "client",
    clientId: client.id,
  };

  if (emptyIndex !== -1) {
    // Fill empty slot
    editingEvent.value.participants[emptyIndex] = participantData;
  } else {
    // Add new participant if no empty slots
    editingEvent.value.participants.push(participantData);
  }

  closeClientsSearch();
};

const closeClientsSearch = () => {
  showClientsSearch.value = false;
  clientsSearchQuery.value = "";
  filteredClients.value = [];
};

// Toast and Popup functions
const showToast = (
  messageKey: string,
  type: "success" | "error" | "warning" | "info" = "info",
  params: any = {}
) => {
  const id = Date.now() + Math.random();
  const toast = {
    id,
    messageKey,
    type,
    params,
    message: t(messageKey, params),
  };

  toasts.value.push(toast);

  // Auto remove after 5 seconds
  setTimeout(() => {
    removeToast(id);
  }, 5000);
};

const removeToast = (id: number) => {
  const index = toasts.value.findIndex((toast) => toast.id === id);
  if (index > -1) {
    toasts.value.splice(index, 1);
  }
};

const showConfirm = (
  titleKey: string,
  messageKey: string,
  confirmAction: () => void,
  confirmTextKey = "common.confirm",
  cancelTextKey = "common.cancel",
  type: "warning" | "danger" | "info" | "success" = "warning",
  params: any = {}
) => {
  confirmPopup.value = {
    title: t(titleKey, params),
    message: t(messageKey, params),
    confirmText: t(confirmTextKey),
    cancelText: t(cancelTextKey),
    confirmAction,
    type,
  };
  showConfirmPopup.value = true;
};

const closeConfirmPopup = () => {
  showConfirmPopup.value = false;
  confirmPopup.value.confirmAction = null;
};

const confirmAction = () => {
  if (confirmPopup.value.confirmAction) {
    confirmPopup.value.confirmAction();
  }
  closeConfirmPopup();
};

// Simple confirm function for basic yes/no questions
const showSimpleConfirmDialog = (
  messageKey: string,
  callback: () => void,
  params: any = {}
) => {
  simpleConfirmMessage.value = t(messageKey, params);
  simpleConfirmCallback.value = callback;
  showSimpleConfirm.value = true;
};

const closeSimpleConfirm = () => {
  showSimpleConfirm.value = false;
  simpleConfirmCallback.value = null;
};

const confirmSimpleAction = () => {
  if (simpleConfirmCallback.value) {
    simpleConfirmCallback.value();
  }
  closeSimpleConfirm();
};

// Update calendar title based on view
const calendarTitle = computed(() => {
  const date = currentDate.value;

  if (currentView.value === "day") {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } else {
    // Week view
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
      return `${startOfWeek.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })}`;
    } else {
      return `${startOfWeek.toLocaleDateString("en-US", {
        month: "short",
      })} - ${endOfWeek.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      })}`;
    }
  }
});

// Filtered products for search
const filteredProducts = computed(() => {
  if (!productSearchQuery.value.trim()) {
    return products.value;
  }

  const query = productSearchQuery.value.toLowerCase().trim();
  return products.value.filter(
    (product) =>
      product.title.toLowerCase().includes(query) ||
      (product.description && product.description.toLowerCase().includes(query))
  );
});

// Calendar Settings functions
const toggleSettings = () => {
  showSettings.value = !showSettings.value;
};

// Fetch breaks from database
const fetchBreaks = async () => {
  try {
    loadingBreaks.value = true;

    // Get current user ID
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    if (!userData.user) {
      throw new Error("User is not authenticated");
    }

    const { data, error } = await supabase
      .from("customer_appointment_break")
      .select("*")
      .eq("user_id", userData.user.id)
      .order("created_at");

    if (error) throw error;

    // Transform data to match existing structure
    blockedTimes.value =
      data?.map((breakItem) => ({
        id: breakItem.id,
        name: breakItem.title || "Break",
        startTime: breakItem.start_time || "09:00",
        endTime: breakItem.end_time || "10:00",
        days: breakItem.days || [],
        isNew: false,
        isEditing: false,
      })) || [];

    console.log("Loaded breaks:", blockedTimes.value);
  } catch (error) {
    console.error("Failed to fetch breaks:", error);
    blockedTimes.value = [];
  } finally {
    loadingBreaks.value = false;
  }
};

const addBlockedTime = () => {
  blockedTimes.value.push({
    id: null,
    name: "New Break",
    startTime: "09:00",
    endTime: "10:00",
    days: [],
    isNew: true,
    isEditing: true,
  });
};

const removeBlockedTime = async (index: number) => {
  const breakItem = blockedTimes.value[index];

  if (!breakItem.id) {
    // If it's a new item (no ID), just remove from array
    blockedTimes.value.splice(index, 1);
    return;
  }

  const deleteBreak = () => {
    performDeleteBreak(index);
  };

  showConfirm(
    "calendar.confirmDeleteBreak",
    "calendar.confirmDeleteBreakMessage",
    deleteBreak,
    "common.delete",
    "common.cancel",
    "danger"
  );

  return; // Exit early, actual deletion will happen in the confirm callback

  async function performDeleteBreak(index: number) {
    try {
      savingBreak.value = true;

      // Delete from database
      const { error } = await supabase
        .from("customer_appointment_break")
        .delete()
        .eq("id", breakItem.id);

      if (error) throw error;

      // Remove from array
      blockedTimes.value.splice(index, 1);

      console.log("Break deleted successfully");
    } catch (error) {
      console.error("Error deleting break:", error);
      showToast("calendar.breakDeleteError", "error");
    } finally {
      savingBreak.value = false;
    }
  }
};

const saveBreak = async (breakItem: any, index: number) => {
  if (!breakItem.name?.trim()) {
    showToast("calendar.breakNameRequired", "warning");
    return;
  }

  if (!breakItem.startTime || !breakItem.endTime) {
    showToast("calendar.breakTimesRequired", "warning");
    return;
  }

  try {
    savingBreak.value = true;

    // Get current user ID
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    if (!userData.user) {
      throw new Error("User is not authenticated");
    }

    const breakData = {
      title: breakItem.name.trim(),
      start_time: breakItem.startTime,
      end_time: breakItem.endTime,
      days: breakItem.days || [],
      user_id: userData.user.id,
    };

    if (breakItem.isNew) {
      // Create new break
      const { data, error } = await supabase
        .from("customer_appointment_break")
        .insert(breakData)
        .select("id")
        .single();

      if (error) throw error;

      // Update the break with the new ID
      blockedTimes.value[index] = {
        ...breakItem,
        id: data.id,
        isNew: false,
        isEditing: false,
      };
    } else {
      // Update existing break
      const { error } = await supabase
        .from("customer_appointment_break")
        .update(breakData)
        .eq("id", breakItem.id);

      if (error) throw error;

      // Update the break
      blockedTimes.value[index] = {
        ...breakItem,
        isEditing: false,
      };
    }

    console.log("Break saved successfully");
  } catch (error) {
    console.error("Error saving break:", error);
    showToast("calendar.breakSaveError", "error");
  } finally {
    savingBreak.value = false;
  }
};

const editBreak = (index: number) => {
  blockedTimes.value[index].isEditing = true;
};

const cancelEditBreak = (index: number) => {
  const breakItem = blockedTimes.value[index];
  if (breakItem.isNew) {
    blockedTimes.value.splice(index, 1);
  } else {
    breakItem.isEditing = false;
  }
};

// Employee Availability functions
const fetchEmployeeAvailability = async () => {
  try {
    loadingAvailability.value = true;

    // Get current user info
    const { userId, organizationId } = await getCurrentUserOrgId();

    const { data, error } = await supabase
      .from("employee_availability")
      .select("*")
      .eq("user_id", userId)
      .eq("org_id", organizationId)
      .eq("is_active", true)
      .single();

    if (error && error.code !== "PGRST116") {
      // PGRST116 = no rows returned
      throw error;
    }

    if (data) {
      // Transform database data to workingDays format
      workingDays.value = [
        {
          key: "Mon",
          name: "Monday",
          short: "Mon",
          enabled: data.monday_available || false,
          startTime: data.monday_start_time || "09:00",
          endTime: data.monday_end_time || "17:00",
        },
        {
          key: "Tue",
          name: "Tuesday",
          short: "Tue",
          enabled: data.tuesday_available || false,
          startTime: data.tuesday_start_time || "09:00",
          endTime: data.tuesday_end_time || "17:00",
        },
        {
          key: "Wed",
          name: "Wednesday",
          short: "Wed",
          enabled: data.wednesday_available || false,
          startTime: data.wednesday_start_time || "09:00",
          endTime: data.wednesday_end_time || "17:00",
        },
        {
          key: "Thu",
          name: "Thursday",
          short: "Thu",
          enabled: data.thursday_available || false,
          startTime: data.thursday_start_time || "09:00",
          endTime: data.thursday_end_time || "17:00",
        },
        {
          key: "Fri",
          name: "Friday",
          short: "Fri",
          enabled: data.friday_available || false,
          startTime: data.friday_start_time || "09:00",
          endTime: data.friday_end_time || "17:00",
        },
        {
          key: "Sat",
          name: "Saturday",
          short: "Sat",
          enabled: data.saturday_available || false,
          startTime: data.saturday_start_time || "10:00",
          endTime: data.saturday_end_time || "14:00",
        },
        {
          key: "Sun",
          name: "Sunday",
          short: "Sun",
          enabled: data.sunday_available || false,
          startTime: data.sunday_start_time || "10:00",
          endTime: data.sunday_end_time || "14:00",
        },
      ];

      workingHoursEnabled.value = workingDays.value.some((day) => day.enabled);
    } else {
      // Set default values if no data found
      workingDays.value = [
        {
          key: "Mon",
          name: "Monday",
          short: "Mon",
          enabled: true,
          startTime: "09:00",
          endTime: "17:00",
        },
        {
          key: "Tue",
          name: "Tuesday",
          short: "Tue",
          enabled: true,
          startTime: "09:00",
          endTime: "17:00",
        },
        {
          key: "Wed",
          name: "Wednesday",
          short: "Wed",
          enabled: true,
          startTime: "09:00",
          endTime: "17:00",
        },
        {
          key: "Thu",
          name: "Thursday",
          short: "Thu",
          enabled: true,
          startTime: "09:00",
          endTime: "17:00",
        },
        {
          key: "Fri",
          name: "Friday",
          short: "Fri",
          enabled: true,
          startTime: "09:00",
          endTime: "17:00",
        },
        {
          key: "Sat",
          name: "Saturday",
          short: "Sat",
          enabled: false,
          startTime: "10:00",
          endTime: "14:00",
        },
        {
          key: "Sun",
          name: "Sunday",
          short: "Sun",
          enabled: false,
          startTime: "10:00",
          endTime: "14:00",
        },
      ];
      workingHoursEnabled.value = true;
    }

    console.log("Loaded employee availability:", workingDays.value);
  } catch (error) {
    console.error("Failed to fetch employee availability:", error);
    // Set defaults on error
    workingDays.value = [
      {
        key: "Mon",
        name: "Monday",
        short: "Mon",
        enabled: true,
        startTime: "09:00",
        endTime: "17:00",
      },
      {
        key: "Tue",
        name: "Tuesday",
        short: "Tue",
        enabled: true,
        startTime: "09:00",
        endTime: "17:00",
      },
      {
        key: "Wed",
        name: "Wednesday",
        short: "Wed",
        enabled: true,
        startTime: "09:00",
        endTime: "17:00",
      },
      {
        key: "Thu",
        name: "Thursday",
        short: "Thu",
        enabled: true,
        startTime: "09:00",
        endTime: "17:00",
      },
      {
        key: "Fri",
        name: "Friday",
        short: "Fri",
        enabled: true,
        startTime: "09:00",
        endTime: "17:00",
      },
      {
        key: "Sat",
        name: "Saturday",
        short: "Sat",
        enabled: false,
        startTime: "10:00",
        endTime: "14:00",
      },
      {
        key: "Sun",
        name: "Sunday",
        short: "Sun",
        enabled: false,
        startTime: "10:00",
        endTime: "14:00",
      },
    ];
    workingHoursEnabled.value = true;
  } finally {
    loadingAvailability.value = false;
  }
};

const saveEmployeeAvailability = async () => {
  try {
    savingAvailability.value = true;

    // Get current user info
    const { userId, organizationId } = await getCurrentUserOrgId();

    // Prepare availability data
    const availabilityData = {
      user_id: userId,
      org_id: organizationId,
      sunday_available: workingDays.value.find((d) => d.key === "Sun")?.enabled || false,
      sunday_start_time:
        workingDays.value.find((d) => d.key === "Sun")?.startTime || null,
      sunday_end_time: workingDays.value.find((d) => d.key === "Sun")?.endTime || null,
      monday_available: workingDays.value.find((d) => d.key === "Mon")?.enabled || false,
      monday_start_time:
        workingDays.value.find((d) => d.key === "Mon")?.startTime || null,
      monday_end_time: workingDays.value.find((d) => d.key === "Mon")?.endTime || null,
      tuesday_available: workingDays.value.find((d) => d.key === "Tue")?.enabled || false,
      tuesday_start_time:
        workingDays.value.find((d) => d.key === "Tue")?.startTime || null,
      tuesday_end_time: workingDays.value.find((d) => d.key === "Tue")?.endTime || null,
      wednesday_available:
        workingDays.value.find((d) => d.key === "Wed")?.enabled || false,
      wednesday_start_time:
        workingDays.value.find((d) => d.key === "Wed")?.startTime || null,
      wednesday_end_time: workingDays.value.find((d) => d.key === "Wed")?.endTime || null,
      thursday_available:
        workingDays.value.find((d) => d.key === "Thu")?.enabled || false,
      thursday_start_time:
        workingDays.value.find((d) => d.key === "Thu")?.startTime || null,
      thursday_end_time: workingDays.value.find((d) => d.key === "Thu")?.endTime || null,
      friday_available: workingDays.value.find((d) => d.key === "Fri")?.enabled || false,
      friday_start_time:
        workingDays.value.find((d) => d.key === "Fri")?.startTime || null,
      friday_end_time: workingDays.value.find((d) => d.key === "Fri")?.endTime || null,
      saturday_available:
        workingDays.value.find((d) => d.key === "Sat")?.enabled || false,
      saturday_start_time:
        workingDays.value.find((d) => d.key === "Sat")?.startTime || null,
      saturday_end_time: workingDays.value.find((d) => d.key === "Sat")?.endTime || null,
      is_active: true,
    };

    // Check if record exists
    const { data: existingData, error: checkError } = await supabase
      .from("employee_availability")
      .select("id")
      .eq("user_id", userId)
      .eq("org_id", organizationId)
      .eq("is_active", true)
      .single();

    if (checkError && checkError.code !== "PGRST116") {
      throw checkError;
    }

    if (existingData) {
      // Update existing record
      const { error } = await supabase
        .from("employee_availability")
        .update(availabilityData)
        .eq("id", existingData.id);

      if (error) throw error;
    } else {
      // Create new record
      const { error } = await supabase
        .from("employee_availability")
        .insert(availabilityData);

      if (error) throw error;
    }

    console.log("Employee availability saved successfully");
    showToast("calendar.workingHoursSaved", "success");
  } catch (error) {
    console.error("Error saving employee availability:", error);
    showToast("calendar.workingHoursSaveError", "error");
  } finally {
    savingAvailability.value = false;
  }
};

const addAppointmentType = () => {
  appointmentTypes.value.push({
    id: null, // null for new items
    name: "סוג פגישה חדש",
    duration: 30,
    color: "work",
    colorCode: "#3b82f6",
    tag: "",
    tagColorCode: "#3b82f6",
    canAllUsersSee: true,
    productId: null,
    selectedUsers: [],
    isNew: true,
    isEditing: true,
  });
};

const removeAppointmentType = async (index: number) => {
  const type = appointmentTypes.value[index];

  if (!type.id) {
    // If it's a new item (no ID), just remove from array
    appointmentTypes.value.splice(index, 1);
    return;
  }

  const deleteType = () => {
    performDeleteAppointmentType(index);
  };

  showConfirm(
    "calendar.confirmDeleteAppointmentType",
    "calendar.confirmDeleteAppointmentTypeMessage",
    deleteType,
    "common.delete",
    "common.cancel",
    "danger"
  );

  return; // Exit early, actual deletion will happen in the confirm callback

  async function performDeleteAppointmentType(index: number) {
    try {
      savingAppointmentType.value = true;

      // Delete from database
      const { error } = await supabase
        .from("customer_appointment_types")
        .delete()
        .eq("id", type.id);

      if (error) throw error;

      // Delete access records
      await supabase
        .from("customer_appointment_access")
        .delete()
        .eq("appointment_id", type.id);

      // Remove from array
      appointmentTypes.value.splice(index, 1);

      console.log("Appointment type deleted successfully");
    } catch (error) {
      console.error("Error deleting appointment type:", error);
      showToast("calendar.appointmentTypeDeleteError", "error");
    } finally {
      savingAppointmentType.value = false;
    }
  }
};

const saveAppointmentType = async (type: any, index: number) => {
  if (!type.name?.trim()) {
    showToast("calendar.appointmentTypeNameRequired", "warning");
    return;
  }

  try {
    savingAppointmentType.value = true;

    const { organizationId } = await getCurrentUserOrgId();

    const appointmentData = {
      organization_id: organizationId,
      name: type.name.trim(),
      color_code: type.colorCode,
      duration: type.duration,
      tag: type.tag?.trim() || null,
      tag_color_code: type.tagColorCode,
      can_all_users_see: type.canAllUsersSee,
      product_id: type.productId || null,
    };

    let appointmentId = type.id;

    if (type.isNew) {
      // Create new appointment type
      const { data, error } = await supabase
        .from("customer_appointment_types")
        .insert(appointmentData)
        .select("id")
        .single();

      if (error) throw error;
      appointmentId = data.id;

      // Update the type with the new ID
      appointmentTypes.value[index] = {
        ...type,
        id: appointmentId,
        isNew: false,
        isEditing: false,
      };
    } else {
      // Update existing appointment type
      const { error } = await supabase
        .from("customer_appointment_types")
        .update(appointmentData)
        .eq("id", type.id);

      if (error) throw error;

      // Update the type
      appointmentTypes.value[index] = {
        ...type,
        isEditing: false,
      };
    }

    // Handle access control
    if (!type.canAllUsersSee && type.selectedUsers?.length > 0) {
      // Delete existing access records
      await supabase
        .from("customer_appointment_access")
        .delete()
        .eq("appointment_id", appointmentId);

      // Insert new access records
      const accessRecords = type.selectedUsers.map((userId: string) => ({
        appointment_id: appointmentId,
        access_user: userId,
      }));

      const { error: accessError } = await supabase
        .from("customer_appointment_access")
        .insert(accessRecords);

      if (accessError) throw accessError;
    } else if (type.canAllUsersSee) {
      // If switching to "all users can see", delete specific access records
      await supabase
        .from("customer_appointment_access")
        .delete()
        .eq("appointment_id", appointmentId);
    }

    console.log("Appointment type saved successfully");
  } catch (error) {
    console.error("Error saving appointment type:", error);
    showToast("calendar.appointmentTypeSaveError", "error");
  } finally {
    savingAppointmentType.value = false;
  }
};

const editAppointmentType = (index: number) => {
  appointmentTypes.value[index].isEditing = true;
};

const cancelEditAppointmentType = (index: number) => {
  const type = appointmentTypes.value[index];
  if (type.isNew) {
    appointmentTypes.value.splice(index, 1);
  } else {
    type.isEditing = false;
    // No need to reload - just reset the editing state
    // The original data is preserved in the database
  }
};

const adjustDuration = (type: any, change: number) => {
  const newDuration = type.duration + change;
  if (newDuration >= 15 && newDuration <= 480) {
    type.duration = newDuration;
  }
};

const validateDuration = (type: any) => {
  if (type.duration < 15) type.duration = 15;
  if (type.duration > 480) type.duration = 480;
  // Round to nearest 15 minutes
  type.duration = Math.round(type.duration / 15) * 15;
};

const getColorValue = (colorKey: string) => {
  const color = colorOptions.value.find((c) => c.value === colorKey);
  return color ? color.bg : "#3b82f6";
};

const resetSettings = () => {
  const performReset = () => {
    // Reset working days
    workingDays.value = [
      {
        key: "Mon",
        name: "Monday",
        short: "Mon",
        enabled: true,
        startTime: "09:00",
        endTime: "17:00",
      },
      {
        key: "Tue",
        name: "Tuesday",
        short: "Tue",
        enabled: true,
        startTime: "09:00",
        endTime: "17:00",
      },
      {
        key: "Wed",
        name: "Wednesday",
        short: "Wed",
        enabled: true,
        startTime: "09:00",
        endTime: "17:00",
      },
      {
        key: "Thu",
        name: "Thursday",
        short: "Thu",
        enabled: true,
        startTime: "09:00",
        endTime: "17:00",
      },
      {
        key: "Fri",
        name: "Friday",
        short: "Fri",
        enabled: true,
        startTime: "09:00",
        endTime: "17:00",
      },
      {
        key: "Sat",
        name: "Saturday",
        short: "Sat",
        enabled: false,
        startTime: "10:00",
        endTime: "14:00",
      },
      {
        key: "Sun",
        name: "Sunday",
        short: "Sun",
        enabled: false,
        startTime: "10:00",
        endTime: "14:00",
      },
    ];

    // Reset blocked times
    blockedTimes.value = [
      {
        name: "Lunch Break",
        startTime: "12:00",
        endTime: "13:00",
        days: ["Mon", "Tue", "Wed", "Thu", "Fri"],
      },
    ];

    // Reset appointment types
    appointmentTypes.value = [
      { name: "Face Treatment", duration: 40, color: "personal" },
      { name: "Pedicure", duration: 90, color: "health" },
      { name: "Manicure", duration: 60, color: "work" },
    ];

    workingHoursEnabled.value = true;

    showToast("calendar.settingsReset", "success");
  };

  showConfirm(
    "calendar.confirmResetSettings",
    "calendar.confirmResetSettingsMessage",
    performReset,
    "common.reset",
    "common.cancel",
    "warning"
  );
};

const isTimeBlocked = (dateStr: string, hour: number) => {
  const date = new Date(dateStr);
  const dayName = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][date.getDay()];
  const dayKey = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][date.getDay()];

  // Check if time is blocked by breaks
  const isBreakTime = blockedTimes.value.some((block) => {
    if (!block.days.includes(dayName)) return false;

    const blockStart = parseInt(block.startTime.split(":")[0]);
    const blockEnd = parseInt(block.endTime.split(":")[0]);

    return hour >= blockStart && hour < blockEnd;
  });

  if (isBreakTime) return true;

  // Check if time is outside working hours
  const workingDay = workingDays.value.find((wd) => wd.key === dayKey);
  if (!workingDay || !workingDay.enabled) {
    return true; // Block entire day if not a working day
  }

  const workStart = parseInt(workingDay.startTime.split(":")[0]);
  const workEnd = parseInt(workingDay.endTime.split(":")[0]);

  // Block if outside working hours
  return hour < workStart || hour >= workEnd;
};

// Check if a day of the week is blocked (for month view)
const isDayOfWeekBlocked = (dateStr: string) => {
  const date = new Date(dateStr);
  const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, etc.
  // Map JavaScript getDay() to our workingDays keys
  const dayMapping = {
    6: "Sun", // Sunday
    0: "Mon", // Monday 
    1: "Tue", // Tuesday
    2: "Wed", // Wednesday
    3: "Thu", // Thursday
    4: "Fri", // Friday
    5: "Sat"  // Saturday
  };
  const dayKey = dayMapping[dayOfWeek];
  
  const workingDay = workingDays.value.find(wd => wd.key === dayKey);
  return !workingDay?.enabled; // Return true if day is not available
};

// Helper function to format time correctly in local timezone
const formatTimeInLocalTimezone = (date: Date) => {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

// Helper function to create date from database timestamp (treat as local time)
const createLocalDateFromDB = (dbTimestamp: string) => {
  // בדיקה שהtimestamp תקין
  if (!dbTimestamp || typeof dbTimestamp !== 'string') {
    console.warn("⚠️ Invalid database timestamp:", dbTimestamp);
    return new Date(); // חזור לתאריך נוכחי כdefault
  }

  // Remove timezone indicators to treat as local time
  // Handles formats like: '2025-06-29 06:15:00+00', '2025-06-29T06:15:00Z', etc.
  const cleanTimestamp = dbTimestamp
    .replace('Z', '')           // Remove Z
    .replace('+00', '')         // Remove +00
    .replace('+00:00', '')      // Remove +00:00
    .replace('-00:00', '')      // Remove -00:00
    .replace('T', ' ');         // Replace T with space for consistent format
  
  const date = new Date(cleanTimestamp);
  
  // בדיקה שהתוצאה תקינה
  if (isNaN(date.getTime())) {
    console.warn("⚠️ Invalid date created from timestamp:", dbTimestamp, "cleaned:", cleanTimestamp);
    return new Date(); // חזור לתאריך נוכחי כdefault
  }
  
  return date;
};

const updateEventDuration = () => {
  // Find the selected appointment type
  const selectedType = appointmentTypes.value.find(
    (type) => type.name === newEvent.value.type
  );

  if (selectedType && newEvent.value.startTime) {
    // Calculate end time based on duration
    const startTime = new Date(`2000-01-01T${newEvent.value.startTime}`);
    const endTime = new Date(startTime.getTime() + selectedType.duration * 60000); // Add duration in milliseconds

    // Format end time as HH:MM
    const hours = endTime.getHours().toString().padStart(2, "0");
    const minutes = endTime.getMinutes().toString().padStart(2, "0");
    newEvent.value.endTime = `${hours}:${minutes}`;
  }

  console.log("Event type changed:", newEvent.value.type, "Duration set automatically");
};

const handleImageError = () => {
  userProfilePicture.value = defaultProfileImage;
};

// Fetch appointment types from database
const fetchAppointmentTypes = async () => {
  try {
    loadingAppointmentTypes.value = true;

    const { userId, organizationId } = await getCurrentUserOrgId();

    // Query to get appointment types with access logic
    const { data, error } = await supabase
      .from("customer_appointment_types")
      .select(
        `
        id,
        name,
        color_code,
        duration,
        tag,
        tag_color_code,
        can_all_users_see,
        product_id,
        customer_appointment_access (
          access_user
        )
      `
      )
      .eq("organization_id", organizationId);

    if (error) {
      console.error("Error fetching appointment types:", error);
      throw error;
    }

    // Filter based on access logic
    const filteredTypes =
      data?.filter((type) => {
        // If can_all_users_see is true, always include
        if (type.can_all_users_see) {
          return true;
        }

        // If can_all_users_see is false, check if user has specific access
        const accessArray = Array.isArray(type.customer_appointment_access)
          ? type.customer_appointment_access
          : [];

        return accessArray.some((access: any) => access.access_user === userId);
      }) || [];

    // Transform to match existing structure
    appointmentTypes.value = filteredTypes.map((type) => {
      const accessArray = Array.isArray(type.customer_appointment_access)
        ? type.customer_appointment_access
        : [];

      return {
        id: type.id,
        name: type.name || "Unnamed Type",
        duration: type.duration || 30,
        color: mapColorCodeToColorKey(type.color_code),
        colorCode: type.color_code || "#3b82f6",
        tag: type.tag || "",
        tagColorCode: type.tag_color_code || "#3b82f6",
        canAllUsersSee: type.can_all_users_see,
        productId: type.product_id,
        selectedUsers: accessArray.map((access: any) => access.access_user),
        isNew: false,
        isEditing: false,
      };
    });

    console.log("Loaded appointment types:", appointmentTypes.value);
  } catch (error) {
    console.error("Failed to fetch appointment types:", error);
    // Fallback to default types on error
    appointmentTypes.value = [
      {
        name: "General Appointment",
        duration: 30,
        color: "work",
      },
    ];
  } finally {
    loadingAppointmentTypes.value = false;
  }
};

// Helper function to map color codes to color keys
const mapColorCodeToColorKey = (colorCode: string) => {
  const colorMap: { [key: string]: string } = {
    "#fbbf24": "personal",
    "#3b82f6": "work",
    "#ec4899": "health",
    "#22c55e": "meeting",
    "#8b5cf6": "call",
    "#6b7280": "task",
  };

  return colorMap[colorCode] || "work"; // Default to 'work' if not found
};

// Fetch products for linking
const fetchProducts = async () => {
  try {
    loadingProducts.value = true;

    const { organizationId } = await getCurrentUserOrgId();

    const { data, error } = await supabase
      .from("products")
      .select("id, title, description, price")
      .eq("org_id", organizationId)
      .order("title");

    if (error) throw error;

    // Transform data to match expected format
    products.value =
      data?.map((product) => ({
        id: product.id,
        name: product.title, // Map title to name for compatibility
        title: product.title,
        description: product.description,
        price: product.price,
        displayName: `${product.title}${product.price ? ` - ₪${product.price}` : ""}`,
      })) || [];
  } catch (error) {
    console.error("Error fetching products:", error);
    products.value = [];
  } finally {
    loadingProducts.value = false;
  }
};

// Fetch users for access control
const fetchUsers = async () => {
  try {
    loadingUsers.value = true;

    const { organizationId } = await getCurrentUserOrgId();

    const { data, error } = await supabase
      .from("user_profiles")
      .select("user_id, display_name, email")
      .eq("org_id", organizationId)
      .order("display_name");

    if (error) throw error;

    users.value =
      data?.map((u) => ({
        id: u.user_id,
        full_name: u.display_name || u.email,
      })) || [];
  } catch (error) {
    console.error("Error fetching users:", error);
    users.value = [];
  } finally {
    loadingUsers.value = false;
  }
};

// Product search functions
const selectProduct = (product: any, type: any) => {
  type.productId = product.id;
  productSearchQuery.value = product.title;
  showProductDropdown.value = false;
};

const clearProductSelection = (type: any) => {
  type.productId = null;
  productSearchQuery.value = "";
  showProductDropdown.value = false;
};

const getSelectedProductName = (productId: string | null) => {
  if (!productId) return "";
  const product = products.value.find((p) => p.id === productId);
  return product ? product.title : "";
};

const openEditEventModal = () => {
  if (!selectedEvent.value) return;

  // Convert the event data to editable format before closing the modal
  const event = selectedEvent.value;
  const startDate = new Date(event.start);
  const endDate = new Date(event.end);

  editingEvent.value = {
    id: event.id,
    title: event.title || "",
    type: event.type || "",
    date: startDate.toISOString().split("T")[0], // YYYY-MM-DD format
            startTime: formatTimeInLocalTimezone(startDate), // HH:MM format
        endTime: formatTimeInLocalTimezone(endDate), // HH:MM format
    location: event.location || "",
    description: event.description || "",
    colorClass: event.colorClass,
    participants: event.participants
      ? [...event.participants]
      : [{ name: "", email: "", phone: "" }],
  };

  // Close the event details modal after saving the data
  closeEventDetail();

  showEditEventModal.value = true;
};

const closeEditEventModal = () => {
  showEditEventModal.value = false;
  editingEvent.value = null;
};

const updateEvent = async () => {
  if (!editingEvent.value) return;

  // Validation
  if (
    !editingEvent.value.title ||
    !editingEvent.value.date ||
    !editingEvent.value.startTime ||
    !editingEvent.value.endTime
  ) {
    showToast("calendar.fillAllFields", "warning");
    return;
  }

  // Create new date objects
  const startDateTime = new Date(
    `${editingEvent.value.date}T${editingEvent.value.startTime}`
  );
  const endDateTime = new Date(
    `${editingEvent.value.date}T${editingEvent.value.endTime}`
  );

  // Validate end time is after start time
  if (endDateTime <= startDateTime) {
    showToast("calendar.endTimeAfterStart", "warning");
    return;
  }

  try {
    // מציאת סוג האירוע אם זה סוג מותאם אישית
    const appointmentType = appointmentTypes.value.find(
      (type) => type.name === editingEvent.value.type
    );
    const eventTypeId = appointmentType?.id || null;

    // עדכון במסד נתונים
    console.log("🕐 Editing - Original startDateTime:", startDateTime.toString());
    console.log("🕐 Editing - Original endDateTime:", endDateTime.toString());
    console.log("🕐 Editing - Saving startTime as:", createLocalISOString(startDateTime));
    console.log("🕐 Editing - Saving endTime as:", createLocalISOString(endDateTime));
    
    const { error: updateError } = await supabase
      .from("calendar_events")
      .update({
        title: editingEvent.value.title,
        start_time: createLocalISOString(startDateTime),
        end_time: createLocalISOString(endDateTime),
        event_type: eventTypeId,
        location: editingEvent.value.location || null,
        description: editingEvent.value.description || null,
        updated_at: createLocalISOString(new Date()),
      })
      .eq("id", editingEvent.value.id);

    if (updateError) {
      console.error("Error updating event:", updateError);
      showToast("calendar.eventUpdateError", "error");
      return;
    }

    // עדכון משתתפים
    if (editingEvent.value.participants) {
      // מחיקת משתתפים קיימים
      await supabase
        .from("calendar_events_participants")
        .delete()
        .eq("event_id", editingEvent.value.id);

      // הוספת משתתפים חדשים
      const validParticipants = editingEvent.value.participants.filter(
        (p) => p.name.trim() !== ""
      );
      if (validParticipants.length > 0) {
        const participantsToInsert = validParticipants.map((participant) => ({
          event_id: editingEvent.value.id,
          lead_id: (participant as any).leadId || null,
          customer_id: (participant as any).clientId || null,
          name: participant.name,
          phone: participant.phone || null,
          email: participant.email || null,
        }));

        const { error: participantsError } = await supabase
          .from("calendar_events_participants")
          .insert(participantsToInsert);

        if (participantsError) {
          console.error("Error updating participants:", participantsError);
        }
      }
    }

    // Find and update the event in the events array
    const eventIndex = events.value.findIndex((e) => e.id === editingEvent.value.id);
    if (eventIndex !== -1) {
      // Update the event
      const existingEvent = events.value[eventIndex];

      // Process participants for display
      const processedParticipants =
        editingEvent.value.participants
          ?.filter((p) => p.name.trim() !== "")
          .map((p, index) => ({
            id: index + 1,
            name: p.name,
            email: p.email,
            phone: p.phone,
            avatar: "/api/placeholder/24/24",
            leadId: (p as any).leadId,
            clientId: (p as any).clientId,
          })) || [];

      events.value[eventIndex] = {
        ...existingEvent,
        title: editingEvent.value.title,
        type: editingEvent.value.type,
        start: startDateTime,
        end: endDateTime,
        timeDisplay: `${formatTimeInLocalTimezone(startDateTime)} - ${formatTimeInLocalTimezone(endDateTime)}`,
        dateDisplay: startDateTime.toLocaleDateString("he-IL", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        participants: processedParticipants,
        ...(editingEvent.value.location && { location: editingEvent.value.location }),
        ...(editingEvent.value.description && {
          description: editingEvent.value.description,
        }),
      };

      console.log("Event updated successfully");
      showToast("calendar.eventUpdated", "success");

      // Update selectedEvent if it's the same event
      if (selectedEvent.value && selectedEvent.value.id === editingEvent.value.id) {
        selectedEvent.value = {
          ...events.value[eventIndex],
        };
      }
    }

    // Close both modals
    closeEditEventModal();
    closeEventDetail();
  } catch (error) {
    console.error("Error updating event:", error);
    showToast("calendar.eventUpdateError", "error");
  }
};

const deleteEventFromView = async () => {
  if (!selectedEvent.value) return;

  const performDelete = async () => {
    try {
      // קודם מחיקת המשתתפים
      const { error: participantsError } = await supabase
        .from("calendar_events_participants")
        .delete()
        .eq("event_id", selectedEvent.value.id);

      if (participantsError) {
        console.error("Error deleting participants:", participantsError);
        showToast("calendar.deleteParticipantsError", "error");
        return;
      }

      // לאחר מכן מחיקת האירוע
      const { error: deleteError } = await supabase
        .from("calendar_events")
        .delete()
        .eq("id", selectedEvent.value.id);

      if (deleteError) {
        console.error("Error deleting event:", deleteError);
        showToast("calendar.deleteEventError", "error");
        return;
      }

      // Find and remove the event from the events array
      const eventIndex = events.value.findIndex((e) => e.id === selectedEvent.value.id);
      if (eventIndex !== -1) {
        events.value.splice(eventIndex, 1);
        console.log("Event deleted successfully");
        showToast("calendar.eventDeleted", "success");
      }

      // Close modal
      closeEventDetail();
    } catch (error) {
      console.error("Error deleting event:", error);
      showToast("calendar.deleteEventError", "error");
    }
  };

  // Show confirmation dialog
  showSimpleConfirmDialog("calendar.confirmDelete", performDelete);
};

const toggleEventCompletion = async () => {
  if (!selectedEvent.value) return;

  try {
    const newStatus = !selectedEvent.value.is_over;
    
    // עדכון המסד נתונים - החלפת סטטוס הפגישה
    const { error } = await supabase
      .from("calendar_events")
      .update({ is_over: newStatus })
      .eq("id", selectedEvent.value.id);

    if (error) {
      console.error("Error updating event completion status:", error);
      showToast("calendar.eventUpdateError", "error");
      return;
    }

    // עדכון האירוע המקומי במערך
    const eventIndex = events.value.findIndex((e) => e.id === selectedEvent.value.id);
    if (eventIndex !== -1) {
      events.value[eventIndex].is_over = newStatus;
    }

    // עדכון האירוע הנבחר
    selectedEvent.value.is_over = newStatus;

    console.log(`Event status updated to: ${newStatus ? 'completed' : 'not completed'}`);
    showToast(newStatus ? "calendar.eventMarkedCompleted" : "calendar.eventMarkedNotCompleted", "success");

    // סגירת המודל
    closeEventDetail();
  } catch (error) {
    console.error("Error updating event completion status:", error);
    showToast("calendar.eventUpdateError", "error");
  }
};

const deleteEvent = async () => {
  if (!editingEvent.value) return;

  const performDelete = async () => {
    try {
      // קודם מחיקת המשתתפים
      const { error: participantsError } = await supabase
        .from("calendar_events_participants")
        .delete()
        .eq("event_id", editingEvent.value.id);

      if (participantsError) {
        console.error("Error deleting participants:", participantsError);
        showToast("calendar.deleteParticipantsError", "error");
        return;
      }

      // לאחר מכן מחיקת האירוע
      const { error: deleteError } = await supabase
        .from("calendar_events")
        .delete()
        .eq("id", editingEvent.value.id);

      if (deleteError) {
        console.error("Error deleting event:", deleteError);
        showToast("calendar.deleteEventError", "error");
        return;
      }

      // Find and remove the event from the events array
      const eventIndex = events.value.findIndex((e) => e.id === editingEvent.value.id);
      if (eventIndex !== -1) {
        events.value.splice(eventIndex, 1);
        console.log("Event deleted successfully");
        showToast("calendar.eventDeleted", "success");
      }

      // Close both modals
      closeEditEventModal();
      closeEventDetail();
    } catch (error) {
      console.error("Error deleting event:", error);
      showToast("calendar.deleteEventError", "error");
    }
  };

  // Show confirmation dialog
  showSimpleConfirmDialog("calendar.confirmDelete", performDelete);
};

const openSettingsModal = () => {
  showSettingsModal.value = true;
};

const openAppointmentTypesSettings = () => {
  activeTab.value = "types";
  showSettingsModal.value = true;
  // Only fetch products and users if not already loaded
  if (products.value.length === 0) {
    fetchProducts();
  }
  if (users.value.length === 0) {
    fetchUsers();
  }
  // Don't re-fetch appointment types as they're already loaded and up-to-date
};

const closeSettingsModal = () => {
  showSettingsModal.value = false;
};

const saveSettings = () => {
  // Implement save settings logic
  console.log("Settings saved:", blockedTimes.value, appointmentTypes.value);
  closeSettingsModal();
};

// Count appointments by type
const getAppointmentTypeCount = (typeName: string) => {
  return events.value.filter((event) => (event as any).type === typeName).length;
};

const myCalendarsExpanded = ref(true);

// Mobile responsive state
const mobileView = ref('sidebar'); // 'sidebar' or 'calendar'

const toggleMyCalendars = () => {
  myCalendarsExpanded.value = !myCalendarsExpanded.value;
};

// Mobile view toggle function
const toggleMobileView = () => {
  mobileView.value = mobileView.value === 'sidebar' ? 'calendar' : 'sidebar';
};

// Watch for mobileView changes and update thumb position
watch(mobileView, (newView) => {
  nextTick(() => {
    const targetValue = newView === 'sidebar' ? 0 : 1;
    const sliders = document.querySelectorAll('.slider-input');
    sliders.forEach(slider => {
      // עדכן רק אם הערך שונה כדי למנוע לולאות
      if (Math.abs(parseFloat(slider.value) - targetValue) > 0.1) {
        slider.value = targetValue;
      }
    });
    updateThumbPosition(targetValue);
  });
}, { immediate: true });

const handleSliderChange = (event) => {
  const value = parseFloat(event.target.value);
  const newView = value < 0.5 ? 'sidebar' : 'calendar';
  
  // עדכן את מיקום העיגול והתצוגות מיידית
  updateThumbPosition(value);
  
  // עדכן את התצוגה הנוכחית רק אם השתנתה
  if (newView !== mobileView.value) {
    mobileView.value = newView;
  }
};

const updateThumbPosition = (value) => {
  const thumbs = document.querySelectorAll('.slider-thumb');
  thumbs.forEach(thumb => {
    const track = thumb.parentElement;
    const trackWidth = track.offsetWidth;
    const thumbWidth = 40; // רוחב העיגול
    const maxMove = trackWidth - thumbWidth - 10; // מקום לתנועה (מינוס הpadding)
    const position = (value * maxMove) + 5; // 5px הוא המיקום התחילי
    
    thumb.style.left = `${position}px`;
    
    // האייקון נשאר קבוע - לא צריך לשנות אותו
    
    // עדכן את התוויות
    updateLabels(value);
  });
  
  // עדכן את מיקום התצוגות בזמן אמת
  updateViewPositions(value);
};

const updateViewPositions = (value) => {
  const sidebarCard = document.querySelector('.sidebar-card');
  const calendarCard = document.querySelector('.main-calendar-card');
  
  if (sidebarCard && calendarCard) {
    // חישוב אחוזי התזוזה (0 = סיידבר מלא, 1 = קלנדר מלא)
    const sidebarTranslateX = -100 * value; // סיידבר זז שמאלה
    const calendarTranslateX = 100 * (1 - value); // קלנדר זז מימינה
    
    // החל את הטרנספורמציה
    sidebarCard.style.transform = `translateX(${sidebarTranslateX}%)`;
    calendarCard.style.transform = `translateX(${calendarTranslateX}%)`;
    
    // עדכן שקיפות
    sidebarCard.style.opacity = 1 - value + 0.3; // מינימום 0.3
    calendarCard.style.opacity = value + 0.3; // מינימום 0.3
    
    // הצג/הסתר תצוגות בהתאם לערך
    if (value < 0.1) {
      sidebarCard.style.display = 'flex';
      calendarCard.style.display = 'none';
    } else if (value > 0.9) {
      sidebarCard.style.display = 'none';
      calendarCard.style.display = 'block';
    } else {
      sidebarCard.style.display = 'flex';
      calendarCard.style.display = 'block';
    }
  }
};

const updateLabels = (value) => {
  const labels = document.querySelectorAll('.slider-labels');
  labels.forEach(labelContainer => {
    const leftLabel = labelContainer.querySelector('.label-left');
    const rightLabel = labelContainer.querySelector('.label-right');
    
    if (leftLabel && rightLabel) {
      if (value < 0.5) {
        leftLabel.style.color = '#667eea';
        leftLabel.style.fontWeight = '600';
        rightLabel.style.color = '#64748b';
        rightLabel.style.fontWeight = '500';
      } else {
        leftLabel.style.color = '#64748b';
        leftLabel.style.fontWeight = '500';
        rightLabel.style.color = '#667eea';
        rightLabel.style.fontWeight = '600';
      }
    }
  });
};

// Touch events for thumb
let isDraggingSlider = false;
let startX = 0;

const handleThumbTouchStart = (e) => {
  e.preventDefault();
  e.stopPropagation();
  isDraggingSlider = true;
  startX = e.touches[0].clientX;
  
  // Add dragging class for enhanced animations
  const thumb = e.target.closest('.slider-thumb');
  if (thumb) {
    thumb.classList.add('dragging');
  }
  
  // הוסף event listeners לטאץ' גלובליים
  document.addEventListener('touchmove', handleThumbTouchMove, { passive: false });
  document.addEventListener('touchend', handleThumbTouchEnd, { passive: false });
};

const handleThumbTouchMove = (e) => {
  if (!isDraggingSlider) return;
  e.preventDefault();
  
  const slider = document.querySelector('.slider-input');
  if (!slider) return;
  
  const rect = slider.getBoundingClientRect();
  const x = e.touches[0].clientX - rect.left;
  const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
  const value = percentage / 100;
  
  slider.value = value;
  updateThumbPosition(value);
  
  // עדכן את התצוגה בזמן אמת בהתאם לערך
  const newView = value < 0.5 ? 'sidebar' : 'calendar';
  if (newView !== mobileView.value) {
    mobileView.value = newView;
  }
};

const handleThumbTouchEnd = (e) => {
  if (!isDraggingSlider) return;
  e.preventDefault();
  isDraggingSlider = false;
  
  // Remove dragging class for enhanced animations
  const thumbs = document.querySelectorAll('.slider-thumb');
  thumbs.forEach(thumb => {
    thumb.classList.remove('dragging');
  });
  
  // הסר event listeners
  document.removeEventListener('touchmove', handleThumbTouchMove);
  document.removeEventListener('touchend', handleThumbTouchEnd);
  
  const slider = document.querySelector('.slider-input');
  if (!slider) return;
  
  const value = parseFloat(slider.value);
  if (value < 0.5) {
    mobileView.value = 'sidebar';
  } else {
    mobileView.value = 'calendar';
  }
};

const handleThumbMouseDown = (e) => {
  e.preventDefault();
  e.stopPropagation();
  isDraggingSlider = true;
  startX = e.clientX;
  
  // Add dragging class for enhanced animations
  const thumb = e.target.closest('.slider-thumb');
  if (thumb) {
    thumb.classList.add('dragging');
  }
  
  const slider = e.target.closest('.view-toggle-slider').querySelector('.slider-input');
  if (!slider) return;
  
  const handleMouseMove = (e) => {
    if (!isDraggingSlider) return;
    
    const rect = slider.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const value = percentage / 100;
    
    slider.value = value;
    updateThumbPosition(value);
    
    // עדכן את התצוגה בזמן אמת בהתאם לערך
    const newView = value < 0.5 ? 'sidebar' : 'calendar';
    if (newView !== mobileView.value) {
      mobileView.value = newView;
    }
  };
  
  const handleMouseUp = () => {
    if (!isDraggingSlider) return;
    isDraggingSlider = false;
    
    // Remove dragging class for enhanced animations
    const thumbs = document.querySelectorAll('.slider-thumb');
    thumbs.forEach(thumb => {
      thumb.classList.remove('dragging');
    });
    
    const value = parseFloat(slider.value);
    if (value < 0.5) {
      mobileView.value = 'sidebar';
    } else {
      mobileView.value = 'calendar';
    }
    
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };
  
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
};

// Simple click handler for thumb
const handleThumbClick = (e) => {
  // רק אם לא בתהליך גרירה
  if (!isDraggingSlider) {
    e.preventDefault();
    e.stopPropagation();
    
    // החלף בין התצוגות
    if (mobileView.value === 'sidebar') {
      mobileView.value = 'calendar';
    } else {
      mobileView.value = 'sidebar';
    }
  }
};



// Computed properties for calendar users
const otherUsers = computed(() => {
  return allUsers.value.filter((user) => user.user_id !== currentUserId.value);
});

// Toggle user calendar visibility
const toggleUserCalendar = (userId: string) => {
  const index = selectedUserCalendars.value.indexOf(userId);
  if (index > -1) {
    selectedUserCalendars.value.splice(index, 1);
  } else {
    selectedUserCalendars.value.push(userId);
  }
  // Re-fetch events to show/hide this user's events
  fetchCalendarEvents();
};

// Get event count for a specific user (only future events)
const getUserEventCount = (userId: string) => {
  if (!userId) return 0;
  const now = new Date();

  // If we have loaded all users events for counting, use that (more accurate)
  if (allUsersEventsForCounting.value.length > 0) {
    return allUsersEventsForCounting.value.filter(
      (event) => event.userId === userId && event.start > now
    ).length;
  }

  // Otherwise, fall back to main events array (for current user's events only)
  return events.value.filter((event) => event.userId === userId && event.start > now)
    .length;
};

// Fetch all users from organization for calendar view
const fetchAllUsers = async (orgId: string) => {
  try {
    const { data, error } = await supabase
      .from("user_profiles")
      .select("user_id, display_name, email")
      .eq("org_id", orgId)
      .order("display_name");

    if (error) throw error;

    allUsers.value = data || [];
  } catch (error) {
    console.error("Error fetching users for calendar view:", error);
  }
};

// Fetch all users' events for counting purposes (only when user has permissions)
const fetchAllUsersEvents = async (orgId: string) => {
  try {
    console.log("🔄 Fetching all users events for counting...");

    const { data: eventsData, error: eventsError } = await supabase
      .from("calendar_events")
      .select("id, user_id, title, start_time, end_time")
      .eq("org_id", orgId)
      .gte("start_time", new Date().toISOString()) // Only future events
      .order("start_time", { ascending: true });

    if (eventsError) {
      console.error("❌ Error fetching all users events:", eventsError);
      return;
    }

    // Store all events for counting but don't replace the main events array
    allUsersEventsForCounting.value = eventsData.map((event) => ({
      id: event.id,
      userId: event.user_id,
      title: event.title,
      start: new Date(event.start_time),
      end: new Date(event.end_time),
    }));

    console.log(
      "✅ Loaded all users events for counting:",
      allUsersEventsForCounting.value.length
    );
  } catch (error) {
    console.error("❌ Error in fetchAllUsersEvents:", error);
  }
};

// Get user display name by ID
const getUserDisplayName = (userId: string) => {
  if (userId === currentUserId.value) {
    return userDisplayName.value;
  }

  const user = allUsers.value.find((u) => u.user_id === userId);
  return user ? user.display_name || user.email : "Unknown User";
};

// Get user initials by ID
const getUserInitials = (userId: string) => {
  const displayName = getUserDisplayName(userId);
  const names = displayName.split(" ");

  if (names.length >= 2) {
    return (names[0][0] + names[1][0]).toUpperCase();
  } else if (names.length === 1) {
    return names[0].substring(0, 2).toUpperCase();
  }

  return "U";
};

// Get user calendar color by index
const getUserCalendarColor = (index: number) => {
  const colors = [
    '#10b981', // green-500
    '#f59e0b', // amber-500  
    '#ef4444', // red-500
    '#8b5cf6', // violet-500
    '#06b6d4', // cyan-500
    '#f97316', // orange-500
    '#84cc16', // lime-500
    '#ec4899', // pink-500
    '#6366f1', // indigo-500
    '#14b8a6'  // teal-500
  ];
  return colors[index % colors.length];
};

// Refresh data when needed
const refreshAppointmentData = () => {
  fetchAppointmentTypes();
};

// Load user profile data
const loadUserProfile = async () => {
  try {
    // Get current session
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (session && session.user) {
      currentUserId.value = session.user.id;
      selectedUserCalendars.value = [session.user.id]; // Initially show current user's calendar

      // Get user email from auth
      const email = session.user.email || "";

      // Get user profile data from user_profiles table
      const { data, error } = await supabase
        .from("user_profiles")
        .select(
          "user_id, display_name, role, email, profile_picture_link, org_id, permissions_json"
        )
        .eq("user_id", session.user.id)
        .single();

      if (error) {
        console.error("Error fetching user profile:", error);
        // Fallback to email
        userDisplayName.value = email.split("@")[0] || "User";
        userRole.value = "User";
        return;
      }

      if (data) {
        // Use display_name for userName
        userDisplayName.value = data.display_name || email.split("@")[0] || "User";
        userRole.value = data.role || "User";

        // Set profile picture if available
        if (data.profile_picture_link && data.profile_picture_link.trim() !== "") {
          userProfilePicture.value = data.profile_picture_link;
        } else {
          // Keep using the default image when no profile picture is set
          userProfilePicture.value = defaultProfileImage;
        }

        // Load user permissions
        const permissions = data.permissions_json || {};
        userPermissions.value = {
          view_all_calendars: permissions.view_all_calendars === true,
          is_owner: permissions.is_owner === true,
          edit_all_leads: permissions.edit_all_leads === true,
          view_all_leads: permissions.view_all_leads === true,
          can_invite_users: permissions.can_invite_users === true,
        };

        // If user has permission to view all calendars, fetch all users and their events
        if (userPermissions.value.view_all_calendars && data.org_id) {
          await fetchAllUsers(data.org_id);
          // Temporarily load all users' events for accurate counts
          await fetchAllUsersEvents(data.org_id);
        }
      } else {
        // Fallback to email
        userDisplayName.value = email.split("@")[0] || "User";
        userRole.value = "User";
      }
    }
  } catch (error) {
    console.error("Error fetching user data:", error);
    userDisplayName.value = "User";
    userRole.value = "User";
  }
};

// Close dropdown when clicking outside
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement;
  if (!target.closest(".product-search-container")) {
    showProductDropdown.value = false;
  }
};

// Fetch calendar events from database
const fetchCalendarEvents = async () => {
  try {
    console.log("🔄 Starting to fetch calendar events...");
    const { userId, organizationId } = await getCurrentUserOrgId();
    console.log("👤 User ID:", userId);
    console.log("🏢 Organization ID:", organizationId);

    // Build query for calendar events based on user permissions and selected users
    let query = supabase
      .from("calendar_events")
      .select(
        `
        *,
        calendar_events_participants (
          id,
          lead_id,
          customer_id,
          name,
          phone,
          email,
          leads (
            id,
            name,
            email,
            phone
          ),
          clients (
            id,
            first_name,
            last_name,
            email,
            phone
          )
        ),
        customer_appointment_types (
          name,
          duration,
          color_code
        )
      `
      )
      .eq("org_id", organizationId)
      .gte("start_time", new Date(new Date().getFullYear() - 1, 0, 1).toISOString()) // מתחילת השנה הקודמת
      .lte("start_time", new Date(new Date().getFullYear() + 1, 11, 31).toISOString()) // עד סוף השנה הבאה
      .order("start_time", { ascending: true });

    // Filter events based on selected user calendars
    if (selectedUserCalendars.value.length > 0) {
      query = query.in("user_id", selectedUserCalendars.value);
    }

    // טעינת אירועים מהמסד נתונים
    const { data: eventsData, error: eventsError } = await query;

    if (eventsError) {
      console.error("❌ Error fetching events:", eventsError);
      return;
    }

    console.log("📊 Raw events data from database:", eventsData);
    console.log("📊 Number of events found:", eventsData?.length || 0);

    // המרת האירועים לפורמט המתאים להצגה
    events.value = eventsData.map((event) => {
      console.log("🔍 Processing event:", event.id, event.title);
      console.log("⏰ Event times:", event.start_time, "->", event.end_time);
      const startDate = createLocalDateFromDB(event.start_time);
      const endDate = createLocalDateFromDB(event.end_time);

      // קביעת צבע האירוע
      let colorClass = "event-blue";
      if (event.customer_appointment_types) {
        const colorMap: { [key: string]: string } = {
          "#fbbf24": "event-yellow",
          "#3b82f6": "event-blue",
          "#ec4899": "event-pink",
          "#22c55e": "event-green",
          "#8b5cf6": "event-purple",
          "#6b7280": "event-gray",
        };
        colorClass =
          colorMap[event.customer_appointment_types.color_code] || "event-blue";
      }

      // עיבוד משתתפים - עדיפות לנתונים מטבלאות הלידים/לקוחות
      const participants =
        event.calendar_events_participants?.map((p: any, index: number) => {
          let name, email, phone;
          
          if (p.leads) {
            // אם זה ליד - קח נתונים מטבלת הלידים
            name = p.leads.name;
            email = p.leads.email;
            phone = p.leads.phone;
          } else if (p.clients) {
            // אם זה לקוח - קח נתונים מטבלת הלקוחות
            name = `${p.clients.first_name || ""} ${p.clients.last_name || ""}`.trim();
            email = p.clients.email;
            phone = p.clients.phone;
          } else {
            // אם זה משתתף ידני - קח נתונים מטבלת המשתתפים
            name = p.name;
            email = p.email;
            phone = p.phone;
          }

          return {
            id: p.id,
            name: name || "ללא שם",
            email: email || "",
            phone: phone || "",
            avatar: "/api/placeholder/24/24",
            leadId: p.lead_id,
            clientId: p.customer_id,
          };
        }) || [];

      return {
        id: event.id,
        title: event.title,
        type: event.customer_appointment_types?.name || "אירוע",
        start: startDate,
        end: endDate,
        colorClass,
        timeDisplay: `${formatTimeInLocalTimezone(startDate)} - ${formatTimeInLocalTimezone(endDate)}`,
        location: event.location,
        description: event.description,
        dateDisplay: startDate.toLocaleDateString("he-IL", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        participants,
        userId: event.user_id, // Add user_id to track event owner
        is_over: event.is_over || false, // הוספת השדה is_over
      };
    });

    console.log("✅ Loaded events from database:", events.value.length);
    console.log("📅 Processed events:", events.value);

    // בדיקה נוספת עם הגנה
    events.value.forEach((event, index) => {
      if (!event || !event.start) {
        console.warn(`⚠️ Invalid event ${index + 1}:`, event);
        return;
      }
      
      console.log(`📌 Event ${index + 1}:`, {
        id: event.id,
        title: event.title,
        start: event.start,
        end: event.end,
        date: isNaN(event.start.getTime()) ? 'Invalid Date' : event.start.toLocaleDateString(),
        time: event.timeDisplay,
      });
    });
  } catch (error) {
    console.error("❌ Error in fetchCalendarEvents:", error);
  }
};

// Helper function to create ISO string without timezone conversion
const createLocalISOString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// Store original swipe handlers to restore them later
let originalTouchHandlers = null;

// Disable sidebar swipe gestures
const disableSidebarSwipe = () => {
  // Store original handlers and remove them
  originalTouchHandlers = [];
  
  // Override global touch event listeners that handle sidebar swipe
  const touchEvents = ['touchstart', 'touchmove', 'touchend'];
  touchEvents.forEach(eventType => {
    // Create a handler that prevents sidebar swipe but allows calendar interactions
    const preventSidebarHandler = (e) => {
      // Only prevent if it's a potential sidebar swipe gesture
      if (e.touches && e.touches.length === 1) {
        const touch = e.touches[0];
        const edgeThreshold = 30; // Same as LeftSidebar
        const isFromLeftEdge = touch.clientX < edgeThreshold;
        const isFromRightEdge = touch.clientX > window.innerWidth - edgeThreshold;
        
        // Prevent sidebar swipe from screen edges
        if (isFromLeftEdge || isFromRightEdge) {
          e.stopImmediatePropagation();
          e.preventDefault();
        }
      }
    };
    
    // Add our override handler with highest priority
    document.addEventListener(eventType, preventSidebarHandler, { 
      capture: true, 
      passive: false 
    });
    
    // Store for cleanup
    originalTouchHandlers.push({ eventType, handler: preventSidebarHandler });
  });
  
  // Also prevent sidebar from opening programmatically
  const originalStateStore = window.stateStore || {};
  if (originalStateStore.onChange) {
    originalStateStore._originalOnChange = originalStateStore.onChange;
    originalStateStore.onChange = () => {
      console.log("🚫 Sidebar state change blocked on calendar page");
      // Do nothing - prevent sidebar from opening
    };
  }
  
  console.log("🚫 Sidebar swipe gestures disabled for calendar page");
};

// Restore sidebar swipe gestures
const enableSidebarSwipe = () => {
  if (originalTouchHandlers) {
    originalTouchHandlers.forEach(({ eventType, handler }) => {
      document.removeEventListener(eventType, handler, { capture: true });
    });
    originalTouchHandlers = null;
  }
  
  // Restore original state store functionality
  const originalStateStore = window.stateStore || {};
  if (originalStateStore._originalOnChange) {
    originalStateStore.onChange = originalStateStore._originalOnChange;
    delete originalStateStore._originalOnChange;
  }
  
  console.log("✅ Sidebar swipe gestures restored");
};

// Load all data needed for the calendar
const loadAllData = async () => {
  try {
    isLoading.value = true;
    console.log("🚀 Loading calendar data...");

    // Load all data in parallel for better performance
    await Promise.all([
      loadUserProfile(),
      fetchAppointmentTypes(),
      fetchBreaks(),
      fetchEmployeeAvailability(),
      fetchProducts(),
      fetchUsers(),
      fetchCalendarEvents()
    ]);

    console.log("✅ All calendar data loaded successfully");
  } catch (error) {
    console.error("❌ Error loading calendar data:", error);
  } finally {
    isLoading.value = false;
  }
};

// Load data on component mount
onMounted(async () => {
  console.log("🚀 Calendar component mounted");
  console.log("📅 Current date:", new Date().toISOString());
  
  // בדיקה שהcurrentDate תקין לפני השימוש
  if (isNaN(currentDate.value.getTime())) {
    console.warn("⚠️ Invalid currentDate, resetting to now");
    currentDate.value = new Date();
  }
  console.log("📅 Calendar current date:", currentDate.value.toISOString());

  // Disable sidebar swipe gestures for this page
  disableSidebarSwipe();

  // Load all data
  await loadAllData();

  // Add event listener for clicking outside dropdown
  document.addEventListener("click", handleClickOutside);

  // Setup theme observer for real-time dark mode updates
  themeObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        updateDarkMode();
      }
    });
  });

  // Start observing changes to body class
  themeObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class']
  });

  // אתחול מיקום התצוגות במובייל
  nextTick(() => {
    if (window.innerWidth <= 768) {
      const initialValue = mobileView.value === 'sidebar' ? 0 : 1;
      updateThumbPosition(initialValue);
    }
  });
});

// Cleanup event listener
onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  
  // Restore sidebar swipe gestures when leaving the page
  enableSidebarSwipe();
  
  // Cleanup theme observer
  if (themeObserver) {
    themeObserver.disconnect();
    themeObserver = null;
  }
});
</script>

  <style scoped>
/* Calendar component specific styles - global utility classes are in style.scss */

/* Loading Screen Styles */
.animation-delay-200 {
  animation-delay: 200ms;
}

.animation-delay-400 {
  animation-delay: 400ms;
}

  <style scoped>
.new-calendar-app {
  display: flex;
  flex-direction: column;
  gap: 0;
  height: 100vh;
  background: white;
  overflow: hidden;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  padding: 24px;
  position: relative;
}

.dark .new-calendar-app {
  background: linear-gradient(to bottom right, #0f172a, #1e293b);
}

.new-calendar-app::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
      circle at 20% 80%,
      rgba(120, 119, 198, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(59, 130, 246, 0.05) 0%, transparent 50%);
  pointer-events: none;
  z-index: 0;
}

.new-calendar-app > * {
  position: relative;
  z-index: 1;
}

/* Calendar Layout */
.calendar-layout {
  display: flex;
  gap: 24px;
  flex: 1;
  min-height: 0;
}

/* Left Sidebar Card */
.sidebar-card {
  width: 320px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(239, 246, 255, 0.9) 50%,
    rgba(219, 234, 254, 0.8) 100%
  );
  backdrop-filter: blur(20px);
  padding: 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Ensure desktop always shows both sidebar and calendar */
@media (min-width: 769px) {
  .sidebar-card,
  .main-calendar-card {
    display: flex !important;
    position: relative !important;
    transform: none !important;
    opacity: 1 !important;
  }
  
  .main-calendar-card {
    display: block !important;
  }
  
  /* Hide mobile slider on desktop */
  .mobile-toggle-fixed {
    display: none !important;
  }
}

.dark .sidebar-card {
  background: linear-gradient(
    135deg,
    rgba(30, 41, 59, 0.9) 0%,
    rgba(51, 65, 85, 0.9) 50%,
    rgba(71, 85, 105, 0.8) 100%
  );
  border: 1px solid #374151;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

/* User Profile */
.user-profile {
  display: flex;
  align-items: center;
  gap: 16px;
  color: #1f2937;
  padding: 20px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.user-profile:hover {
  background: rgba(255, 255, 255, 0.6);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.user-avatar {
  position: relative;
}

.user-avatar img {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.notification-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
  border-radius: 12px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}

.user-info {
  flex: 1;
}

.user-name {
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #1f2937;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: -0.01em;
}

.user-role {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
}

.profile-actions {
  display: flex;
  gap: 8px;
}

.calendar-icon {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05));
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
}

.calendar-icon:hover {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.1));
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3);
}

/* Mini Calendar */
.mini-calendar {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 16px;
  padding: 20px;
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.mini-calendar:hover {
  background: rgba(255, 255, 255, 0.7);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.mini-calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.mini-calendar-header h4 {
  color: #1f2937;
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: -0.01em;
}

.mini-nav {
  display: flex;
  gap: 8px;
}

.mini-nav button {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05));
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 10px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  font-size: 16px;
}

.mini-nav button:hover {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.1));
  transform: translateY(-1px) scale(1.05);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.3);
}

.mini-calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.mini-day-header {
  text-align: center;
  font-size: 0.7rem;
  color: #6b7280;
  padding: 8px 4px;
  font-weight: 500;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.mini-day {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  color: #1f2937;
  cursor: pointer;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 500;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  position: relative;
}

.mini-day:hover {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05));
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.mini-day.today {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
  font-weight: 600;
  box-shadow: 0 6px 16px rgba(139, 92, 246, 0.4);
}

.mini-day.today:hover {
  transform: scale(1.1);
  box-shadow: 0 8px 20px rgba(139, 92, 246, 0.5);
}

.mini-day.selected {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.1));
  color: #1f2937;
  border: 2px solid #3b82f6;
}

.mini-day.other-month {
  opacity: 0.4;
  color: #9ca3af;
}

/* My Calendars & Categories */
.my-calendars,
.categories {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 16px;
  padding: 20px;
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.my-calendars:hover,
.categories:hover {
  background: rgba(255, 255, 255, 0.7);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header h4 {
  color: #1f2937;
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: -0.01em;
}

.expand-btn {
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(107, 114, 128, 0.8);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.875rem;
}

.expand-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  color: rgba(107, 114, 128, 1);
  transform: scale(1.05);
}

.expand-btn i {
  transition: transform 0.25s ease;
}

.expand-btn i.rotate-180 {
  transform: rotate(180deg);
}

.calendar-list,
.category-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.calendar-item,
.category-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.calendar-item:hover,
.category-item:hover {
  background: rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.calendar-checkbox {
  position: relative;
}

.calendar-checkbox input {
  opacity: 0;
  position: absolute;
}

.calendar-checkbox label {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  display: block;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.8);
}

.calendar-checkbox input:checked + label {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  border-color: #8b5cf6;
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}

.calendar-checkbox input:checked + label::after {
  content: "✓";
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.calendar-name,
.category-name {
  flex: 1;
  color: #1f2937;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif;
}

.calendar-count {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.1));
  color: #3b82f6;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif;
  border: 1px solid rgba(59, 130, 246, 0.3);
  backdrop-filter: blur(10px);
}

.category-color {
  width: 16px;
  height: 16px;
  border-radius: 8px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.category-progress {
  width: 60px;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  border-radius: 2px;
}

.progress-bar.personal {
  background: #fbbf24;
  width: 70%;
}

.progress-bar.work {
  background: #3b82f6;
  width: 45%;
}

.progress-bar.health {
  background: #ec4899;
  width: 85%;
}

/* Main Calendar Card */
.main-calendar-card {
  flex: 1;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(239, 246, 255, 0.9) 50%,
    rgba(219, 234, 254, 0.8) 100%
  );
  backdrop-filter: blur(20px);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 32px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.dark .main-calendar-card {
  background: linear-gradient(
    135deg,
    rgba(30, 41, 59, 0.9) 0%,
    rgba(51, 65, 85, 0.9) 50%,
    rgba(71, 85, 105, 0.8) 100%
  );
  border: 1px solid #374151;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

/* Calendar Header */
.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding: 0 4px;
  position: relative;
}

.calendar-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: -0.02em;
}

.header-left {
  flex: 1;
  display: flex;
  align-items: center;
}

.header-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  z-index: 1;
}

.header-right {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
}

.view-tabs {
  display: flex;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 16px;
  padding: 6px;
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.view-tab {
  padding: 12px 24px;
  border: none;
  background: transparent;
  color: #6b7280;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.875rem;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  position: relative;
}

.view-tab.active {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7));
  color: #1f2937;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
  font-weight: 600;
}

.view-tab:hover:not(.active) {
  background: rgba(255, 255, 255, 0.4);
  color: #374151;
  transform: translateY(-1px);
}

.nav-btn {
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.4));
  border-radius: 12px;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(15px);
  font-size: 18px;
}

.nav-btn:hover {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.1));
  color: #3b82f6;
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3);
}

.today-btn {
  padding: 12px 24px;
  border: 1px solid rgba(59, 130, 246, 0.3);
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-radius: 12px;
  color: white;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.875rem;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4);
}

.today-btn:hover {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.5);
}

.add-event-btn {
  padding: 12px 20px;
  border: 1px solid rgba(34, 197, 94, 0.3);
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: white;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 16px rgba(34, 197, 94, 0.4);
}

.add-event-btn:hover {
  background: linear-gradient(135deg, #16a34a, #15803d);
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 8px 24px rgba(34, 197, 94, 0.5);
}

/* Week Header */
.week-header {
  display: grid;
  grid-template-columns: 80px repeat(7, 1fr);
  gap: 0;
  margin-bottom: 16px;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  width: 100%;
  box-sizing: border-box;
}

.dark .week-header {
  background: #1f2937;
  border-color: #374151;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.time-column-header {
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 1.2rem;
  border-right: 1px solid #e2e8f0;
  padding: 16px 0;
  width: 100%;
  min-width: 0;
  height: 100%;
  min-height: 100%;
}

.dark .time-column-header {
  background: #1f2937;
  color: #9ca3af;
  border-right-color: #374151;
}

.day-header {
  background: #ffffff;
  padding: 16px 0;
  text-align: center;
  color: #374151;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 0;
  height: 100%;
  min-height: 100%;
  cursor: pointer;
  box-sizing: border-box;
}

.dark .day-header {
  background: #1f2937;
  color: #d1d5db;
  border-right-color: #374151;
}

.day-header:hover {
  background: #f3f4f6;
}

.dark .day-header:hover {
  background: #374151;
}

.day-header:last-child {
  border-right: none;
}

.day-header.today {
  background: #1f2937;
  color: white;
  padding: 16px 0;
  box-sizing: border-box;
  outline: none;
  box-shadow: none;
  transform: none;
  border-right: 1px solid #e2e8f0;
}

.day-header.today .day-name {
  font-size: 0.8rem;
  opacity: 1;
  margin-bottom: 4px;
}

.day-header.today .day-number {
  font-size: 1.2rem;
  font-weight: 700;
}

.day-header.today:hover {
  background: #374151;
  border-right: 1px solid #e2e8f0;
}

.day-header.selected {
  background: #3b82f6;
  color: white;
  border-right: 1px solid #e2e8f0;
  padding: 16px 0;
  box-sizing: border-box;
  outline: none;
  box-shadow: none;
  transform: none;
}

.day-header.selected .day-name {
  font-size: 0.8rem;
  opacity: 1;
  margin-bottom: 4px;
}

.day-header.selected .day-number {
  font-size: 1.2rem;
  font-weight: 700;
}

.day-header.selected:hover {
  background: #2563eb;
  border-right: 1px solid #e2e8f0;
}

.day-name {
  font-size: 0.8rem;
  opacity: 1;
  margin-bottom: 4px;
}

.day-number {
  font-size: 1.2rem;
  font-weight: 700;
}

/* Calendar Grid */
.calendar-grid {
  display: grid;
  grid-template-columns: 80px repeat(7, 1fr);
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  min-height: 600px;
  max-height: calc(100vh - 350px);
  overflow-y: auto;
}

.calendar-grid.day-grid {
  grid-template-columns: 80px 1fr;
}

/* Month View Styles */
.calendar-grid.month-grid {
  grid-template-columns: repeat(7, 1fr);
  min-height: 500px;
}

.calendar-content.month-content {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: repeat(6, 1fr);
  gap: 1px;
  background: #e2e8f0;
}

.day-column.month-day {
  background: white;
  padding: 8px;
  min-height: 80px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
}

.day-column.month-day:hover {
  background: #ffffff;
}

.day-column.month-day.today {
  background: rgba(59, 130, 246, 0.1);
}

.day-column.month-day.other-month {
  opacity: 0.3;
}

.month-day-number {
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.month-events {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.month-event {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.month-event:hover {
  transform: scale(1.02);
}

.more-events {
  font-size: 0.7rem;
  color: #6b7280;
  font-weight: 500;
  padding: 2px 6px;
  cursor: pointer;
}

.more-events:hover {
  color: #374151;
}

/* Time Column */
.time-column {
  background: #ffffff;
  border-right: 2px solid #e2e8f0;
}

.dark .time-column {
  background: #1f2937;
  border-right-color: #374151;
}

.time-slot {
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 500;
  border-bottom: 1px solid #f1f5f9;
}

/* Calendar Content */
.calendar-content {
  display: contents;
}

.day-column {
  border-right: 1px solid #e2e8f0;
  position: relative;
  min-height: 100%;
}

.day-column.today {
  background: rgba(59, 130, 246, 0.02);
}

.hour-slot {
  height: 100px;
  border-bottom: 1px solid #f1f5f9;
  position: relative;
  cursor: pointer;
  transition: background 0.2s ease;
}

.quarter-lines {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.quarter-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: rgba(226, 232, 240, 0.3);
  pointer-events: none;
}

.quarter-line:nth-child(2) {
  background: rgba(226, 232, 240, 0.5);
}

.hour-slot.occupied .quarter-lines {
  opacity: 0.2;
}

.hour-slot:hover {
  background: rgba(59, 130, 246, 0.05);
}

.hour-slot.occupied {
  background: linear-gradient(
    135deg,
    rgba(107, 114, 128, 0.5) 0%,
    rgba(156, 163, 175, 0.4) 50%,
    rgba(209, 213, 219, 0.3) 100%
  );
  cursor: not-allowed;
}

.hour-slot.occupied:hover {
  background: linear-gradient(
    135deg,
    rgba(75, 85, 99, 0.6) 0%,
    rgba(107, 114, 128, 0.5) 50%,
    rgba(156, 163, 175, 0.4) 100%
  );
}

.hour-slot.drag-over {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(29, 78, 216, 0.1));
  border: 2px dashed #3b82f6;
  animation: pulseGlow 1s ease-in-out infinite alternate;
}

/* Events */
.time-event {
  position: absolute;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  overflow: hidden;
  pointer-events: auto;
  z-index: 5;
}

.time-event:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  z-index: 10;
}

.time-event.dragging {
  opacity: 0.5;
  transform: rotate(5deg) scale(0.95);
  z-index: 1000;
}

.event-time {
  font-size: 0.7rem;
  opacity: 0.9;
  margin-bottom: 2px;
  font-weight: 600;
}

.event-title {
  font-weight: 600;
  font-size: 0.85rem;
  line-height: 1.2;
  margin-bottom: 4px;
}

.event-location {
  font-size: 0.7rem;
  opacity: 0.8;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}

.event-participants {
  display: flex;
  gap: 2px;
  margin-top: 4px;
}

.participant-avatar {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.participant-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.drag-handle {
  position: absolute;
  top: 4px;
  right: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  cursor: grab;
}

.time-event:hover .drag-handle {
  opacity: 1;
}

.drag-handle:active {
  cursor: grabbing;
}

/* User Indicator */
.event-user-indicator {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 20px;
  height: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(5px);
  z-index: 10;
  transition: all 0.2s ease;
  cursor: pointer;
}

.event-user-indicator:hover {
  background: rgba(0, 0, 0, 0.9);
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

/* RTL support for user indicator */
[dir="rtl"] .event-user-indicator,
.rtl .event-user-indicator {
  left: auto;
  right: 4px;
}

/* Event Colors - Matching the image exactly */
.event-blue {
  background: linear-gradient(135deg, #60a5fa, #3b82f6);
  color: white;
}

.event-green {
  background: linear-gradient(135deg, #4ade80, #22c55e);
  color: white;
}

.event-purple {
  background: linear-gradient(135deg, #a78bfa, #8b5cf6);
  color: white;
}

.event-purple-gradient {
  background: linear-gradient(135deg, #c084fc, #a855f7);
  color: white;
}

.event-yellow {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: white;
}

.event-pink {
  background: linear-gradient(135deg, #f472b6, #ec4899);
  color: white;
}

.event-gray {
  background: linear-gradient(135deg, #9ca3af, #6b7280);
  color: white;
}

.event-gray-light {
  background: linear-gradient(135deg, #d1d5db, #9ca3af);
  color: #374151;
}

.event-blue-light {
  background: linear-gradient(135deg, #93c5fd, #60a5fa);
  color: white;
}

.event-detail {
  background: linear-gradient(135deg, #e5e7eb, #d1d5db);
  color: #374151;
  border: 1px solid #d1d5db;
}

/* Custom Event Colors - for appointment types */
.event-custom-personal {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: white;
}

.event-custom-work {
  background: linear-gradient(135deg, #60a5fa, #3b82f6);
  color: white;
}

.event-custom-health {
  background: linear-gradient(135deg, #f472b6, #ec4899);
  color: white;
}

.event-custom-meeting {
  background: linear-gradient(135deg, #4ade80, #22c55e);
  color: white;
}

.event-custom-call {
  background: linear-gradient(135deg, #a78bfa, #8b5cf6);
  color: white;
}

.event-custom-task {
  background: linear-gradient(135deg, #9ca3af, #6b7280);
  color: white;
}

/* Event Detail Modal */
.event-detail-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}

.modal-content.settings-modal,
.modal-content.no-frame {
  background: transparent;
  border-radius: 0px;
  padding: 0px;
  border: none;
  box-shadow: none;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #1f2937;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.modal-body {
  margin-bottom: 20px;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  color: #6b7280;
  font-size: 0.9rem;
}

.detail-row i {
  width: 16px;
  text-align: center;
}

.detail-tags {
  display: flex;
  gap: 8px;
  margin: 16px 0;
  flex-wrap: wrap;
}

.tag {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.tag.design {
  background: #dbeafe;
  color: #1d4ed8;
}

.tag.personal {
  background: #fef3c7;
  color: #d97706;
}

.tag.dev {
  background: #e0e7ff;
  color: #6366f1;
}

.detail-participants {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
}

.participant {
  display: flex;
  align-items: center;
  gap: 8px;
}

.participant img {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
}

.participant span {
  font-size: 0.85rem;
  color: #374151;
}

.modal-footer {
  display: flex;
  gap: 12px;
  align-items: center;
}

.btn-secondary {
  padding: 8px 16px;
  border: 1px solid #dc2626;
  background: #dc2626;
  color: #ffffff;
  border-radius: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background: #b91c1c;
  border-color: #b91c1c;
}

.btn-primary {
  padding: 8px 16px;
  border: none;
  background: #1f2937;
  color: white;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
}

.btn-more {
  padding: 8px;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: auto;
}

.btn-more:hover {
  background: #e5e7eb;
  color: #374151;
}

/* Drag Ghost */
.drag-ghost {
  position: fixed;
  pointer-events: none;
  z-index: 9999;
  border-radius: 8px;
  padding: 8px 12px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  transform: rotate(5deg);
  animation: dragFloat 0.3s ease-out;
}

.ghost-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ghost-title {
  font-weight: 600;
  font-size: 0.85rem;
  color: white;
}

.ghost-time {
  font-size: 0.7rem;
  opacity: 0.8;
  color: white;
}

@keyframes dragFloat {
  0% {
    transform: rotate(0deg) scale(1);
    opacity: 0.8;
  }
  100% {
    transform: rotate(5deg) scale(1.05);
    opacity: 1;
  }
}

@keyframes pulseGlow {
  0% {
    box-shadow: 0 0 5px rgba(59, 130, 246, 0.3);
  }
  100% {
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.6);
  }
}

/* Responsive */
@media (max-width: 1024px) {
  .sidebar-card {
    width: 280px;
  }

  .calendar-title {
    font-size: 2rem;
  }
}

@media (max-width: 768px) {
  .new-calendar-app {
    flex-direction: column;
    padding: 16px;
    gap: 16px;
    margin-left: -15px !important;
    margin-right: -15px !important;
  }

  .sidebar-card {
    width: 100%;
    height: auto;
    max-height: 300px;
    flex-direction: row;
    gap: 16px;
    overflow-x: auto;
    padding: 16px;
  }

  .sidebar-card > * {
    flex-shrink: 0;
    min-width: 200px;
  }

  .main-calendar-card {
    border-radius: 16px;
    padding: 16px;
  }

  .calendar-header {
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
    position: static;
  }

  .header-left {
    width: 100%;
    justify-content: center;
  }

  .header-center {
    position: static;
    transform: none;
    width: 100%;
  }

  .header-right {
    width: 100%;
    justify-content: center;
  }

  .calendar-title {
    font-size: 1.8rem;
  }

  .view-tabs {
    width: 100%;
    justify-content: center;
  }

  .view-tab {
    flex: 1;
    padding: 10px 16px;
    font-size: 0.8rem;
  }

  /* Mobile calendar container - unified scrolling for header and calendar */
  .mobile-calendar-container {
    overflow-x: auto; /* Allow horizontal scrolling for entire container */
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background: white;
  }

  /* Mobile week header - part of scrollable content */
  .week-header {
    display: grid;
    grid-template-columns: 40px repeat(7, 80px); /* Match calendar grid columns */
    min-width: 600px; /* Same minimum width as calendar */
    margin-bottom: 0; /* Remove margin to connect with calendar */
    border-bottom: 1px solid #e2e8f0; /* Separate from calendar */
    border-radius: 12px 12px 0 0; /* Only top corners rounded */
    background: white;
    position: sticky;
    top: 0;
    z-index: 5;
  }

  /* Mobile time column header - smaller to match calendar */
  .time-column-header {
    padding: 8px 0;
    font-size: 1rem;
  }

  /* Mobile day headers - consistent sizing */
  .day-header {
    padding: 8px 0;
    min-width: 80px;
    margin-bottom: 0;
  }

  /* Mobile calendar with horizontal and vertical scroll */
  .calendar-grid {
    display: grid;
    grid-template-columns: 40px repeat(7, 80px); /* Smaller time column, equal day columns */
    min-width: 600px; /* Minimum width to force horizontal scroll */
    max-height: calc(100vh - 280px);
    overflow-y: auto; /* Only vertical scrolling - horizontal handled by container */
    background: white;
    border-radius: 0 0 12px 12px; /* Only bottom corners rounded */
    margin-bottom: 10px;
  }

  .time-slot {
    height: 100px;
    font-size: 0.6rem;
    padding: 4px 2px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .hour-slot {
    height: 100px;
  }

  .time-event {
    padding: 6px 8px;
    font-size: 0.75rem;
  }

  .event-title {
    font-size: 0.75rem;
  }

  .event-time {
    font-size: 0.65rem;
  }

  /* Mobile day numbers - smaller size */
  .day-number {
    font-size: 0.9rem;
    font-weight: 600;
  }

  .day-name {
    display: none; /* Hide day names in mobile */
  }
}

/* Add Event Modal Styles - Modern Design */
.add-event-modal {
  max-width: 600px;
  width: 95%;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Modal Header with Gradient */
.modal-header-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 24px 32px;
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  overflow: hidden;
}

.modal-header-gradient::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
  pointer-events: none;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.header-icon i {
  font-size: 24px;
  color: white;
}

.header-text {
  flex: 1;
}

.header-text h3 {
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 4px 0;
}

.header-text p {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  margin: 0;
}

.close-btn-modern {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 10px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.close-btn-modern:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

/* Modal Body */
.modal-body-modern {
  padding: 32px;
  background: white;
}

/* Form Groups */
.form-group-modern {
  margin-bottom: 24px;
  position: relative;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  color: #374151;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: right;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.modern-input {
  width: 100%;
  padding: 14px 16px 14px 48px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  background: white;
  transition: all 0.3s ease;
  outline: none;
  position: relative;
  z-index: 1;
}

.modern-input:focus {
  border-color: #667eea;
  background: white;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
}

.modern-input:focus ~ .input-icon {
  color: #667eea;
}

.input-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  font-size: 18px;
  z-index: 2;
  transition: all 0.3s ease;
}

/* Textarea specific */
.textarea-wrapper .input-icon {
  top: 20px;
  transform: none;
}

.modern-textarea {
  resize: vertical;
  min-height: 100px;
  padding-top: 14px;
}

/* Select Styling */
.modern-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: left 16px center;
  background-repeat: no-repeat;
  background-size: 16px 12px;
  padding-left: 48px;
}

.modern-textarea:focus + .floating-label,
.modern-textarea:not(:placeholder-shown) + .floating-label {
  transform: translateY(-32px) scale(0.85);
  color: #667eea;
  background: white;
  padding: 2px 8px;
  border-radius: 4px;
  z-index: 4;
}

/* Time Section */
.time-section {
  margin: 32px 0;
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 20px 0;
  color: #374151;
  font-size: 1.1rem;
  font-weight: 600;
}

.section-title i {
  color: #667eea;
  font-size: 20px;
}

.time-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

/* Modal Footer */
.modal-footer-modern {
  padding: 24px 32px;
  background: #f8fafc;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  border-top: 1px solid #e5e7eb;
}

.btn-modern {
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  min-width: 120px;
  justify-content: center;
}

.btn-cancel {
  background: #f3f4f6;
  color: #6b7280;
  border: 2px solid #e5e7eb;
}

.btn-cancel:hover {
  background: #e5e7eb;
  color: #374151;
  transform: translateY(-1px);
}

.btn-save {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.btn-save:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

/* Responsive Design */
@media (max-width: 768px) {
  .add-event-modal {
    max-width: 95%;
    margin: 10px;
    border-radius: 16px;
  }

  .modal-header-gradient {
    padding: 20px 24px;
  }

  .modal-body-modern {
    padding: 24px 20px;
  }

  .form-group-modern {
    margin-bottom: 20px;
  }

  .time-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .time-section {
    padding: 20px 16px;
    margin: 24px 0;
  }

  .modal-footer-modern {
    padding: 20px 24px;
    flex-direction: column;
  }

  .btn-modern {
    width: 100%;
    padding: 14px 24px;
  }

  .modern-input {
    padding: 12px 14px 12px 44px;
    font-size: 0.95rem;
  }

  .input-icon {
    left: 14px;
    font-size: 16px;
  }

  .field-label {
    font-size: 0.85rem;
  }

  .participant-inputs {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

.input-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  font-size: 18px;
  z-index: 2;
  transition: all 0.3s ease;
}

/* Participants Section */
.participants-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.add-participant-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.add-participant-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.participants-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.participant-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.participant-inputs {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

.participant-input {
  margin: 0;
}

.remove-participant-btn {
  width: 36px;
  height: 36px;
  background: #fee2e2;
  color: #dc2626;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.remove-participant-btn:hover {
  background: #fecaca;
  transform: scale(1.05);
}

/* Add Event Modal Styles - Clean Modern Design */
.add-event-modal {
  max-width: 700px;
  width: 95%;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  animation: modalSlideIn 0.3s ease-out;
  background: white;
}

/* Remove frame when no-frame class is applied */
.add-event-modal.no-frame {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

/* Modal Header - Clean */
.modal-header-clean {
  padding: 24px 32px;
  background: linear-gradient(135deg, #007aff 0%, #5856d6 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 16px 16px 0 0;
  position: relative;
}

.modal-header-clean::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
  pointer-events: none;
  border-radius: 16px 16px 0 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon-clean {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.header-text-clean h3 {
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 4px 0;
}

.header-text-clean p {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  margin: 0;
}

.close-btn-clean {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 10px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
}

.close-btn-clean:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

/* Modal Body - Clean */
.modal-body-clean {
  padding: 32px;
  background: white;
}

.event-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Form Rows */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-row:has(.form-group:nth-child(3)) {
  grid-template-columns: 1fr 1fr 1fr;
}

/* Form Groups */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  color: #374151;
  font-size: 0.875rem;
  font-weight: 600;
}

/* Event Detail Styles - Clean Design */
.event-detail-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.detail-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-row-clean {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background: rgba(248, 250, 252, 0.5);
  border-radius: 12px;
  border: 1px solid rgba(229, 231, 235, 0.5);
  transition: all 0.2s ease;
}

.detail-row-clean:hover {
  background: rgba(248, 250, 252, 0.8);
  border-color: rgba(209, 213, 219, 0.8);
}

.detail-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05));
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-size: 18px;
  flex-shrink: 0;
}

.detail-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-value {
  font-size: 0.95rem;
  color: #1f2937;
  font-weight: 500;
  line-height: 1.5;
}

.participants-section {
  background: rgba(248, 250, 252, 0.5);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(229, 231, 235, 0.5);
}

.section-title {
  margin: 0 0 16px 0;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
}

.participants-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.participant-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 10px;
  border: 1px solid rgba(229, 231, 235, 0.5);
  transition: all 0.2s ease;
}

.participant-item:hover {
  border-color: rgba(59, 130, 246, 0.3);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}

.participant-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(229, 231, 235, 0.5);
}

.participant-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.participant-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f2937;
}

.participant-email {
  font-size: 0.75rem;
  color: #6b7280;
}

.participant-phone {
  font-size: 0.75rem;
  color: #8b5cf6;
  font-weight: 500;
}

/* Participant item for event detail view */
.participant-item-detail {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 10px;
  border: 1px solid rgba(229, 231, 235, 0.5);
  transition: all 0.2s ease;
}

.participant-item-detail:hover {
  border-color: rgba(59, 130, 246, 0.3);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}

.participant-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05));
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-size: 18px;
  flex-shrink: 0;
}

/* Input Groups */
.input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.form-input {
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.9rem;
  background: white;
  transition: all 0.2s ease;
  outline: none;
}

.form-input::placeholder {
  color: #6b7280;
  opacity: 1;
  font-size: 0.875rem;
}

.form-textarea::placeholder {
  color: #6b7280;
  opacity: 1;
  font-size: 0.875rem;
}

.form-select {
  color: #6b7280;
}

.form-select:valid {
  color: #374151;
}

.form-select option {
  color: #374151;
}

.form-select option[value=""] {
  color: #6b7280;
}

/* Fix placeholder visibility with icons */
.input-group .form-input {
  position: relative;
  z-index: 0;
}

.input-group .input-icon-clean {
  background: transparent;
}

/* Ensure placeholder text is not hidden */
.form-input:placeholder-shown {
  text-indent: 0;
}

/* Fix icon position for textarea */
.input-group:has(.form-textarea) .input-icon-clean {
  top: 14px;
  transform: none;
}

/* RTL Support for inputs */
[dir="rtl"] .form-input,
[dir="rtl"] .form-select,
[dir="rtl"] .form-textarea,
.rtl .form-input,
.rtl .form-select,
.rtl .form-textarea {
  text-align: right;
  direction: rtl;
}

[dir="rtl"] .form-input::placeholder,
[dir="rtl"] .form-select::placeholder,
[dir="rtl"] .form-textarea::placeholder,
.rtl .form-input::placeholder,
.rtl .form-select::placeholder,
.rtl .form-textarea::placeholder {
  text-align: right;
}

/* Fix phone input RTL */
[dir="rtl"] input[type="tel"],
.rtl input[type="tel"] {
  text-align: right;
  direction: ltr;
}

[dir="rtl"] input[type="tel"]::placeholder,
.rtl input[type="tel"]::placeholder {
  text-align: right;
}

/* RTL icon positions */
[dir="rtl"] .input-icon-clean,
.rtl .input-icon-clean {
  left: auto;
  right: 16px;
}

[dir="rtl"] .form-input,
[dir="rtl"] .form-textarea,
.rtl .form-input,
.rtl .form-textarea {
  padding: 12px 48px 12px 16px;
}

[dir="rtl"] .participant-fields .input-group .form-input,
.rtl .participant-fields .input-group .form-input {
  padding: 12px 48px 12px 16px;
}

/* RTL for inputs without icons */
[dir="rtl"] .input-group:not(:has(.input-icon-clean)) .form-input,
[dir="rtl"] .input-group:not(:has(.input-icon-clean)) .form-select,
[dir="rtl"] .input-group:not(:has(.input-icon-clean)) .form-textarea,
.rtl .input-group:not(:has(.input-icon-clean)) .form-input,
.rtl .input-group:not(:has(.input-icon-clean)) .form-select,
.rtl .input-group:not(:has(.input-icon-clean)) .form-textarea {
  padding: 12px 16px;
}

.form-input:focus {
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-select {
  appearance: none;
  background-color: white;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 16px center;
  background-repeat: no-repeat;
  background-size: 16px 12px;
  padding-right: 44px;
  cursor: pointer;
}

.form-select:focus {
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-select option {
  padding: 8px 12px;
  background: white;
  color: #374151;
  font-size: 0.9rem;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
  padding: 12px 16px 12px 48px;
  border-radius: 12px;
}

.input-icon-clean {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 14px;
  z-index: 1;
  transition: all 0.2s ease;
  pointer-events: none;
  opacity: 0.6;
}

.form-input:focus ~ .input-icon-clean,
.form-input:focus + .input-icon-clean {
  color: #3b82f6;
  opacity: 1;
}

.form-select ~ .input-icon-clean {
  pointer-events: none;
  z-index: 1;
}

.form-select:focus ~ .input-icon-clean {
  color: #3b82f6;
}

/* Participants Section - Clean */
.participants-header-clean {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.participants-actions {
  display: flex;
  gap: 8px;
}

.contact-search-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.search-contacts-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #f3f4f6;
  color: #374151;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.search-contacts-btn:hover {
  background: #e5e7eb;
  color: #1f2937;
  border-color: #d1d5db;
  transform: translateY(-1px);
}

.search-leads-btn,
.search-clients-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 2px solid transparent;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
  min-width: 120px;
  justify-content: center;
}

.search-leads-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-color: #2563eb;
}

.search-leads-btn:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  border-color: #1d4ed8;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.search-clients-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-color: #2563eb;
}

.search-clients-btn:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  border-color: #1d4ed8;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.add-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-btn:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

/* Contact Search Modal */
.contact-search-modal {
  max-width: 500px;
  width: 95%;
  max-height: 80vh;
  overflow: hidden;
  border-radius: 16px;
}

.contact-search-body {
  padding: 24px 32px;
  background: white;
  max-height: 60vh;
  overflow-y: auto;
}

.search-input-group {
  position: relative;
  margin-bottom: 24px;
}

.contact-search-input {
  width: 100%;
  padding: 14px 16px 14px 48px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  background: white;
  transition: all 0.2s ease;
  outline: none;
}

.contact-search-input:focus {
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  font-size: 18px;
  z-index: 2;
}

.contact-search-input:focus ~ .search-icon {
  color: #3b82f6;
}

/* Contact Results */
.contact-results {
  min-height: 200px;
}

.search-loading,
.no-results,
.search-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #6b7280;
  text-align: center;
}

.search-loading i,
.no-results i,
.search-placeholder i {
  font-size: 2.5rem;
  margin-bottom: 12px;
  opacity: 0.5;
}

.search-loading p,
.no-results p,
.search-placeholder p {
  margin: 0;
  font-size: 0.9rem;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Type Edit Form Styles */
.type-edit-form {
  background: #f8fafc;
  border-radius: 8px;
  padding: 20px;
  margin-top: 12px;
  border: 1px solid #e2e8f0;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
}

.form-input {
  padding: 8px 12px;
  border: 1px solidrgb(168, 168, 168);
  border-radius: 12px;
  font-size: 0.875rem;
  transition: border-color 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-select {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;
}

.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 500;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
}

.users-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
  max-height: 150px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
}

.user-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.user-checkbox:hover {
  background: #f3f4f6;
}

.user-checkbox input[type="checkbox"] {
  width: 14px;
  height: 14px;
  accent-color: #3b82f6;
  margin-right: 8px;
}

/* RTL support for user checkbox */
[dir="rtl"] .user-checkbox input[type="checkbox"],
.rtl .user-checkbox input[type="checkbox"] {
  margin-right: 0;
  margin-left: 8px;
}

/* Fix minutes text color */
.minutes-text {
  color: #374151;
}

.duration-label {
  color: #374151;
}

/* White background for tag input */
.tag-input {
  background-color: white !important;
  border-radius: 12px !important;
}

/* Fix duration text color */
.duration {
  color: #374151 !important;
}

.type-meta .duration {
  color: #6b7280 !important;
}

/* Ensure all form labels and text are dark */
.type-edit-form label {
  color: #374151 !important;
}

.form-group label {
  color: #374151 !important;
}

.checkbox-label {
  color: #374151 !important;
}

.loading-users,
.loading-products {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  color: #6b7280;
  font-size: 0.875rem;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

/* RTL support for form actions */
.form-actions.rtl .btn-secondary {
  order: 3;
}

.form-actions.rtl .btn-primary {
  order: 1;
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  border-radius: 12px;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #dc2626;
  color: #ffffff;
  border: 1px solid #dc2626;
  border-radius: 12px;
}

.btn-secondary:hover:not(:disabled) {
  background: #b91c1c;
  border-color: #b91c1c;
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Type Actions */
.type-actions {
  display: flex;
  gap: 8px;
}

.edit-btn,
.remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.edit-btn {
  background: #e0f2fe;
  color: #0369a1;
}

.edit-btn:hover {
  background: #0369a1;
  color: white;
}

.remove-btn {
  background: #fef2f2;
  color: #dc2626;
}

.remove-btn:hover:not(:disabled) {
  background: #dc2626;
  color: white;
}

.remove-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Enhanced type name styling */
.type-name.editing {
  border: 1px solid #3b82f6;
  border-radius: 12px;
  padding: 4px 8px;
  background: white;
}

.tag {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
}

/* Duration input improvements */
.duration-input {
  display: flex;
  align-items: center;
  gap: 8px;
}

.duration-input input {
  width: 80px;
  text-align: center;
  border-radius: 12px;
}

.duration-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.duration-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

/* Color grid improvements */
.color-grid {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.color-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
}

.color-dot:hover {
  transform: scale(1.1);
}

.color-dot.active {
  border-color: #1f2937;
  box-shadow: 0 0 0 2px white, 0 0 0 4px #3b82f6;
}

/* Contacts List */
.contacts-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.contact-item:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.contact-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.contact-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: 600;
}

.contact-info {
  flex: 1;
}

.contact-name {
  margin: 0 0 4px 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
}

.contact-email {
  margin: 0 0 2px 0;
  font-size: 0.85rem;
  color: #6b7280;
}

.contact-phone {
  margin: 0;
  font-size: 0.8rem;
  color: #9ca3af;
}

.contact-status {
  font-size: 0.75rem;
  color: #8b5cf6;
  margin: 4px 0 0 0;
  font-weight: 500;
  background: #ede9fe;
  padding: 2px 8px;
  border-radius: 12px;
  display: inline-block;
}

.contact-select {
  color: #3b82f6;
  font-size: 1.2rem;
  transition: all 0.2s ease;
}

.contact-item:hover .contact-select {
  color: #2563eb;
  transform: scale(1.1);
}

.participants-list-clean {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.participant-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.participant-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.participant-fields {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

.participant-input {
  margin: 0;
  background: white;
  border-color: #d1d5db;
}

.participant-fields .input-group .form-input {
  padding-left: 48px;
}

.remove-btn {
  width: 36px;
  height: 36px;
  background: #fee2e2;
  color: #dc2626;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.remove-btn:hover {
  background: #fecaca;
  transform: scale(1.05);
}

/* Modal Footer - Clean */
.modal-footer-clean {
  padding: 24px 32px;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  border-radius: 0 0 16px 16px;
}

.btn-clean {
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  min-width: 120px;
  justify-content: center;
}

.btn-secondary {
  background: #dc2626;
  color: #ffffff;
  border: 2px solid #dc2626;
  border-radius: 12px;
}

.btn-secondary:hover {
  background: #b91c1c;
  color: #ffffff;
  border-color: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.2);
}

.btn-primary {
  background: #3b82f6;
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  border-radius: 12px;
}

.btn-primary:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
}

.btn-danger {
  background: #ef4444;
  color: white;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  border-radius: 12px;
}

.btn-danger:hover {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.4);
}

/* Responsive Design - Clean */
@media (max-width: 768px) {
  .add-event-modal {
    max-width: 95%;
    margin: 10px;
  }

  .add-event-modal.no-frame {
    border-radius: 12px;
  }

  .modal-header-clean {
    padding: 20px 24px;
    border-radius: 12px 12px 0 0;
  }

  .modal-footer-clean {
    border-radius: 0 0 12px 12px;
  }

  .modal-body-clean {
    padding: 24px 20px;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .participant-fields {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .modal-footer-clean {
    padding: 20px 24px;
    flex-direction: column;
  }

  .btn-clean {
    width: 100%;
  }

  .form-input {
    padding: 10px 14px 10px 48px;
    font-size: 0.85rem;
  }

  .input-icon-clean {
    left: 12px;
    font-size: 14px;
  }

  .participants-actions {
    flex-direction: column;
    gap: 8px;
  }

  .search-contacts-btn,
  .add-btn {
    width: 100%;
    justify-content: center;
  }
}

/* Ensure select dropdown is properly styled */
.form-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-color: white;
  position: relative;
  z-index: 2;
}

.form-select::-ms-expand {
  display: none;
}

/* Fix for Safari */
.form-select:focus {
  outline: none;
}

/* Date and Time inputs without icons */
input[type="date"].form-input,
input[type="time"].form-input {
  padding: 12px 16px;
}

/* Inputs without icons */
.input-group:not(:has(.input-icon-clean)) .form-input,
.input-group:not(:has(.input-icon-clean)) .form-select,
.input-group:not(:has(.input-icon-clean)) .form-textarea {
  padding: 12px 16px;
}

/* Calendar Settings */
.calendar-settings {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 16px;
  backdrop-filter: blur(10px);
}

.rotate-180 {
  transform: rotate(180deg);
}

.settings-content {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.setting-group {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 12px;
}

.setting-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
}

.add-setting-btn {
  background: rgba(139, 92, 246, 0.8);
  border: none;
  border-radius: 6px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: auto;
}

.add-setting-btn:hover {
  background: #8b5cf6;
  transform: scale(1.1);
}

/* Blocked Times */
.blocked-times-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.blocked-time-item {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px;
  position: relative;
}

.time-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.time-input {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 6px 8px;
  color: white;
  font-size: 0.8rem;
  width: 80px;
}

.time-input:focus {
  outline: none;
  border-color: #8b5cf6;
  background: rgba(255, 255, 255, 0.15);
}

.time-separator {
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
}

.block-days {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.day-checkbox {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
}

.day-checkbox input {
  width: 12px;
  height: 12px;
  accent-color: #8b5cf6;
}

.remove-setting-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(239, 68, 68, 0.8);
  border: none;
  border-radius: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.7rem;
}

.remove-setting-btn:hover {
  background: #ef4444;
  transform: scale(1.1);
}

/* Appointment Types */
.appointment-types-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.appointment-type-item {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px;
  position: relative;
}

.type-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.type-name-input {
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 8px 10px;
  color: white;
  font-size: 0.85rem;
}

.type-name-input:focus {
  outline: none;
  border-color: #8b5cf6;
  background: rgba(255, 255, 255, 0.15);
}

.type-name-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.type-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.duration-input {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
}

.duration-field {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 6px 8px;
  color: white;
  font-size: 0.8rem;
  width: 60px;
  text-align: center;
}

.duration-field:focus {
  outline: none;
  border-color: #8b5cf6;
  background: rgba(255, 255, 255, 0.15);
}

.color-picker {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
}

.color-options {
  display: flex;
  gap: 6px;
}

.color-option {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.color-option:hover {
  transform: scale(1.1);
}

.color-option.active {
  border-color: white;
  transform: scale(1.2);
}

.fallback-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
}

.manager-image {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

/* Settings Modal Styles */
.settings-modal {
  max-width: 1000px;
  width: 95%;
  max-height: 95vh;
  overflow: hidden;
  border-radius: 20px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  border: none;
}

.dark .settings-modal {
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  border-radius: 20px;
}

.settings-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;
  border-radius: 20px 20px 0px 0px;
}

.settings-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
  pointer-events: none;
}

.settings-icon {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.settings-body {
  max-height: 75vh;
  overflow-y: auto;
  padding: 32px;
  background: white;
  border-radius: 0px;
}

.dark .settings-body {
  background: #1e293b;
}

.settings-section {
  margin-bottom: 40px;
  padding: 32px;
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.settings-section:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.dark .settings-section {
  background: #334155;
  border: 1px solid #475569;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.dark .settings-section:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
}

.section-header-settings {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f1f5f9;
}

.dark .section-header-settings {
  border-bottom: 2px solid #475569;
}

.section-title-settings {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.section-title-settings i {
  color: #667eea;
  font-size: 1.5rem;
  margin-top: 4px;
}

.title-group h4 {
  margin: 0 0 4px 0;
  color: #1f2937;
  font-size: 1.3rem;
  font-weight: 700;
}

.section-description {
  margin: 0;
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.4;
}

.dark .title-group h4 {
  color: #f1f5f9;
}

.dark .section-description {
  color: #94a3b8;
}

.add-setting-btn-clean {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.add-setting-btn-clean:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

/* Working Hours Styles */
.working-hours-section {
  background: white;
  border-left: 4px solid #667eea;
}

.dark .working-hours-section {
  background: #334155;
}

.working-hours-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

input:checked + .toggle-slider {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

input:checked + .toggle-slider:before {
  transform: translateX(26px);
}

.toggle-label {
  color: #374151;
  font-weight: 600;
  font-size: 0.9rem;
}

.dark .toggle-label {
  color: #e2e8f0;
}

.working-hours-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 24px;
}

.working-day-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
}

.working-day-card:hover {
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
}

.dark .working-day-card {
  background: #475569;
  border: 2px solid #64748b;
}

.dark .working-day-card:hover {
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.working-day-card.disabled {
  background: #f9fafb;
  border-color: #e5e7eb;
  opacity: 0.7;
}

.dark .working-day-card.disabled {
  background: #1e293b;
  border-color: #334155;
}

.day-header {
  margin-bottom: 16px;
}

.day-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.day-checkbox {
  position: relative;
  cursor: pointer;
}

.day-checkbox input {
  opacity: 0;
  position: absolute;
}

.checkmark {
  width: 20px;
  height: 20px;
  background: #f3f4f6;
  border: 2px solid #d1d5db;
  border-radius: 6px;
  display: block;
  transition: all 0.3s ease;
}

.day-checkbox input:checked + .checkmark {
  background: #667eea;
  border-color: #667eea;
}

.day-checkbox input:checked + .checkmark::after {
  content: "✓";
  color: white;
  font-weight: bold;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.8rem;
}

.day-details h5 {
  margin: 0 0 2px 0;
  color: #1f2937;
  font-size: 1rem;
  font-weight: 600;
}

.day-short {
  color: #6b7280;
  font-size: 0.8rem;
}

.dark .day-details h5 {
  color: #f1f5f9;
}

.dark .day-short {
  color: #94a3b8;
}

.day-times {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.time-input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.time-label {
  color: #374151;
  font-size: 0.8rem;
  font-weight: 600;
}

.dark .time-label {
  color: #e2e8f0;
}

.time-input {
  width: 100%;
}

.day-disabled {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  color: #9ca3af;
  font-weight: 500;
}

.day-disabled i {
  font-size: 1.2rem;
}

/* Break Times Styles */
.blocked-times-list-clean {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.blocked-time-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  position: relative;
  transition: all 0.3s ease;
  border-left: 4px solid #f59e0b;
}

.blocked-time-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.dark .blocked-time-card {
  background: #334155;
  border: 1px solid #475569;
}

.dark .blocked-time-card:hover {
  border-color: #64748b;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.break-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.time-inputs-clean {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.time-input-clean {
  width: 140px;
}

.time-separator-clean {
  color: #6b7280;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0 8px;
}

.days-selection {
  margin-bottom: 16px;
}

.days-grid {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
}

.day-checkbox-clean {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f8fafc;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.85rem;
}

.day-checkbox-clean:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.day-checkbox-clean.active {
  background: #667eea;
  border-color: #667eea;
  color: white;
}

.day-checkbox-clean input {
  width: 16px;
  height: 16px;
  accent-color: #667eea;
}

.day-label {
  font-weight: 500;
}

/* Appointment Types Styles */
.appointment-types-section {
  background: linear-gradient(135deg, #fef7ff 0%, #f3e8ff 100%);
  border-left: 4px solid #a855f7;
}

.appointment-types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
}

.appointment-type-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  position: relative;
  transition: all 0.3s ease;
  border-left: 4px solid #3b82f6;
}

.premium-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.premium-card:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.type-header-clean {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.type-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.type-info {
  flex: 1;
}

.type-name-input {
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  background: transparent;
  padding: 8px 0;
  color: #1f2937;
}

.type-name-input:focus {
  background: #f9fafb;
  border-radius: 8px;
  padding: 8px 12px;
}

.type-meta {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.duration-badge,
.color-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.duration-badge {
  background: #e0e7ff;
  color: #3730a3;
}

.color-badge {
  color: white;
  text-transform: capitalize;
}

.type-details-clean {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.duration-section,
.color-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.duration-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.duration-btn {
  width: 36px;
  height: 36px;
  border: 2px solid #e5e7eb;
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6b7280;
}

.duration-btn:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
  background: #f8fafc;
}

.duration-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.duration-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.duration-input {
  width: 80px;
  text-align: center;
  font-weight: 600;
  font-size: 1.1rem;
}

.duration-unit {
  color: #6b7280;
  font-size: 0.9rem;
  font-weight: 500;
}

.color-options-clean {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.color-option-clean {
  position: relative;
  height: 60px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 3px solid transparent;
  display: flex;
  align-items: end;
  padding: 8px;
  overflow: hidden;
}

.color-option-clean:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.color-option-clean.active {
  border-color: #1f2937;
  transform: scale(1.08);
}

.color-name {
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.remove-btn-clean {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  background: #fee2e2;
  color: #dc2626;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-size: 1rem;
}

.remove-btn-clean:hover {
  background: #fecaca;
  transform: scale(1.1);
}

.type-remove {
  position: relative;
  top: auto;
  right: auto;
  margin-left: auto;
}

/* Settings Footer */
.settings-footer {
  background: white !important;
  border-top: 1px solid #e2e8f0;
  padding: 24px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 0px 0px 20px 20px;
  position: relative;
}

.footer-info {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 0.85rem;
}

.footer-info i {
  color: #3b82f6;
}

.dark .settings-footer {
  background: #334155 !important;
  border-top: 1px solid #475569;
  border-radius: 0px 0px 20px 20px;
  overflow: hidden;
}

.dark .footer-info {
  color: #94a3b8;
}

.dark .footer-info i {
  color: #60a5fa;
}

.btn-reset {
  background: #dc2626;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  order: 1;
}

.btn-reset:hover {
  background: #b91c1c;
  transform: translateY(-1px);
}

.btn-done {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  order: 3;
}

.btn-done:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

/* RTL Support for Settings Footer */
.settings-footer.rtl {
  justify-content: space-between;
}

.settings-footer.rtl .btn-reset {
  order: 1;
  margin-left: 0;
  margin-right: 0;
}

.settings-footer.rtl .btn-done {
  order: 3;
  margin-left: 0;
  margin-right: 0;
}

.settings-footer.rtl .footer-info {
  direction: ltr; /* Keep the info text readable */
  order: 2;
}

/* Responsive Design for Settings Modal */
@media (max-width: 1024px) {
  .settings-modal {
    max-width: 95%;
  }

  .working-hours-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }

  .appointment-types-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .settings-modal {
    max-width: 95%;
    margin: 10px;
    border-radius: 16px;
  }

  .settings-body {
    padding: 20px 16px;
  }

  .settings-section {
    padding: 20px 16px;
    margin-bottom: 24px;
  }

  .section-header-settings {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }

  .add-setting-btn-clean {
    width: 100%;
    justify-content: center;
  }

  .working-hours-grid {
    grid-template-columns: 1fr;
  }

  .day-times {
    grid-template-columns: 1fr;
  }

  .time-inputs-clean {
    flex-direction: column;
    gap: 12px;
  }

  .time-input-clean {
    width: 100%;
  }

  .type-details-clean {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .duration-controls {
    justify-content: center;
  }

  .color-options-clean {
    grid-template-columns: repeat(2, 1fr);
  }

  .settings-footer {
    flex-direction: column;
    gap: 16px;
    padding: 20px 16px;
    position: static;
  }

  .footer-info {
    position: static;
    transform: none;
    order: 2;
  }

  .btn-reset {
    width: 100%;
    justify-content: center;
    order: 1;
    margin-left: 0;
  }

  .btn-done {
    width: 100%;
    justify-content: center;
    order: 3;
    margin-left: 0;
  }

  .btn-clean {
    width: 100%;
  }

  /* RTL Mobile Layout */
  .settings-footer.rtl {
    flex-direction: column;
  }

  .settings-footer.rtl .btn-reset {
    order: 1;
    margin-right: 0;
    margin-left: 0;
  }

  .settings-footer.rtl .btn-done {
    order: 3;
    margin-right: 0;
    margin-left: 0;
  }
}

/* Apple-Style Settings Modal */
.apple-style {
  max-width: 700px;
  width: 90%;
  max-height: 85vh;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  background: white;
}

.apple-header {
  background: linear-gradient(135deg, #007aff 0%, #5856d6 100%);
  padding: 20px 24px;
  color: white;
  position: relative;
}

.apple-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
  pointer-events: none;
}

.apple-icon {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  font-size: 18px;
}

.header-text h3 {
  margin: 0 0 4px 0;
  font-size: 1.3rem;
  font-weight: 600;
}

.header-text p {
  margin: 0;
  opacity: 0.8;
  font-size: 0.85rem;
}

.apple-close {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.apple-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Settings Navigation */
.settings-nav {
  display: flex;
  background: white;
  border-bottom: 1px solid #e9ecef;
  padding: 0 4px;
}

.nav-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: #6c757d;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  margin: 4px 2px;
}

.nav-tab:hover {
  background: rgba(0, 122, 255, 0.1);
  color: #007aff;
}

.nav-tab.active {
  background: #007aff;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 122, 255, 0.3);
}

.nav-tab i {
  font-size: 16px;
}

.dark .settings-nav {
  background: #334155;
  border-bottom: 1px solid #475569;
}

.dark .nav-tab {
  color: #94a3b8;
}

.dark .nav-tab:hover {
  background: rgba(96, 165, 250, 0.2);
  color: #60a5fa;
}

.dark .nav-tab.active {
  background: #3b82f6;
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
}

/* Settings Body */
.apple-body {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
  background: white;
}

.settings-tab {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.setting-group {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e9ecef;
}

.dark .apple-body {
  background: #1e293b;
}

.dark .setting-group {
  background: #334155;
  border: 1px solid #475569;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.group-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f3f4;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.group-header h4 {
  margin: 0 0 4px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
}

.group-header p {
  margin: 0;
  font-size: 0.8rem;
  color: #86868b;
  line-height: 1.3;
}

.dark .group-header {
  border-bottom: 1px solid #475569;
}

.dark .group-header h4 {
  color: #f1f5f9;
}

.dark .group-header p {
  color: #94a3b8;
}

.setting-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f7;
}

.setting-row:last-child {
  border-bottom: none;
}

.setting-info label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #374151;
  margin: 0 0 2px 0;
  display: block;
}

.setting-desc {
  font-size: 0.75rem;
  color: #86868b;
}

.dark .setting-row {
  border-bottom: 1px solid #475569;
}

.dark .setting-info label {
  color: #f1f5f9;
}

.dark .setting-desc {
  color: #94a3b8;
}

/* Apple Controls */
.apple-select {
  padding: 6px 12px;
  border: 1px solid #d2d2d7;
  border-radius: 8px;
  background: white;
  font-size: 0.85rem;
  color: #1d1d1f;
  min-width: 120px;
  cursor: pointer;
}

.apple-select:focus {
  outline: none;
  border-color: #007aff;
  box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
}

.dark .apple-select {
  background: #475569;
  border: 1px solid #64748b;
  color: #f1f5f9;
}

.dark .apple-select:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
}

.toggle-group {
  display: flex;
  background: #f2f2f7;
  border-radius: 8px;
  padding: 2px;
}

.toggle-btn {
  padding: 6px 16px;
  border: none;
  background: transparent;
  color: #86868b;
  font-size: 0.8rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.toggle-btn.active {
  background: white;
  color: #1d1d1f;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.dark .toggle-group {
  background: #475569;
}

.dark .toggle-btn {
  color: #94a3b8;
}

.dark .toggle-btn.active {
  background: #334155;
  color: #f1f5f9;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.apple-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 26px;
}

.apple-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e9e9eb;
  transition: 0.3s;
  border-radius: 26px;
}

.switch-slider:before {
  position: absolute;
  content: "";
  height: 22px;
  width: 22px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.dark .switch-slider {
  background-color: #64748b;
}

.dark .switch-slider:before {
  background-color: #f1f5f9;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

input:checked + .switch-slider {
  background-color: #34c759;
}

input:checked + .switch-slider:before {
  transform: translateX(18px);
}

/* Working Hours Compact */
.working-hours-compact {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e9ecef;
}

.dark .working-hours-compact {
  background: #334155;
  border: 1px solid #475569;
}

.days-header {
  display: grid;
  grid-template-columns: 120px 1fr 80px;
  gap: 16px;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.day-row {
  display: grid;
  grid-template-columns: 120px 1fr 80px;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid #f5f5f7;
  align-items: center;
  transition: all 0.2s ease;
  background: white;
}

.day-row:last-child {
  border-bottom: none;
}

.day-row:hover {
  background: #f5f5f5;
}

.day-row.disabled {
  opacity: 0.5;
}

.dark .days-header {
  background: #334155;
  border-bottom: 1px solid #475569;
  color: #94a3b8;
}

.dark .day-row {
  border-bottom: 1px solid #475569;
  background: #334155;
}

.dark .day-row:hover {
  background: #475569;
}

.day-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.day-checkbox {
  position: relative;
  cursor: pointer;
}

.day-checkbox input {
  opacity: 0;
  position: absolute;
}

.checkmark {
  width: 18px;
  height: 18px;
  background: #f2f2f7;
  border: 1px solid #d2d2d7;
  border-radius: 4px;
  display: block;
  transition: all 0.2s ease;
}

.day-checkbox input:checked + .checkmark {
  background: #007aff;
  border-color: #007aff;
}

.dark .checkmark {
  background: #475569;
  border: 1px solid #64748b;
}

.dark .day-checkbox input:checked + .checkmark {
  background: #3b82f6;
  border-color: #3b82f6;
}

.day-checkbox input:checked + .checkmark::after {
  content: "✓";
  color: white;
  font-weight: bold;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.7rem;
}

.day-name .full-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #374151;
}

.dark .day-name .full-name {
  color: #f1f5f9;
}

.day-name .short-name {
  display: none;
}

.time-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.time-input.compact {
  padding: 4px 8px;
  border: 1px solid #d2d2d7;
  border-radius: 6px;
  font-size: 0.8rem;
  width: 70px;
  text-align: center;
  color: #374151;
  background: white;
}

/* Calendar Settings time inputs - override default white text */
.settings-modal .time-input,
.settings-modal .time-input.compact,
.working-hours-compact .time-input,
.break-time .time-input,
input[type="time"] {
  color: #374151 !important;
  background: white !important;
}

.time-separator {
  color: #86868b;
  font-weight: 500;
}

.dark .time-input.compact {
  background: #475569;
  border: 1px solid #64748b;
  color: #f1f5f9;
}

.dark .settings-modal .time-input,
.dark .settings-modal .time-input.compact,
.dark .working-hours-compact .time-input,
.dark .break-time .time-input,
.dark input[type="time"] {
  color: #f1f5f9 !important;
  background: #475569 !important;
  border: 1px solid #64748b !important;
}

.dark .time-separator {
  color: #94a3b8;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-align: center;
}

.status-badge.active {
  background: #d1f2eb;
  color: #00875a;
}

.status-badge.inactive {
  background: #f5f5f7;
  color: #86868b;
}

.dark .status-badge.active {
  background: #065f46;
  color: #6ee7b7;
}

.dark .status-badge.inactive {
  background: #475569;
  color: #94a3b8;
}

/* Breaks List */
.breaks-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.break-item {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid #e9ecef;
}

.break-header {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.break-name {
  flex: 1;
  padding: 6px 12px;
  border: 1px solid #d2d2d7;
  border-radius: 6px;
  font-size: 0.85rem;
  background: white;
  color: #374151;
}

.break-name.editing {
  color: #374151;
}

.break-time {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.break-time label {
  font-size: 0.8rem;
  color: #374151;
  font-weight: 500;
  white-space: nowrap;
}

.break-days {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.days-label {
  font-size: 0.75rem;
  color: #86868b;
  font-weight: 500;
}

.days-pills {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.day-pill {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: white;
  border: 1px solid #d2d2d7;
  border-radius: 16px;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.day-pill.active {
  background: #007aff;
  border-color: #007aff;
  color: white;
}

.day-pill input {
  display: none;
}

/* Types List */
.types-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.type-item {
  background: white;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e9ecef;
  border-left: 4px solid #007aff;
}

.dark .type-item {
  background: #334155;
  border: 1px solid #475569;
}

.type-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.type-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
}

.type-details {
  flex: 1;
}

.type-name {
  width: 100%;
  border: none;
  background: transparent;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  padding: 4px 0;
}

.type-name:focus {
  outline: none;
  background: #f8f9fa;
  border-radius: 6px;
  padding: 4px 8px;
}

.dark .type-name {
  color: #f1f5f9;
}

.dark .type-name:focus {
  background: #475569;
}

.type-meta {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.duration,
.color-name {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 500;
}

.duration {
  background: #e3f2fd;
  color: #1976d2;
}

.color-name {
  background: #f5f5f7;
  color: #86868b;
  text-transform: capitalize;
}

.dark .duration {
  background: #1e40af;
  color: #93c5fd;
}

.dark .color-name {
  background: #475569;
  color: #94a3b8;
}

.type-controls {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.duration-control,
.color-control {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.duration-control label,
.color-control label {
  font-size: 0.75rem;
  color: #374151;
  font-weight: 500;
}

.form-group label {
  font-size: 0.8rem;
  color: #374151;
  font-weight: 500;
  margin-bottom: 4px;
  display: block;
}

.dark .duration-control label,
.dark .color-control label {
  color: #f1f5f9;
}

.dark .form-group label {
  color: #f1f5f9;
}

.duration-input {
  display: flex;
  align-items: center;
  gap: 8px;
}

.duration-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #d2d2d7;
  background: white;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #86868b;
  font-size: 12px;
}

.duration-btn:hover:not(:disabled) {
  border-color: #007aff;
  color: #007aff;
}

.duration-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.duration-display {
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  min-width: 50px;
  text-align: center;
}

.dark .duration-btn {
  background: #475569;
  border: 1px solid #64748b;
  color: #94a3b8;
}

.dark .duration-btn:hover:not(:disabled) {
  border-color: #60a5fa;
  color: #60a5fa;
}

/* Dark mode for form inputs */
.dark .form-input {
  background: #475569;
  border: 2px solid #64748b;
  color: #f1f5f9;
}

.dark .form-input::placeholder {
  color: #94a3b8;
}

.dark .form-input:focus {
  border-color: #60a5fa;
  background: #475569;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
}

.dark .form-textarea {
  background: #475569;
  border: 2px solid #64748b;
  color: #f1f5f9;
}

.dark .form-textarea::placeholder {
  color: #94a3b8;
}

.dark .form-select {
  background: #475569;
  border: 2px solid #64748b;
  color: #f1f5f9;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2394a3b8' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e") !important;
  background-position: right 16px center !important;
  background-repeat: no-repeat !important;
  background-size: 16px 12px !important;
}

.dark .form-select:focus {
  border-color: #60a5fa;
  background: #475569;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
}

.dark .form-select option {
  background: #475569;
  color: #f1f5f9;
}

.dark .input-icon-clean {
  color: #94a3b8;
}

.dark .form-input:focus ~ .input-icon-clean,
.dark .form-input:focus + .input-icon-clean {
  color: #60a5fa;
}

.dark .participant-input {
  background: #475569;
  border: 1px solid #64748b;
  color: #f1f5f9;
}

.dark .participant-input::placeholder {
  color: #94a3b8;
}

.dark .participant-input:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.1);
}

.dark .tag-input {
  background: #475569;
  border: 1px solid #64748b;
  color: #f1f5f9;
}

.dark .product-search-input {
  background: #475569;
  border: 1px solid #64748b;
  color: #f1f5f9;
}

/* Dark mode for Calendar Settings additional elements */
.dark .day-pill {
  background: #475569;
  border: 1px solid #64748b;
  color: #94a3b8;
}

.dark .break-name {
  background: #475569;
  border: 1px solid #64748b;
  color: #f1f5f9;
}

.dark .break-name.editing {
  color: #f1f5f9;
}

.dark .break-time label {
  color: #f1f5f9;
}

.dark .days-label {
  color: #94a3b8;
}

.dark .break-item {
  background: #475569;
  border: 1px solid #64748b;
}

.dark .checkbox-label {
  color: #f1f5f9;
}

.dark .users-grid label {
  color: #f1f5f9;
}

.dark .product-link-item {
  background: #475569;
  border: 1px solid #64748b;
  color: #f1f5f9;
}

/* Dark mode for type edit form specific elements */
.dark .type-edit-form input[type="number"] {
  background: #475569;
  border: 1px solid #64748b;
  color: #f1f5f9;
}

.dark .type-edit-form input[type="number"]:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.1);
}

.dark .duration-label {
  color: #f1f5f9;
}

.dark .minutes-text {
  color: #94a3b8;
}

.dark .color-grid .color-dot {
  border: 2px solid #64748b;
}

.dark .color-grid .color-dot.active {
  border: 2px solid #60a5fa;
  box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.3);
}

.dark .user-checkbox {
  color: #f1f5f9;
}

.dark .loading-users {
  color: #94a3b8;
}

/* Dark mode for type-edit-form container */
.dark .type-edit-form {
  background: #334155;
  border: 1px solid #475569;
}

.dark .form-group label {
  color: #f1f5f9 !important;
}

.dark .users-grid {
  background: #475569;
  border: 1px solid #64748b;
}

.dark .user-checkbox:hover {
  background: #64748b;
}

.dark .loading-products {
  color: #94a3b8;
}

.dark .form-actions {
  border-top: 1px solid #64748b;
}

/* Dark mode for product search components */
.dark .product-search-container {
  color: #f1f5f9;
}

.dark .clear-product-btn {
  background: #64748b;
  color: #94a3b8;
}

.dark .clear-product-btn:hover {
  background: #475569;
  color: #f1f5f9;
}

.dark .product-dropdown {
  background: #475569;
  border: 1px solid #64748b;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
}

.dark .product-option {
  border-bottom: 1px solid #64748b;
}

.dark .product-option:hover {
  background: #64748b;
}

.dark .product-option.selected {
  background: rgba(96, 165, 250, 0.1);
  color: #60a5fa;
}

.dark .product-name {
  color: #f1f5f9;
}

.dark .product-option.selected .product-name {
  color: #60a5fa;
}

.dark .product-price {
  color: #94a3b8;
}

.dark .product-option.selected .product-price {
  color: #93c5fd;
}

.dark .selected-icon {
  color: #60a5fa;
}

.dark .no-results {
  color: #94a3b8;
}

.dark .no-products,
.dark .loading-products {
  background: #475569;
  border: 1px solid #64748b;
  color: #94a3b8;
}

/* Dark mode for Add Event Modal */
.dark .add-event-modal {
  background: #1e293b;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  border: 1px solid #334155;
}

.dark .add-event-modal.no-frame {
  background: #1e293b;
  border: 1px solid #334155;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
}

.dark .modal-body-clean {
  background: #1e293b;
}

.dark .modal-body-modern {
  background: #1e293b;
}

.dark .field-label {
  color: #f1f5f9;
}

.dark .modern-input {
  background: #334155;
  border: 2px solid #475569;
  color: #f1f5f9;
}

.dark .modern-input:focus {
  border-color: #60a5fa;
  background: #334155;
  box-shadow: 0 0 0 4px rgba(96, 165, 250, 0.1);
}

.dark .modern-input::placeholder {
  color: #94a3b8;
}

.dark .modern-textarea {
  background: #334155;
  border: 2px solid #475569;
  color: #f1f5f9;
}

.dark .modern-select {
  background: #334155;
  border: 2px solid #475569;
  color: #f1f5f9;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2394a3b8' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e") !important;
  background-position: left 16px center !important;
  background-repeat: no-repeat !important;
  background-size: 16px 12px !important;
}

.dark .input-icon {
  color: #94a3b8;
}

.dark .modern-input:focus ~ .input-icon {
  color: #60a5fa;
}

.dark .participant-row {
  background: #334155;
  border: 1px solid #475569;
}

.dark .participant-input {
  background: #475569;
  border: 1px solid #64748b;
  color: #f1f5f9;
}

.dark .remove-participant-btn {
  background: #7f1d1d;
  color: #fca5a5;
}

.dark .remove-participant-btn:hover {
  background: #991b1b;
}

/* Dark mode for modal footer and buttons */
.dark .modal-footer-clean {
  background: #1e293b;
  border-top: 1px solid #334155;
}

.dark .close-btn-clean {
  background: rgba(148, 163, 184, 0.2);
  color: #94a3b8;
}

.dark .close-btn-clean:hover {
  background: rgba(148, 163, 184, 0.3);
  color: #f1f5f9;
}

.dark .btn-clean.btn-secondary {
  background: #475569;
  color: #f1f5f9;
  border: 1px solid #64748b;
}

.dark .btn-clean.btn-secondary:hover {
  background: #64748b;
  border-color: #94a3b8;
}

.dark .btn-clean.btn-primary {
  background: #3b82f6;
  border: 1px solid #3b82f6;
}

.dark .btn-clean.btn-primary:hover {
  background: #2563eb;
  border-color: #2563eb;
}

.dark .btn-clean.btn-danger {
  background: #dc2626;
  border: 1px solid #dc2626;
}

.dark .btn-clean.btn-danger:hover {
  background: #b91c1c;
  border-color: #b91c1c;
}

/* Dark mode for participant cards */
.dark .participant-card {
  background: #334155;
  border: 1px solid #475569;
}

.dark .participant-card:hover {
  border-color: #64748b;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.dark .remove-btn {
  background: #7f1d1d;
  color: #fca5a5;
}

.dark .remove-btn:hover {
  background: #991b1b;
  color: #fecaca;
}

/* Dark mode overrides for !important rules */
.dark .tag-input {
  background-color: #475569 !important;
  border: 1px solid #64748b !important;
  color: #f1f5f9 !important;
}

.dark .type-edit-form label {
  color: #f1f5f9 !important;
}

.dark .checkbox-label {
  color: #f1f5f9 !important;
}

.dark .type-name.editing {
  background: #475569;
  border: 1px solid #64748b;
  color: #f1f5f9;
}

/* Dark mode for buttons and additional controls */
.dark .edit-btn {
  background: #475569;
  color: #60a5fa;
}

.dark .edit-btn:hover {
  background: #60a5fa;
  color: #1e293b;
}

.dark .remove-btn {
  background: #7f1d1d;
  color: #fca5a5;
}

.dark .remove-btn:hover:not(:disabled) {
  background: #dc2626;
  color: white;
}

.dark .duration-input input {
  background: #475569;
  border: 1px solid #64748b;
  color: #f1f5f9;
}

.dark .duration-btn {
  background: #475569;
  border: 1px solid #64748b;
  color: #94a3b8;
}

.dark .duration-btn:hover {
  background: #64748b;
  border-color: #60a5fa;
  color: #60a5fa;
}



  .duration-number-input{
    color: #000000 !important;
  }

  /* Completed event styles */
  .completed-event {
    background-color: rgba(34, 197, 94, 0.1) !important;
    border-color: rgba(34, 197, 94, 0.3) !important;
  }

  .dark .completed-event {
    background-color: rgba(34, 197, 94, 0.15) !important;
    border-color: rgba(34, 197, 94, 0.4) !important;
  }

/* Dark mode for duration number input */
.dark .duration-number-input {
  background: #475569 !important;
  border: 1px solid #64748b !important;
  color: #f1f5f9 !important;
}

.dark .duration-number-input:focus {
  border-color: #60a5fa !important;
  box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.1) !important;
}

/* Fix minutes text in dark mode */
.dark .minutes-text {
  color: #f1f5f9 !important;
}

.dark .duration-display {
  color: #f1f5f9;
}

.color-picker.compact {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.color-dot {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.color-dot:hover {
  transform: scale(1.1);
}

.color-dot.active {
  border-color: #1d1d1f;
  transform: scale(1.15);
}

/* Compact Buttons */
.add-btn.compact,
.remove-btn.compact {
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
}

.add-btn.compact {
  background: #007aff;
  color: white;
}

.add-btn.compact:hover {
  background: #0056cc;
}

.edit-btn.compact {
  background: #e0f2fe;
  color: #0369a1;
  padding: 6px;
  width: 32px;
  height: 32px;
  justify-content: center;
}

.edit-btn.compact:hover {
  background: #0369a1;
  color: white;
}

.remove-btn.compact {
  background: #fef2f2;
  color: #dc2626;
  padding: 6px;
  width: 32px;
  height: 32px;
  justify-content: center;
}

.remove-btn.compact:hover:not(:disabled) {
  background: #dc2626;
  color: white;
}

/* Footer */
.apple-footer {
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-info {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #86868b;
  font-size: 0.75rem;
}

.footer-info i {
  color: #007aff;
}

.dark .apple-footer {
  background: #334155;
  border-top: 1px solid #475569;
}

.dark .footer-info {
  color: #94a3b8;
}

.dark .footer-info i {
  color: #60a5fa;
}

.footer-actions {
  display: flex;
  gap: 8px;
}

.apple-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-secondary.apple-btn {
  background: #f2f2f7;
  color: #86868b;
}

.btn-secondary.apple-btn:hover {
  background: #e5e5ea;
  color: #1d1d1f;
}

.btn-primary.apple-btn {
  background: #007aff;
  color: white;
}

.btn-primary.apple-btn:hover {
  background: #0056cc;
}

/* Responsive */
@media (max-width: 768px) {
  .apple-style {
    width: 95%;
    max-height: 90vh;
  }

  .apple-body {
    padding: 16px;
  }

  .setting-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .days-header,
  .day-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .day-name .full-name {
    display: none;
  }

  .day-name .short-name {
    display: block;
  }

  .type-controls {
    grid-template-columns: 1fr;
  }

  .apple-footer {
    flex-direction: column;
    gap: 12px;
  }

  .footer-actions {
    width: 100%;
    justify-content: center;
  }
}

/* Appointment Types Duration Display */
.category-duration {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  min-width: 50px;
}

.duration-text {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-align: center;
}

.appointment-count {
  background: #8b5cf6;
  color: white;
  padding: 2px 6px;
  border-radius: 50%;
  font-size: 0.65rem;
  font-weight: 700;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
}

/* Loading and Empty States */
.loading-state,
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
}

.loading-state i {
  animation: spin 1s linear infinite;
}

/* Product Search and Display */
.product-search-container {
  position: relative;
  width: 100%;
}

.product-search-input {
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.875rem;
  color: #374151;
  background: white;
  transition: all 0.2s ease;
  font-family: inherit;
}

.product-search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.clear-product-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: #f3f4f6;
  border: none;
  border-radius: 6px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s ease;
}

.clear-product-btn:hover {
  background: #e5e7eb;
  color: #374151;
}

.product-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
}

.product-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f3f4f6;
}

.product-option:last-child {
  border-bottom: none;
}

.product-option:hover {
  background: #f8fafc;
}

.product-option.selected {
  background: rgba(59, 130, 246, 0.05);
  color: #3b82f6;
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.product-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.product-option.selected .product-name {
  color: #3b82f6;
}

.product-price {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 600;
}

.product-option.selected .product-price {
  color: #2563eb;
}

.selected-icon {
  color: #3b82f6;
  font-size: 1rem;
}

.no-results {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  color: #6b7280;
  font-size: 0.875rem;
  text-align: center;
  justify-content: center;
}

.no-products,
.loading-products {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  color: #6b7280;
  font-size: 0.875rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.no-products i,
.loading-products i {
  font-size: 1rem;
}

.loading-products i {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Break Times Styles */
.break-item {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  transition: all 0.2s ease;
}

.break-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.break-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.break-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  flex: 1;
}

.break-name.editing {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.875rem;
}

.break-actions {
  display: flex;
  gap: 8px;
}

.break-edit-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.break-time {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.break-time label {
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 500;
}

.time-input.compact {
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  min-width: 100px;
  color: #374151;
  background: white;
}

.time-separator {
  color: #6b7280;
  font-weight: 600;
}

.break-days {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.days-label {
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 500;
}

.days-pills {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.day-pill {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
}

.day-pill.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.day-pill input {
  display: none;
}

.break-summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.time-display,
.days-display {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: #6b7280;
}

.time-display i,
.days-display i {
  font-size: 1rem;
  color: #9ca3af;
}

/* Availability Actions */
.availability-actions {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: center;
}

/* Responsive Design for Top Level Statistics */
@media (max-width: 768px) {
  .new-calendar-app {
    flex-direction: column;
    padding: 16px;
    gap: 0;
  }

  .calendar-stats-top-level {
    padding: 8px;
    margin-bottom: 8px;
    border-radius: 8px;
  }

  .calendar-layout {
    flex-direction: column;
    gap: 16px;
  }

  .sidebar-card {
    width: 100%;
    height: auto;
    max-height: 300px;
    flex-direction: row;
    gap: 16px;
    overflow-x: auto;
    padding: 16px;
  }

  .sidebar-card > * {
    flex-shrink: 0;
    min-width: 200px;
  }

  .main-calendar-card {
    border-radius: 16px;
    padding: 16px;
  }

  /* Mobile adjustments for type edit form */
  .form-row {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  /* Special mobile layout for date/time row (3 fields) */
  .form-row .form-group:first-child {
    width: 100%;
  }

  /* Create a flex container for time fields */
  .form-row {
    position: relative;
  }

  .form-row .form-group:nth-child(2) {
    position: absolute;
    top: calc(100% + 12px);
    left: 0;
    width: calc(50% - 6px);
  }

  .form-row .form-group:nth-child(3) {
    position: absolute;
    top: calc(100% + 12px);
    right: 0;
    width: calc(50% - 6px);
  }

  /* Add space for the absolute positioned time fields */
  .form-row {
    margin-bottom: 80px;
  }

  /* Mobile layout for participants section */
  .participants-header-clean {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .participants-actions {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
    width: 100%;
  }

  .contact-search-buttons {
    display: flex;
    gap: 8px;
    width: 100%;
  }

  .search-leads-btn,
  .search-clients-btn {
    flex: 1;
    min-width: auto;
    font-size: 0.8rem;
    padding: 10px 8px;
  }

  .add-btn {
    width: 100%;
    justify-content: center;
    padding: 12px 16px;
    font-size: 0.9rem;
  }

  .users-grid {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    justify-content: center;
  }

  /* Mobile break time layout */
  .break-time {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .days-pills {
    gap: 4px;
  }

  .day-pill {
    font-size: 0.7rem;
    padding: 3px 6px;
  }
}

/* Toast Container */
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
}

.toast {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  animation: slideInRight 0.3s ease-out;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.toast-success {
  background: #10b981;
  color: white;
}

.toast-error {
  background: #ef4444;
  color: white;
}

.toast-warning {
  background: #f59e0b;
  color: white;
}

.toast-info {
  background: #3b82f6;
  color: white;
}

.toast-icon {
  font-size: 20px;
  min-width: 20px;
}

.toast-content {
  flex: 1;
  font-weight: 500;
}

.toast-close {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.toast-close:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.1);
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Popup Overlay */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  backdrop-filter: blur(4px);
}

.popup-content {
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  animation: popupIn 0.3s ease-out;
}

@keyframes popupIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.popup-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 24px 0 24px;
}

.popup-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.popup-success .popup-icon {
  background: #dcfce7;
  color: #16a34a;
}

.popup-danger .popup-icon {
  background: #fee2e2;
  color: #dc2626;
}

.popup-warning .popup-icon {
  background: #fef3c7;
  color: #d97706;
}

.popup-info .popup-icon {
  background: #dbeafe;
  color: #2563eb;
}

.popup-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
}

.popup-body {
  padding: 16px 24px;
}

.popup-body p {
  margin: 0;
  color: #6b7280;
  line-height: 1.5;
}

.popup-footer {
  display: flex;
  gap: 12px;
  padding: 0 24px 24px 24px;
  justify-content: space-between;
}

.btn-cancel {
  background: #f3f4f6;
  color: #374151;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.btn-cancel:hover {
  background: #e5e7eb;
}

.btn-confirm {
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  color: white;
  transition: background-color 0.2s ease;
}

.btn-success {
  background: #16a34a;
}

.btn-success:hover {
  background: #15803d;
}

.btn-danger {
  background: #dc2626;
}

.btn-danger:hover {
  background: #b91c1c;
}

.btn-warning {
  background: #d97706;
}

.btn-warning:hover {
  background: #b45309;
}

.btn-info {
  background: #2563eb;
}

.btn-info:hover {
  background: #1d4ed8;
}

/* Dark mode for popup */
.dark .popup-overlay {
  background: rgba(0, 0, 0, 0.7);
}

.dark .popup-content {
  background: #1e293b;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
}

.dark .popup-header h3 {
  color: #f1f5f9;
}

.dark .popup-body p {
  color: #94a3b8;
}

.dark .btn-cancel {
  background: #475569;
  color: #f1f5f9;
}

.dark .btn-cancel:hover {
  background: #64748b;
}

.dark .popup-success .popup-icon {
  background: #065f46;
  color: #6ee7b7;
}

.dark .popup-danger .popup-icon {
  background: #7f1d1d;
  color: #fca5a5;
}

.dark .popup-warning .popup-icon {
  background: #78350f;
  color: #fbbf24;
}

.dark .popup-info .popup-icon {
  background: #1e3a8a;
  color: #93c5fd;
}

/* Mobile Responsiveness for Toast and Popup */
@media (max-width: 768px) {
  .toast-container {
    top: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
  }

  .popup-content {
    width: 95%;
    margin: 10px;
  }

  .popup-footer {
    flex-direction: column;
  }

  .btn-cancel,
  .btn-confirm {
    width: 100%;
  }
}

/* General rounded corners for all inputs in settings modal */
.calendar-settings input,
.calendar-settings textarea,
.calendar-settings select {
  border-radius: 12px !important;
}

/* Additional specific styling for better coverage */
input[type="text"],
input[type="number"],
input[type="time"],
textarea {
  border-radius: 12px !important;
}

/* Mobile Responsive Styles - Hide on Desktop */
.view-toggle-slider {
  display: none;
}

/* Hide all mobile toggle elements on desktop */
.mobile-toggle-inside {
  display: none;
}

.mobile-toggle-calendar {
  display: none;
}

.mobile-only {
  display: none;
}

/* View Toggle Slider Styles - Enhanced for Smooth Animation */
.view-toggle-slider {
  position: relative;
  width: 300px;
  margin: 20px auto;
  direction: ltr;
  /* Performance optimizations */
  will-change: transform;
  contain: layout style paint;
}

.slider-track {
  position: relative;
  height: 50px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 25px;
  border: 2px solid #e2e8f0;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1);
  /* Smooth background transition */
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  /* Performance optimizations */
  backface-visibility: hidden;
  transform: translateZ(0);
}

.slider-track:hover {
  background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(102, 126, 234, 0.2);
  border-color: rgba(102, 126, 234, 0.3);
}

.slider-input {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
}

.slider-thumb {
  position: absolute;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  top: 5px;
  left: 5px;
  /* Enhanced smooth transitions */
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 3;
  pointer-events: auto;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  /* Performance optimizations */
  will-change: transform, box-shadow;
  backface-visibility: hidden;
  transform: translateZ(0);
}

/* Enhanced hover and active states */
.slider-thumb:hover {
  transform: translateZ(0) scale(1.05);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4), 0 4px 8px rgba(0, 0, 0, 0.15);
  background: linear-gradient(135deg, #7c8aed 0%, #8b5aa5 100%);
}

.slider-thumb:active {
  transform: translateZ(0) scale(0.98);
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Smooth dragging state */
.slider-thumb.dragging {
  transition: box-shadow 0.2s ease, transform 0.1s ease;
  transform: translateZ(0) scale(1.02);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.5), 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Dark mode enhancements */
.dark .slider-track {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-color: #475569;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.3);
}

.dark .slider-track:hover {
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(102, 126, 234, 0.2);
  border-color: rgba(102, 126, 234, 0.4);
}

.dark .slider-thumb {
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3);
}

.dark .slider-thumb:hover {
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.5), 0 4px 8px rgba(0, 0, 0, 0.4);
}

.dark .slider-thumb.dragging {
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.6), 0 4px 12px rgba(0, 0, 0, 0.5);
}

.dark .slider-labels {
  color: #94a3b8;
}

.dark .label-left::before,
.dark .label-right::before {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2));
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  padding: 0 10px;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.label-left,
.label-right {
  /* Enhanced smooth transitions for labels */
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  padding: 4px 8px;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
}

.label-left::before,
.label-right::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
  border-radius: 8px;
  opacity: 0;
  transform: scale(0.8);
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  z-index: -1;
}

/* Active label styling with enhanced animations */
.view-toggle-slider:has(.slider-thumb:not(.moved)) .label-left {
  color: #667eea;
  font-weight: 600;
  transform: translateY(-1px);
}

.view-toggle-slider:has(.slider-thumb:not(.moved)) .label-left::before {
  opacity: 1;
  transform: scale(1);
}

.view-toggle-slider:has(.slider-thumb.moved) .label-right {
  color: #667eea;
  font-weight: 600;
  transform: translateY(-1px);
}

.view-toggle-slider:has(.slider-thumb.moved) .label-right::before {
  opacity: 1;
  transform: scale(1);
}



@media (max-width: 768px) {
  /* Prevent body overflow issues that might affect modals */
  body:has(.event-detail-modal),
  body:has(.popup-overlay),
  body:has(.mobile-sidebar-modal) {
    overflow: hidden !important;
  }

  /* Hide slider completely when any modal is open */
  body:has(.mobile-sidebar-modal) .mobile-toggle-fixed,
  body:has(.event-detail-modal) .mobile-toggle-fixed,
  body:has(.popup-overlay) .mobile-toggle-fixed {
    display: none !important;
    visibility: hidden !important;
  }

  /* Hide swipe indicator and sidebar on calendar page */
  .swipe-indicator {
    display: none !important;
    visibility: hidden !important;
  }
  
  /* Force hide sidebar even if state changes */
  .sidebar-area {
    display: none !important;
    visibility: hidden !important;
    transform: translateX(-100%) !important;
    pointer-events: none !important;
  }

  /* Fix the main container overflow issue on mobile */
  .new-calendar-app {
    height: auto !important;
    min-height: 100vh !important;
    max-height: none !important;
    overflow: visible !important;
    overflow-y: auto !important;
    padding: 0 !important;
    width: 100vw !important;
    box-sizing: border-box !important;
  }

  /* Ensure calendar layout allows scrolling */
  .calendar-layout {
    height: auto !important;
    min-height: calc(100vh - 60px) !important; /* מקום לslider - קטנתי מ-80px ל-60px */
    max-height: none !important;
    overflow: visible !important;
    overflow-y: auto !important;
  }

  /* Mobile slider background - same as calendar layout */
  .mobile-toggle-fixed {
    background: #ffffff;
    padding: 10px 24px; /* קטנתי מ-20px ל-10px */
    border: none !important;
    border-bottom: none !important;
    box-shadow: none !important;
    z-index: 500; /* נמוך יותר מהpopup */
    position: relative;
  }

  .dark .mobile-toggle-fixed {
    background: #0c1427;
  }
  .main-content:where([dir="ltr"], [dir="ltr"] *) {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }

  .main-content:where([dir="rtl"], [dir="rtl"] *) {
    padding-right: 0px !important;
    padding-left: 0px !important;
  }

  /* Show slider only on mobile */
  .view-toggle-slider {
    display: block;
  }

  .dark .mobile-toggle-container {
    background: transparent;
    border-color: transparent;
  }

  .calendar-layout {
    position: relative;
    overflow: hidden; /* חשוב לאנימציות */
    min-height: 100vh;
    height: auto;
    background: #f8fafc;
    padding-top: 0;
    padding-bottom: 40px; /* קטנתי מ-60px ל-40px */
  }

  /* Ensure new-calendar-app container doesn't restrict modals */
  .new-calendar-app {
    position: relative;
    overflow: visible; /* אפשר לmodals לצאת */
  }

  .dark .calendar-layout {
    background: #0f172a;
  }

  /* Apply same background to sidebar card on mobile */
  .sidebar-card.mobile-active {
    background: #f8fafc !important;
  }

  .dark .sidebar-card.mobile-active {
    background: #0f172a !important;
  }

  /* Remove height restrictions from sidebar on mobile */
  .sidebar-card {
    height: auto !important;
    min-height: unset !important;
    overflow: visible !important;
  }

  .sidebar-card,
  .main-calendar-card {
    display: none;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    transition: none; /* הסרנו את הטרנזישן כדי שהתנועה תהיה מיידית */
  }



  .sidebar-card.mobile-active,
  .main-calendar-card.mobile-active {
    display: block;
  }

  /* Ensure sidebar takes full width and height on mobile when active */
  .sidebar-card.mobile-active {
    max-width: none;
    width: 100%;
    margin: 0;
    border-radius: 0;
    height: auto;
    min-height: 100vh;
    max-height: none;
    overflow: visible;
    background: transparent !important;
    backdrop-filter: none !important;
    padding: 16px 24px 90px 24px !important; /* קטנתי מ-24px ל-16px בחלק העליון */
    display: flex !important;
    flex-direction: column !important;
    gap: 20px !important; /* קטנתי גם את הgap מ-24px ל-20px */
    border: none !important;
    box-shadow: none !important;
  }

  .dark .sidebar-card.mobile-active {
    background: transparent !important;
  }

  /* Ensure main calendar takes full width and height on mobile when active */
  .main-calendar-card.mobile-active {
    max-width: none;
    width: 100%;
    margin: 0;
    border-radius: 0;
    height: auto;
    min-height: auto;
    overflow: visible;
    background: transparent !important;
    padding: 0;
    overflow-y: auto;
  }

  .dark .main-calendar-card.mobile-active {
    background: transparent !important;
  }

  /* Keep sidebar internal elements with normal background on mobile like desktop */
  .calendar-layout .sidebar-card.mobile-active .categories {
    margin-bottom: 90px !important; /* רק שמירת המרווח התחתון */
  }

  /* Mobile toggle button inside sidebar */
  .mobile-toggle-inside {
    display: none;
  }



  .sidebar-card.mobile-active .mobile-toggle-inside {
    display: block;
  }

  /* Mobile toggle slider for calendar view */
  .mobile-toggle-calendar {
    display: none;
    padding: 8px 16px; /* קטנתי מ-16px ל-8px אנכית */
  }

  .main-calendar-card.mobile-active .mobile-toggle-calendar {
    display: block;
  }

  /* Ensure slider works in both locations */
  .mobile-toggle-inside .view-toggle-slider,
  .mobile-toggle-calendar .view-toggle-slider {
    width: 100%;
    max-width: 350px; /* רוחב מקסימלי גדול יותר */
    margin: 0 auto;
  }

  /* Ensure the sidebar content area also has background */
  .sidebar-card.mobile-active .card-body {
    background: transparent;
    min-height: calc(100vh - 120px);
    padding-bottom: 20px;
  }

  /* Ensure the calendar content area also has background */
  .main-calendar-card.mobile-active .card-body {
    background: #f8fafc;
    min-height: calc(100vh - 120px);
    padding-bottom: 20px;
  }

  /* Show mobile-only elements only on mobile */
  .mobile-only {
    display: block;
  }

  /* Show only first letter of day names on mobile - applies to both sidebar and week header */
  .day-name,
  .day-header .day-name,
  .day-header.today .day-name,
  .day-header.selected .day-name,
  .week-header .day-header .day-name {
    font-size: 0 !important; /* הסתר את הטקסט הרגיל */
  }

  .day-name::before,
  .day-header .day-name::before,
  .day-header.today .day-name::before,
  .day-header.selected .day-name::before,
  .week-header .day-header .day-name::before {
    content: attr(data-short) !important; /* הציג את האות הראשונה */
    font-size: 0.8rem !important; /* גודל הפונט לאות */
    font-weight: 600 !important;
    color: #6b7280 !important;
    display: inline-block !important;
  }

  /* Specific styling for week header day names with higher specificity */
  .week-header .day-header .day-name::before,
  .week-header .day-header.today .day-name::before,
  .week-header .day-header.selected .day-name::before {
    font-size: 0.75rem !important; /* גודל פונט קצת יותר קטן ל-week header */
    font-weight: 700 !important; /* יותר bold */
  }

  /* Dark mode for day name letters - with higher specificity */
  .dark .day-name::before,
  .dark .day-header .day-name::before,
  .dark .day-header.today .day-name::before,
  .dark .day-header.selected .day-name::before,
  .dark .week-header .day-header .day-name::before {
    color: #9ca3af !important;
  }

  /* Mobile Sidebar Modal Styles - Responsive */
  .mobile-sidebar-modal {
    position: fixed;
    top: 0; /* מכסה את כל המסך כולל הslider */
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1500; /* גבוה יותר מהslider והתפריט */
    display: flex;
    align-items: flex-start; /* יישור לחלק העליון */
    justify-content: center;
    padding: 10px; /* קטן יותר במובייל */
    padding-top: 80px; /* מרווח מלמעלה לתפריט העליון */
  }

  .mobile-modal-content {
    background: white;
    border-radius: 16px;
    max-height: calc(100vh - 100px); /* גובה מותאם - יותר מקום כי אנחנו מתחילים מלמעלה */
    width: 95%; /* רוחב מותאם למסך */
    max-width: 420px; /* מקסימום רוחב */
    min-height: 300px; /* גובה מינימלי */
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    margin: 5px; /* מרווחים קטנים */
  }

  .dark .mobile-modal-content {
    background: #1f2937;
  }

  .mobile-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 16px 16px 0 0;
  }

  .dark .mobile-modal-header {
    background: #374151;
    border-bottom-color: #4b5563;
  }

  .mobile-modal-header .header-content {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .mobile-modal-header .header-icon {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 18px;
  }

  .mobile-modal-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
  }

  .dark .mobile-modal-header h3 {
    color: #f9fafb;
  }

  .mobile-modal-header .close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: #e5e7eb;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .dark .mobile-modal-header .close-btn {
    background: #4b5563;
    color: #d1d5db;
  }

  .mobile-modal-header .close-btn:hover {
    background: #d1d5db;
    color: #374151;
  }

  .dark .mobile-modal-header .close-btn:hover {
    background: #6b7280;
    color: #f9fafb;
  }

  .mobile-settings-nav {
    display: flex;
    padding: 0 20px;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
  }

  .dark .mobile-settings-nav {
    background: #374151;
    border-bottom-color: #4b5563;
  }

  .mobile-nav-tab {
    flex: 1;
    padding: 12px 8px;
    border: none;
    background: transparent;
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    transition: all 0.2s ease;
    border-bottom: 2px solid transparent;
  }

  .mobile-nav-tab.active {
    color: #667eea;
    border-bottom-color: #667eea;
  }

  .dark .mobile-nav-tab {
    color: #9ca3af;
  }

  .dark .mobile-nav-tab.active {
    color: #93c5fd;
    border-bottom-color: #93c5fd;
  }

  .mobile-nav-tab i {
    font-size: 16px;
  }

  .mobile-modal-body {
    flex: 1;
    overflow-y: auto; /* גלילה אנכית */
    padding: 15px; /* פדינג קטן יותר */
    max-height: calc(100vh - 220px); /* גובה מוגבל עם מקום לכותרת ותפריט עליון */
  }

  /* Mobile Modal Fixes - Ensure all modals cover the slider */
  /* Event Detail Modal - Mobile Override */
  .event-detail-modal {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    z-index: 10002 !important; /* גבוה יותר מהslider והכל */
    background: rgba(0, 0, 0, 0.6) !important;
    backdrop-filter: blur(4px);
  }

  /* Popup Overlay - Mobile Override */
  .popup-overlay {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    z-index: 10003 !important; /* הכי גבוה */
    background: rgba(0, 0, 0, 0.6) !important;
    backdrop-filter: blur(4px);
  }

  /* Modal Content Mobile Adjustments */
  .event-detail-modal .modal-content {
    margin: 10px;
    max-height: calc(100vh - 20px);
    width: calc(100vw - 20px);
    max-width: calc(100vw - 20px);
    overflow-y: auto;
  }

  /* Popup Content Mobile Adjustments */
  .popup-content {
    margin: 10px;
    max-height: calc(100vh - 20px);
    width: calc(100vw - 20px);
    max-width: calc(100vw - 20px);
  }

  /* Add Event Modal Content - Mobile Specific */
  .modal-content.add-event-modal {
    max-height: calc(100vh - 20px);
    overflow-y: auto;
  }

  /* Contact/Leads/Clients Search Modals */
  .modal-content.contact-search-modal {
    max-height: calc(100vh - 20px);
    overflow-y: auto;
  }

  /* Settings Modal Desktop - Hide on Mobile */
  .event-detail-modal.desktop-modal {
    display: none !important;
  }

  /* Mobile Add Event Modal - Responsive like sidebar modal */
  .event-detail-modal {
    display: flex !important;
    align-items: flex-start !important; /* יישור לחלק העליון */
    justify-content: center !important;
    padding: 10px !important;
    padding-top: 30px !important; /* מרווח מלמעלה לתפריט העליון - קטנתי מ-80px ל-60px */
  }

  .event-detail-modal .modal-content.add-event-modal {
    background: white !important;
    border-radius: 16px !important;
    max-height: calc(100vh - 80px) !important; /* גובה מותאם - יותר מקום כי אנחנו מתחילים מלמעלה */
    width: 95% !important; /* רוחב מותאם למסך */
    max-width: 420px !important; /* מקסימום רוחב */
    min-height: 400px !important; /* גובה מינימלי גדול יותר */
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3) !important;
    margin: 5px !important; /* מרווחים קטנים */
    position: relative !important;
    z-index: 1 !important;
  }

  /* Modal Header - Fixed at top */
  .event-detail-modal .modal-content.add-event-modal .modal-header-clean {
    flex-shrink: 0 !important; /* לא יקטן */
    position: relative !important;
    z-index: 2 !important;
  }

  /* Modal Body Scroll for Add Event Modal - Only middle part scrolls */
  .event-detail-modal .modal-content.add-event-modal .modal-body-clean {
    flex: 1 !important;
    overflow-y: auto !important; /* גלילה אנכית רק בbody */
    padding: 15px !important;
    min-height: 0 !important; /* חשוב לflex scrolling */
  }

  /* Modal Footer - Fixed at bottom */
  .event-detail-modal .modal-content.add-event-modal .modal-footer-clean {
    flex-shrink: 0 !important; /* לא יקטן */
    position: relative !important;
    z-index: 2 !important;
  }

  /* Apply same styling to all event modals with no-frame class */
  .event-detail-modal .modal-content.no-frame {
    background: white !important;
    border-radius: 16px !important;
    max-height: calc(100vh - 120px) !important;
    width: 95% !important;
    max-width: 420px !important;
    min-height: 400px !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3) !important;
    margin: 5px !important;
    position: relative !important;
    z-index: 1 !important;
  }

  /* Modal Header - Fixed at top for no-frame modals */
  .event-detail-modal .modal-content.no-frame .modal-header-clean {
    flex-shrink: 0 !important; /* לא יקטן */
    position: relative !important;
    z-index: 2 !important;
  }

  /* Modal Body Scroll for no-frame modals - Only middle part scrolls */
  .event-detail-modal .modal-content.no-frame .modal-body-clean {
    flex: 1 !important;
    overflow-y: auto !important; /* גלילה אנכית רק בbody */
    padding: 15px !important;
    min-height: 0 !important; /* חשוב לflex scrolling */
  }

  /* Modal Footer - Fixed at bottom for no-frame modals */
  .event-detail-modal .modal-content.no-frame .modal-footer-clean {
    flex-shrink: 0 !important; /* לא יקטן */
    position: relative !important;
    z-index: 2 !important;
  }

  /* Dark mode for all no-frame modals */
  .dark .event-detail-modal .modal-content.no-frame {
    background: #1e293b !important;
    border: 1px solid #334155 !important;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5) !important;
  }



  /* Ensure mobile sidebar modal remains highest for settings */
  .mobile-sidebar-modal {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    z-index: 10004 !important; /* הכי הכי גבוה לעברית */
    background: rgba(0, 0, 0, 0.6) !important;
    backdrop-filter: blur(4px);
  }

  /* Additional specific override for the slider when modal is open */
  .mobile-sidebar-modal ~ .mobile-toggle-fixed,
  body:has(.mobile-sidebar-modal) .mobile-toggle-fixed {
    z-index: 100 !important; /* נמוך מאוד כשיש modal */
  }

  /* Force mobile sidebar modal to be on top of everything */
  .mobile-sidebar-modal {
    isolation: isolate;
  }

  /* Ensure modal content is properly positioned */
  .mobile-sidebar-modal .mobile-modal-content {
    position: relative;
    z-index: 1;
    max-width: 95% !important;
    width: 95% !important;
    margin: 10px auto !important;
  }

  /* Dark mode fixes for modals on mobile */
  .dark .event-detail-modal {
    background: rgba(0, 0, 0, 0.8) !important;
  }

  .dark .popup-overlay {
    background: rgba(0, 0, 0, 0.8) !important;
  }

  /* Additional fixes for modal content dark mode */
  .dark .modal-content.add-event-modal,
  .dark .modal-content.contact-search-modal {
    background: #1e293b !important;
    border: 1px solid #334155 !important;
  }

  /* Dark mode for Add Event Modal on mobile */
  .dark .event-detail-modal .modal-content.add-event-modal {
    background: #1e293b !important;
    border: 1px solid #334155 !important;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5) !important;
  }



  /* Dark mode for mobile sidebar modal */
  .dark .mobile-sidebar-modal {
    background: rgba(0, 0, 0, 0.8) !important;
  }

  /* Switch order of navigation buttons and add event button on mobile */
  .header-right .nav-btn {
    order: 2; /* החיצים יהיו בסוף */
  }

  .header-right .add-event-btn {
    order: 1; /* כפתור הוספת אירוע יהיה בהתחלה */
  }

  .mobile-setting-group {
    margin-bottom: 20px;
  }

  .mobile-group-header {
    margin-bottom: 16px;
  }

  .mobile-group-header h4 {
    margin: 0 0 4px 0;
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
  }

  .dark .mobile-group-header h4 {
    color: #f9fafb;
  }

  .mobile-group-header p {
    margin: 0 0 12px 0;
    font-size: 14px;
    color: #6b7280;
  }

  .dark .mobile-group-header p {
    color: #9ca3af;
  }

  .mobile-setting-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    background: #f9fafb;
    border-radius: 12px;
    margin-bottom: 12px;
  }

  .dark .mobile-setting-row {
    background: #374151;
  }

  .mobile-setting-info label {
    display: block;
    font-weight: 500;
    color: #1f2937;
    margin-bottom: 4px;
  }

  .dark .mobile-setting-info label {
    color: #f9fafb;
  }

  .mobile-setting-desc {
    font-size: 12px;
    color: #6b7280;
  }

  .dark .mobile-setting-desc {
    color: #9ca3af;
  }

  .mobile-switch {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
  }

  .mobile-switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .mobile-switch-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #d1d5db;
    transition: 0.3s;
    border-radius: 24px;
  }

  .mobile-switch-slider:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }

  .mobile-switch input:checked + .mobile-switch-slider {
    background-color: #667eea;
  }

  .mobile-switch input:checked + .mobile-switch-slider:before {
    transform: translateX(20px);
  }

  .mobile-working-hours {
    margin-top: 16px;
  }

  .mobile-day-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px; /* פדינג גדול יותר */
    background: #f9fafb;
    border-radius: 12px; /* עיגול גדול יותר */
    margin-bottom: 12px; /* מרווח גדול יותר */
    min-height: 60px; /* גובה מינימלי */
    box-sizing: border-box;
  }

  .dark .mobile-day-row {
    background: #374151;
  }

  .mobile-day-row.disabled {
    opacity: 0.5;
  }

  .mobile-day-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .mobile-day-checkbox {
    position: relative;
    display: inline-block;
  }

  .mobile-day-checkbox input {
    opacity: 0;
    position: absolute;
  }

  .mobile-checkmark {
    width: 20px;
    height: 20px;
    background: #e5e7eb;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .dark .mobile-checkmark {
    background: #4b5563;
  }

  .mobile-day-checkbox input:checked + .mobile-checkmark {
    background: #667eea;
    color: white;
  }

  .mobile-day-checkbox input:checked + .mobile-checkmark:after {
    content: '✓';
    font-size: 12px;
    font-weight: bold;
  }

  .mobile-day-name {
    font-weight: 500;
    color: #1f2937;
    min-width: 60px;
  }

  .dark .mobile-day-name {
    color: #f9fafb;
  }

  .mobile-time-controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .mobile-time-input {
    padding: 10px 12px; /* פדינג גדול יותר */
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 16px; /* טקסט גדול יותר */
    width: 90px; /* רוחב גדול יותר */
    min-height: 44px; /* גובה מינימלי */
    box-sizing: border-box;
  }

  .dark .mobile-time-input {
    background: #4b5563;
    border-color: #6b7280;
    color: #f9fafb;
  }

  .mobile-time-separator {
    color: #6b7280;
    font-weight: 500;
  }

  .mobile-actions {
    margin-top: 15px;
    padding: 0 5px; /* מרווח צדדי */
  }

  .mobile-btn-primary {
    width: 100%; /* רוחב מלא */
    padding: 14px 20px; /* פדינג גדול יותר לנוחות */
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 12px; /* עיגול יותר גדול */
    font-weight: 600;
    font-size: 16px; /* טקסט גדול יותר */
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s ease;
    min-height: 48px; /* גובה מינימלי לנוחות */
  }

  .mobile-btn-primary:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  .mobile-btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .mobile-add-btn {
    padding: 12px 20px; /* padding גדול יותר למובייל */
    background: #667eea;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 16px; /* פונט גדול יותר למובייל */
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px; /* מרחק גדול יותר בין האייקון לטקסט */
    transition: all 0.2s ease;
    min-height: 44px; /* גובה מינימלי למובייל */
    /* תמיכה במובייל */
    -webkit-tap-highlight-color: transparent;
    tap-highlight-color: transparent;
    position: relative;
    z-index: 10;
    white-space: nowrap; /* למנוע שבירת שורה */
  }

  .mobile-add-btn:hover {
    background: #5a67d8;
  }

  /* מובייל - שיפורים נוספים לנגישות מגע */
  @media (max-width: 768px) {
    .mobile-add-btn {
      min-height: 48px; /* גובה גדול יותר במובייל */
      font-size: 17px;
    }
    
    .mobile-edit-btn,
    .mobile-remove-btn {
      width: 48px; /* רוחב גדול יותר במובייל */
      height: 48px; /* גובה גדול יותר במובייל */
      min-width: 48px;
      min-height: 48px;
      font-size: 20px; /* אייקון גדול יותר במובייל */
    }
    
    .mobile-type-actions {
      gap: 12px !important; /* מרחק גדול יותר בין הכפתורים במובייל */
    }
  }

  /* הגדרות נוספות למכשירי מגע */
  @media (pointer: coarse) {
    .mobile-add-btn,
    .mobile-edit-btn,
    .mobile-remove-btn {
      transform: scale(1);
      transition: transform 0.1s ease, background-color 0.2s ease;
    }
    
    .mobile-add-btn:active,
    .mobile-edit-btn:active,
    .mobile-remove-btn:active {
      transform: scale(0.95); /* אפקט לחיצה במובייל */
    }
  }

  .mobile-loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 20px;
    color: #6b7280;
  }

  .dark .mobile-loading-state {
    color: #9ca3af;
  }

  .mobile-breaks-list,
  .mobile-types-list {
    margin-top: 16px;
  }

  .mobile-break-item,
  .mobile-type-item {
    background: #f9fafb;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 8px;
  }

  .dark .mobile-break-item,
  .dark .mobile-type-item {
    background: #374151;
  }

  .mobile-break-header,
  .mobile-type-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .mobile-break-name,
  .mobile-type-name {
    font-weight: 500;
    color: #1f2937;
  }

  .dark .mobile-break-name,
  .dark .mobile-type-name {
    color: #f9fafb;
  }

  .mobile-break-actions,
  .mobile-type-actions {
    display: flex;
    gap: 8px;
  }

  .mobile-edit-btn,
  .mobile-remove-btn {
    width: 44px; /* רוחב גדול יותר למובייל */
    height: 44px; /* גובה גדול יותר למובייל - גודל מינימלי מומלץ למגע */
    border: none;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    font-size: 18px; /* אייקון גדול יותר למובייל */
    min-width: 44px; /* רוחב מינימלי למובייל */
    min-height: 44px; /* גובה מינימלי למובייל */
    padding: 0; /* הסרת padding פנימי שיכול להפריע */
    margin: 2px; /* מרחק קטן בין הכפתורים */
    /* תמיכה במובייל */
    -webkit-tap-highlight-color: transparent;
    tap-highlight-color: transparent;
    position: relative;
    z-index: 10;
  }

  .mobile-edit-btn {
    background: #e0e7ff;
    color: #667eea;
  }

  .mobile-edit-btn:hover {
    background: #c7d2fe;
  }

  .mobile-remove-btn {
    background: #fee2e2;
    color: #dc2626;
  }

  .mobile-remove-btn:hover {
    background: #fecaca;
  }

  .dark .mobile-edit-btn {
    background: #312e81;
    color: #93c5fd;
  }

  .dark .mobile-remove-btn {
    background: #7f1d1d;
    color: #fca5a5;
  }

  .mobile-type-info {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .mobile-type-color {
    width: 16px;
    height: 16px;
    border-radius: 50%;
  }

  .mobile-type-duration {
    font-size: 12px;
    color: #6b7280;
    background: #e5e7eb;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .dark .mobile-type-duration {
    color: #9ca3af;
    background: #4b5563;
  }

  /* Mobile type name input when editing */
  .mobile-type-name-input {
    background: #f9fafb;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 500;
    color: #1f2937;
    width: 100%;
    margin-top: 4px;
  }

  .dark .mobile-type-name-input {
    background: #374151;
    border-color: #4b5563;
    color: #f9fafb;
  }

  /* Mobile edit form */
  .mobile-type-edit-form {
    margin-top: 16px;
    padding: 16px;
    background: #f3f4f6;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }

  .dark .mobile-type-edit-form {
    background: #4b5563;
    border-color: #6b7280;
  }

  .mobile-form-group {
    margin-bottom: 16px;
  }

  .mobile-form-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
  }

  .dark .mobile-form-label {
    color: #d1d5db;
  }

  /* Duration controls */
  .mobile-duration-control {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #ffffff;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 4px;
  }

  .dark .mobile-duration-control {
    background: #374151;
    border-color: #4b5563;
  }

  .mobile-duration-btn {
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 6px;
    background: #e5e7eb;
    color: #374151;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    -webkit-tap-highlight-color: transparent;
  }

  .mobile-duration-btn:hover {
    background: #d1d5db;
  }

  .mobile-duration-btn:active {
    transform: scale(0.95);
  }

  .dark .mobile-duration-btn {
    background: #4b5563;
    color: #d1d5db;
  }

  .dark .mobile-duration-btn:hover {
    background: #6b7280;
  }

  .mobile-duration-input {
    flex: 1;
    text-align: center;
    border: none;
    background: transparent;
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    padding: 8px;
  }

  .dark .mobile-duration-input {
    color: #f9fafb;
  }

  /* Color selection */
  .mobile-color-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 8px;
    margin-top: 8px;
  }

  .mobile-color-dot {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid transparent;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    -webkit-tap-highlight-color: transparent;
  }

  .mobile-color-dot:active {
    transform: scale(0.9);
  }

  .mobile-color-dot.active {
    border-color: #1f2937;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
  }

  .dark .mobile-color-dot.active {
    border-color: #f9fafb;
  }

  /* Form actions */
  .mobile-form-actions {
    display: flex;
    gap: 12px;
    margin-top: 20px;
  }

  .mobile-btn-secondary,
  .mobile-btn-primary {
    flex: 1;
    padding: 12px 16px;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s ease;
    min-height: 48px;
    -webkit-tap-highlight-color: transparent;
  }

  .mobile-btn-secondary {
    background: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;
  }

  .mobile-btn-secondary:hover {
    background: #e5e7eb;
  }

  .mobile-btn-secondary:active {
    transform: scale(0.98);
  }

  .mobile-btn-primary {
    background: #3b82f6;
    color: white;
  }

  .mobile-btn-primary:hover {
    background: #2563eb;
  }

  .mobile-btn-primary:active {
    transform: scale(0.98);
  }

  .mobile-btn-secondary:disabled,
  .mobile-btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .dark .mobile-btn-secondary {
    background: #4b5563;
    color: #d1d5db;
    border-color: #6b7280;
  }

  .dark .mobile-btn-secondary:hover {
    background: #6b7280;
  }

  /* Mobile break edit form styling */
  .mobile-break-edit-form {
    margin-top: 16px;
    padding: 16px;
    background: #f3f4f6;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }

  .dark .mobile-break-edit-form {
    background: #4b5563;
    border-color: #6b7280;
  }

  .mobile-break-time {
    margin-bottom: 16px;
  }

  .mobile-break-time label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
  }

  .dark .mobile-break-time label {
    color: #d1d5db;
  }

  .mobile-break-buttons {
    display: flex;
    gap: 12px;
    margin-top: 20px;
  }

  .mobile-save-btn,
  .mobile-cancel-btn {
    flex: 1;
    padding: 12px 16px;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s ease;
    min-height: 48px;
    -webkit-tap-highlight-color: transparent;
  }

  .mobile-save-btn {
    background: #3b82f6;
    color: white;
  }

  .mobile-save-btn:hover {
    background: #2563eb;
  }

  .mobile-save-btn:active {
    transform: scale(0.98);
  }

  .mobile-save-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .mobile-cancel-btn {
    background: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;
  }

  .mobile-cancel-btn:hover {
    background: #e5e7eb;
  }

  .mobile-cancel-btn:active {
    transform: scale(0.98);
  }

  .dark .mobile-cancel-btn {
    background: #4b5563;
    color: #d1d5db;
    border-color: #6b7280;
  }

  .dark .mobile-cancel-btn:hover {
    background: #6b7280;
  }

  /* Mobile break name input when editing */
  .mobile-break-name.editing {
    background: #f9fafb;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 500;
    color: #1f2937;
    width: 100%;
    margin-top: 4px;
  }

  .dark .mobile-break-name.editing {
    background: #374151;
    border-color: #4b5563;
    color: #f9fafb;
  }

  /* Mobile break days selection */
  .mobile-break-days {
    margin-bottom: 16px;
  }

  .mobile-days-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-top: 8px;
  }

  .mobile-day-pill {
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 12px 8px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
    -webkit-tap-highlight-color: transparent;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .mobile-day-pill:active {
    transform: scale(0.98);
  }

  .mobile-day-pill.active {
    background: #3b82f6;
    border-color: #2563eb;
    color: white;
  }

  .dark .mobile-day-pill {
    background: #4b5563;
    border-color: #6b7280;
    color: #d1d5db;
  }

  .dark .mobile-day-pill.active {
    background: #3b82f6;
    border-color: #2563eb;
    color: white;
  }

  /* Hide desktop modal when in sidebar view on mobile */
  .mobile-sidebar-view .desktop-modal {
    display: none !important;
  }

  /* Hide Today button on mobile in main calendar */
  .main-calendar-card.mobile-active .today-btn {
    display: none !important;
  }

  /* Remove border/line between slider and calendar content */
  .main-calendar-card.mobile-active {
    border-top: none !important;
    border: none !important;
    box-shadow: none !important;
  }

  .main-calendar-card.mobile-active .calendar-header {
    border-top: none !important;
    border-bottom: none !important;
    margin-top: 0 !important;
    padding-top: 0 !important;
  }

  /* Show only first letter of day names on mobile in main calendar */
  .main-calendar-card.mobile-active .day-name {
    position: relative;
    overflow: hidden;
  }

  /* Hide full day name and show only first letter */
  .main-calendar-card.mobile-active .day-name::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: inherit;
    z-index: 1;
  }

  .main-calendar-card.mobile-active .day-name::after {
    content: attr(data-short);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
    color: inherit;
    font-weight: inherit;
    font-size: inherit;
  }

  /* Hide original text on mobile */
  .main-calendar-card.mobile-active .day-name {
    color: transparent;
  }

  /* New responsive improvements */
  .calendar-container-height {
    min-height: 70vh;
  }

  /* Base calendar hour height for all views */
  .calendar-hour-height {
    min-height: 60px;
    max-height: 60px;
  }

  /* Sidebar responsive behavior */
  .sidebar-responsive {
    display: block;
  }

  /* Calendar responsive behavior */
  .calendar-responsive {
    display: block;
  }

  @media (min-width: 1024px) {
    .sidebar-responsive {
      display: block !important;
    }

    .calendar-responsive {
      display: block !important;
    }
  }

  @media (max-width: 1023px) {
    .sidebar-responsive.sidebar-hidden {
      display: none !important;
    }
    
    .sidebar-responsive.mobile-active {
      display: block !important;
    }

    .calendar-responsive.calendar-hidden {
      display: none !important;
    }
    
    .calendar-responsive.mobile-active {
      display: block !important;
    }

    .calendar-container-height {
      min-height: auto;
      height: auto;
    }
    
    /* Ensure mobile sidebar shows/hides properly */
    .mobile-active {
      display: block !important;
    }

    /* Better spacing for mobile */
    .max-w-7xl {
      max-width: none;
      padding-left: 1rem;
      padding-right: 1rem;
    }

    /* Mobile calendar adjustments */
    .calendar-day-min-height {
      min-height: 60px;
    }

    /* Mobile day view improvements */
    .calendar-hour-height {
      height: 50px !important; /* קצת יותר קטן במובייל */
    }
    
    /* Mobile day view grid adjustments */
    .grid-cols-2 .calendar-hour-height {
      font-size: 11px; /* שעות קצת יותר קטנות במובייל */
    }
    
    /* אירועים במובייל - תצוגת יום */
    .grid-cols-2 .absolute.left-1.right-1 {
      left: 2px !important;
      right: 2px !important;
      padding: 4px !important;
      font-size: 10px !important;
    }
    
    /* הסתרת פרטים מיותרים במובייל בתצוגת יום */
    .grid-cols-2 .absolute.left-1.right-1 .ri-map-pin-line {
      display: none;
    }
    
    /* שיפור המרווחים במובייל - תצוגת יום */
    .grid-cols-2 {
      grid-template-columns: 70px 1fr !important; /* עמודת שעות קבועה, עמודת אירועים גמישה */
    }
    
    /* Hide some elements on very small screens */
    @media (max-width: 480px) {
      .calendar-day-min-height {
        min-height: 50px;
      }
      
      /* תצוגת יום במובייל קטן */
      .calendar-hour-height {
        height: 45px !important;
      }
      
      .grid-cols-2 {
        grid-template-columns: 60px 1fr !important; /* מסכים קטנים - עמודת שעות יותר צרה */
      }
      
      .grid-cols-2 .border-r:first-child {
        font-size: 10px;
      }
    }
  }
}
</style>
